// The texts of the backoffice in German — the BASE LANGUAGE (root CLAUDE.md, „Konstantendatei je
// Projekt — erst einsammeln, dann entscheiden“: texts are collected like values, but in their own
// file, so the values do not drown between hundreds of sentences). Client and server import them
// through shared/texts (index.ts), never from here.
//
// Every entry is a plain sentence. A value goes in as {name} and is set with fill() from
// shared/texts; singular and plural are two entries, picked with plural(). This file is the
// template for any further language: same keys, same placeholders (checked by the type, see
// index.ts), and whatever a language leaves out falls back to German.
// Texts that already live in templates or settings stay there.

export const de = {
    // Was aus einem Verkauf geworden ist — die Woerter, die der Betreiber liest. Die Schnittstelle
    // liefert Schluessel ('settled' …), die Uebersetzung steht NUR hier: An zwei Stellen gepflegt
    // laufen Wortlaute auseinander, und im Freigabe-Hinweis muss dasselbe Wort stehen wie in der
    // Spalte, sonst sucht er in der Liste nach etwas anderem.
    //
    // ⚠️ `excluded` hiess zuerst „ausgebucht". Der Betreiber hat es verworfen, und sein Einwand
    // wiegt schwerer als das buchhalterische Argument: *„ausgebucht hört sich an als ob diese
    // Zahlung schon angewiesen wurde"* — im Zahlungsverkehr liest sich das Wort als „ist raus",
    // also als das GEGENTEIL des Zustands. Bei einem Wort, an dem Geld haengt, ist eine mögliche
    // Verwechslung kein Geschmack.
    // „Zurückgehalten" und nicht „geblockt", weil sonst ein Wort zwei Dinge meinte: den gesperrten
    // AUTOR und die einbehaltene ZEILE, die auch dann zurueckgehalten bleibt, wenn die Sperre
    // laengst beendet ist.
    SALE_STATE_LABELS: {
        open: 'offen',
        settled: 'abgerechnet',
        excluded: 'zurückgehalten',
        pending: 'vorgemerkt',
    },

    // Was die Zeile IST — unabhaengig davon, was mit ihr passiert ist.
    SALE_KIND_LABELS: {
        sale: 'Verkauf',
        reversal: 'Storno',
        chargeback: 'Rückbelastung',
    },

    // RBAC display labels (mirror the server's modules; see auth/permissions.ts).
    // Status-Beschriftungen. ⚠️ Sie standen bis zum 12.09.2026 NUR als Filterliste in der
    // jeweiligen Maske, und die Tabellen zeigten daneben den Rohwert („draft", „active") —
    // „Status value anstelle von label angezeigt" (Betreiber). Hier stehen sie jetzt einmal, und
    // die Filterlisten bauen sich daraus. Eine zweite Tabelle daneben wäre genau die Sorte
    // Doppelung, bei der eine Umbenennung nur an einer Stelle ankommt.
    INVOICE_STATUS_LABELS: {
        draft: 'Entwurf', finalized: 'Finalisiert', sent: 'Versendet', void: 'Storniert',
    },

    // The tab name of an invoice form — ONE source for every opener and for the form itself, which
    // renames its tab when the number arrives (finalize). Picked in client/lookups.ts invoiceTabName.
    INVOICE_TAB_NAME: {
        numbered: 'Rechnung {invoiceNumber}',
        draft: 'Rechnung (Entwurf)',
    },

    SUBSCRIPTION_STATUS_LABELS: {
        active: 'Aktiv', paused: 'Pausiert', ended: 'Beendet',
    },

    // Form of a manual abo, next to the status (operator, 02.10.2026). {date} = TT.MM.JJJJ.
    SUBSCRIPTION_STATUS_SINCE: {
        paused: 'pausiert seit {date}', ended: 'beendet am {date}',
    },
    // Save refused (409): the abo was changed after the form loaded it — by the month-end run, a
    // payment provider or someone else. The input stays on screen; reloading shows the new state.
    SUBSCRIPTION_STALE: 'Dieses Abo wurde inzwischen geändert (z. B. vom Rechnungslauf) und ist deshalb nicht gespeichert. Deine Eingaben stehen noch da — bitte notieren, dann neu laden und erneut eintragen.',
    // Same refusal for a customer (a sale rewrites address, company, VAT id) and an invoice draft
    // (no edit lock — two people on one draft).
    CUSTOMER_STALE: 'Dieser Kunde wurde inzwischen geändert (z. B. durch einen Kauf) und ist deshalb nicht gespeichert. Deine Eingaben stehen noch da — bitte notieren, dann neu laden und erneut eintragen.',
    INVOICE_DRAFT_STALE: 'Dieser Entwurf wurde inzwischen an anderer Stelle geändert und ist deshalb nicht gespeichert. Deine Eingaben stehen noch da — bitte notieren, dann neu laden und erneut eintragen.',
    // The button in all three messages: loads the record as it is now, the input on screen is given up.
    STALE_RELOAD: 'Neu laden',
    SUBSCRIPTION_REACTIVATE_HINT: 'Beim Übernehmen rückt das nächste Abrechnungsdatum auf den nächsten Termin ab heute vor — die Zeit dazwischen wird nicht abgerechnet. Ein hier von Hand geändertes Datum hat Vorrang.',

    // Messages for form fields (operator, 02.10.2026: no browser bubble, a German message AT the field
    // that names it). {label} = the field's label; {min}/{max}/{step}/{maxLength} from its attributes.
    // Used by forms/checkForm.ts for every form.
    FIELD_ERROR_TEXTS: {
        required: 'Bitte „{label}“ angeben.',
        email: '„{label}“ ist keine gültige E-Mail-Adresse.',
        url: '„{label}“ ist keine gültige Adresse — bitte vollständig mit https:// angeben.',
        number: '„{label}“ muss eine Zahl sein.',
        date: '„{label}“ ist kein gültiges Datum.',
        min: '„{label}“ muss mindestens {min} sein.',
        max: '„{label}“ darf höchstens {max} sein.',
        step: '„{label}“ erlaubt nur Schritte von {step}.',
        tooLong: '„{label}“ darf höchstens {maxLength} Zeichen haben.',
        tooShort: '„{label}“ muss mindestens {minLength} Zeichen haben.',
        invalid: '„{label}“ ist ungültig.',
        // When a field has no name at all: stands in for „{label}" (without the quotes).
        unnamed: 'dieses Feld',
    },

    // Einstellungen → Allgemein → Währung (operator, 02.10.2026: set once, then fixed).
    CURRENCY_SETTING_TEXTS: {
        title: 'Währung',
        label: 'Währung des backoffice',
        hint: 'Gilt für alle neuen Rechnungen, Abos, Abrechnungen und Auswertungen. Jeder Beleg behält die Währung, in der er ausgestellt wurde; was ein Zahlungsdienst liefert, bleibt, wie es ist. SEPA-Überweisungen gibt es nur in EUR.',
        locked: 'Festgelegt, seit es Belege gibt (eine finalisierte Rechnung oder eine Dozenten-Abrechnung). Katalogpreise, Dozentenguthaben, Mindestauszahlung und Gebühren tragen keine eigene Währung — ein Wechsel würde sie ohne Umrechnung in der neuen lesen.',
    },

    MODULE_LABELS: {
        dashboard: 'Dashboard',
        revenue: 'Umsätze',
        balance: 'Bilanz',
        customers: 'Kunden',
        invoices: 'Rechnungen',
        subscriptions: 'Abos',
        export: 'Monats-Export',
        settings: 'Einstellungen',
        users: 'Benutzer',
        roles: 'Rollen',
        webhooks: 'Webhooks',
        logs: 'Protokolle',
        trash: 'Papierkorb',
        'expense-mapping': 'Beleg-Zuordnung',
    },
    CAP_LABELS: { read: 'Lesen', write: 'Schreiben', delete: 'Löschen' },
    CAP_SHORT: { read: 'R', write: 'W', delete: 'D' },

    // Anzeigenamen der Monats-Verzeichnis-Kategorien (Ordnernamen auf der Platte sind englisch).
    DOC_CATEGORY_LABELS: {
        'invoices': 'Eigene Rechnungen',
        'vendor-invoices': 'Lieferanten-Rechnungen',
        'payment-reports': 'Zahlungs-Reports',
        'root': 'Monatsordner',
    },

    // Why the rule decided as it did (services/vat.ts ReverseChargeCode), for the invoice form.
    TAX_DECISION_REASONS: {
        ok: 'USt-IdNr. von VIES bestätigt, Land passt',
        domestic: 'Kunde im Sitzland',
        empty: 'Keine USt-IdNr. (oder kein Land) beim Kunden',
        format: 'Die USt-IdNr. ist nicht lesbar',
        invalid: 'VIES weist die USt-IdNr. zurück',
        country_mismatch: 'USt-IdNr. gültig, aber aus einem anderen Land als die Anschrift',
        unreachable: 'VIES nicht erreichbar — ob Reverse Charge gilt, ist offen',
    },


    // The checklist's source and requirement columns: one symbol per state (CHECK_MARK_SYMBOLS in
    // shared/constants.ts), its text is the tooltip, the aria-label and the legend below the list.
    // The texts are the ones the row used to show spelled out.
    CHECK_MARKS: {
        auto: 'automatisch',
        autoDone: 'automatisch ✓',
        noMovement: 'keine Bewegung ✓',
        optional: 'optional',
        mayBeEmpty: 'kann leer sein',
        mailNote: 'Hinweis in Mail',
    },
    // Tooltip of a mark that carries a detail (brands, binding).
    CHECK_MARK_DETAIL: '{text} · {detail}',

    // ── Auswahllisten: Wert → Text. Nur der Wert (Schlüssel) wird gespeichert und verglichen. ──

    // Brand names on the server: full name (documents, Art. 226 MwStSystRL: complete issuer name) and
    // short form (file names, tight UI). Keys and number prefixes stay in models/brands.ts — they are
    // identifiers.
    BRAND_NAMES: {
        d: { name: 'deja', shortName: 'deja' },
        C: { name: 'MSBC-Converter', shortName: 'MSBC' },
        V: { name: 'Gesellschaft für Vitalpilzkunde', shortName: 'GfV' },
    },
    // Brand names in the client's pickers and lists (order: CLIENT_BRAND_KEYS in shared/constants.ts)
    // — NOT the same as BRAND_NAMES (V reads „GfV / Vitalpilze“ here). Kept apart, because unifying
    // them would change what the screen shows.
    CLIENT_BRAND_NAMES: {
        d: 'deja',
        C: 'MSBC-Converter',
        V: 'GfV / Vitalpilze',
    },
    // Salutation of an author or customer by its code (SALUTATIONS in shared/constants.ts).
    SALUTATION_LABELS: {
        mr: 'Herr',
        ms: 'Frau',
    },
    // Display names of the brand template folders in the template editor (key = folder name).
    TEMPLATE_BRAND_LABELS: {
        deja: 'deja',
        gfv: 'GfV / Vitalpilze',
        'msbc-converter': 'MSBC-Converter',
    },
    // Label sheets (data: LABEL_PRESET_DATA in shared/constants.ts): the name per key and the option
    // text built from the sheet's own data.
    LABEL_PRESET_NAMES: {
        zw3474: 'Zweckform 3474',
        zw3475: 'Zweckform 3475',
        zw3477: 'Zweckform 3477',
    },
    LABEL_PRESET_OPTION: '{name} — {w} × {h} mm, {count} je Bogen',
    // Envelope formats (data: ENVELOPE_FORMATS, stored landscape): the name per key and the option
    // text, which names the size upright (short edge first).
    ENVELOPE_NAMES: {
        C4: 'C4',
        C5: 'C5',
        DL: 'DL',
    },
    ENVELOPE_OPTION: '{name} ({h} × {w} mm)',

    // ══ Server: Routen ══

    // Server texts (routes, webhooks, middleware, auth, app).

    // src/server/app.ts
    APP_TEXTS: {
        internalError: 'internal error',
    },

    // src/server/routes/analytics.ts
    ROUTES_ANALYTICS_TEXTS: {
        invalidGranularity: 'invalid granularity',
        invalidFromTo: 'invalid from/to',
    },

    // src/server/routes/auth.ts
    ROUTES_AUTH_TEXTS: {
        loginWrong: 'E-Mail oder Passwort falsch',
        loginFailed: 'Anmeldung fehlgeschlagen, bitte erneut versuchen',
        nameEmpty: 'Name darf nicht leer sein',
        currentPasswordWrong: 'Aktuelles Passwort ist falsch',
    },

    // src/server/routes/authorPortalApi.ts
    ROUTES_AUTHOR_PORTAL_API_TEXTS: {
        payoutBlockedVatId: 'USt-IdNr.',
        brandRequired: 'brand is required',
        refRequired: 'ref is required',
    },

    // src/server/routes/authorRegistryApi.ts
    ROUTES_AUTHOR_REGISTRY_API_TEXTS: {
        saveFailed: 'Stammdaten konnten nicht gespeichert werden',
        reasonMissing: 'fehlt',
        authorUnknown: 'Dozent im Backoffice nicht bekannt',
        verdictFailed: 'Urteil konnte nicht ermittelt werden',
    },

    // src/server/routes/authors.ts
    ROUTES_AUTHORS_TEXTS: {
        notFound: 'not found',
        ibanError: 'IBAN: {fehler}',
        salutationInvalid: 'Anrede: erwartet wird {allowed} (oder leer)',
        titleTooLong: 'Titel: höchstens {max} Zeichen',
        checkFailed: 'Pruefung fehlgeschlagen',
        authorIdRequired: 'authorId noetig',
        noRowsSelected: 'Keine Zeilen ausgewaehlt',
        noneHeldBack: 'Keine der gewaehlten Zeilen ist zurueckgehalten und noch offen.',
        brandPeriodAuthorRequired: 'brand, period und authorId noetig',
        brandYearMonthRequired: 'brand, year und month noetig',
        finalizeFailed: 'Abrechnung konnte nicht abgeschlossen werden',
        failed: 'Fehlgeschlagen',
        noFinalizedStatement: 'keine festgeschriebene Abrechnung',
        noIban: 'Beim Dozenten ist keine IBAN hinterlegt',
        ibanChangedAgain: 'Die IBAN hat sich inzwischen erneut geändert — Vorschau neu laden und erneut prüfen',
        brandPeriodRequired: 'brand und period noetig',
        nothingToTransfer: 'Nichts zu ueberweisen.',
        draftPdfFileName: 'entwurf-{period}.pdf',
        purgeBlocked: 'Dozent hat {statements} Abrechnung(en), {sales} Verkauf/Verkäufe und {items} Artikel und kann nicht endgültig gelöscht werden.',
    },

    // src/server/routes/brandConditions.ts
    ROUTES_BRAND_CONDITIONS_TEXTS: {
        unknownBrand: 'unknown brand',
    },

    // src/server/routes/catalog.ts
    ROUTES_CATALOG_TEXTS: {
        itemsRequired: 'items required',
        unknownBrand: 'unknown brand',
        itemIdPositive: 'itemId must be a positive integer',
        brandItemIdRequired: 'brand and itemId required',
        unknownItem: 'unknown item',
    },

    // src/server/routes/certificates.ts
    ROUTES_CERTIFICATES_TEXTS: {
        notFound: 'not found',
        alreadyShipped: 'Das Zertifikat ist bereits als versandt markiert — die Anschrift ist damit festgeschrieben. Zum Korrigieren zuerst den Versand-Haken zurücknehmen.',
        addressSaveFailed: 'Anschrift konnte nicht gespeichert werden',
        pdfLoadFailed: 'PDF konnte nicht geladen werden',
    },

    // src/server/routes/certificatesApi.ts
    ROUTES_CERTIFICATES_API_TEXTS: {
        numberRequired: 'number is required',
        notFound: 'not found',
        pdfLoadFailed: 'PDF konnte nicht geladen werden',
        invalidBrand: 'invalid brand',
        userIdRequired: 'userId required',
        courseIdRequired: 'courseId required',
        holderNameRequired: 'holderName required',
        courseTitleRequired: 'courseTitle required',
        notIssued: 'certificate could not be issued',
    },

    // src/server/routes/checklist.ts
    ROUTES_CHECKLIST_TEXTS: {
        invalidYearMonth: 'invalid year/month',
        notFound: 'not found',
    },

    // src/server/routes/checkout.ts
    ROUTES_CHECKOUT_TEXTS: {
        invalidBrand: 'invalid brand',
        invalidMode: 'invalid mode',
        invalidInterval: 'invalid interval',
        lineItemsRequired: 'lineItems required',
        customerEmailRequired: 'customer.email required',
        externalIdRequired: 'externalId required',
        urlsRequired: 'successUrl/cancelUrl required',
        currencyMustBe: 'currency must be {currency}',
        salutationInvalid: 'customer.salutation must be {allowed}, "" or null (or omitted)',
        titleInvalid: 'customer.title must be a string of at most {max} characters, "" or null (or omitted)',
        checkoutFailed: 'checkout failed',
    },

    // src/server/routes/customers.ts
    ROUTES_CUSTOMERS_TEXTS: {
        displayNameEmailRequired: 'displayName and email are required',
        salutationInvalid: 'Anrede: erwartet wird {allowed} (oder leer)',
        titleInvalid: 'Titel: höchstens {max} Zeichen',
        notFound: 'not found',
        purgeBlocked: 'Kunde hat {invoices} Rechnung(en) und {subscriptions} Abo(s) und kann nicht endgültig gelöscht werden.',
    },

    // src/server/routes/documents.ts
    ROUTES_DOCUMENTS_TEXTS: {
        invalidYearMonth: 'invalid year/month',
        invalidTarget: 'invalid target',
        monthNotEmpty: 'Monat ist nicht leer — erst alle Dateien entfernen.',
        monthNotFound: 'Monat nicht vorhanden.',
        deleteFailed: 'Löschen fehlgeschlagen.',
        invalidYear: 'invalid year',
        yearNotEmpty: 'Jahr enthält noch Monate — erst alle Monate entfernen.',
        yearNotFound: 'Jahr nicht vorhanden.',
        invalidFolder: 'invalid folder',
        invalidTargetFolder: 'invalid target folder',
        sameSourceTarget: 'Quelle und Ziel sind identisch.',
        moveFailed: 'Verschieben fehlgeschlagen.',
        invalidPath: 'invalid path',
        notFound: 'not found',
        invalidAmount: 'invalid amount',
        noRecord: 'Kein Datensatz zu dieser Datei (vor dem Tracking abgelegt).',
    },

    // src/server/routes/expenseConfig.ts
    ROUTES_EXPENSE_CONFIG_TEXTS: {
        keyLabelRequired: 'key und label sind erforderlich',
        typeExists: 'Typ „{key}" existiert bereits.',
        notFound: 'not found',
        typeInUse: 'Typ wird von {used} Zuordnung(en) verwendet und kann nicht gelöscht werden.',
        invalidDomain: 'Ungültige Domain.',
        typeRequired: 'Typ ist erforderlich.',
        domainMapped: 'Domain „{domain}" ist bereits zugeordnet.',
    },

    // src/server/routes/exports.ts
    ROUTES_EXPORTS_TEXTS: {
        invalidYearMonth: 'invalid year/month',
        checklistIncomplete: 'Checkliste unvollständig — bitte erst alle erforderlichen Punkte erledigen.',
    },

    // src/server/routes/health.ts
    ROUTES_HEALTH_TEXTS: {
        dejaEnvInvalid: 'DEJA_ENV fehlt oder ist ungültig ("{raw}")',
    },

    // src/server/routes/ingest.ts
    ROUTES_INGEST_TEXTS: {
        fetchFailed: 'Abruf fehlgeschlagen',
        unknownAction: 'Unbekannte Aktion',
        actionFailed: 'Aktion fehlgeschlagen',
    },

    // src/server/routes/ingestActions.ts
    ROUTES_INGEST_ACTIONS_TEXTS: {
        backToBackoffice: 'Zum Backoffice',
        retrySuccess: 'Import erfolgreich — der Beleg wurde abgelegt.',
        retryGone: 'Die E-Mail ist nicht mehr im Postfach vorhanden.',
        retryStillFailed: 'Import weiterhin fehlgeschlagen (kein PDF-Anhang?).',
        retryTitle: 'Import wiederholt',
        ignoredTitle: 'Ignoriert',
        ignoredMessage: 'Die E-Mail wurde in den Papierkorb verschoben.',
        unknownActionTitle: 'Unbekannte Aktion',
        unknownActionMessage: 'Diese Aktion ist nicht bekannt.',
        linkInvalidTitle: 'Link ungültig',
        linkInvalidMessage: 'Dieser Link ist nicht (mehr) gültig — vermutlich wurde die E-Mail bereits bearbeitet.',
    },

    // src/server/routes/publicDocuments.ts — the page behind a download link from a customer mail
    // ({invoiceUrl}) when there is no document to give. Read by the CUSTOMER. Status: offen.
    ROUTES_PUBLIC_DOCUMENTS_TEXTS: {
        notFoundTitle: 'Link ungültig',
        notFoundMessage: 'Zu diesem Link gibt es keinen Beleg. Bitte prüfen Sie, ob der Link vollständig ist, oder wenden Sie sich an uns.',
        failedTitle: 'Beleg nicht verfügbar',
        failedMessage: 'Der Beleg kann gerade nicht geladen werden. Bitte versuchen Sie es später noch einmal.',
    },

    // src/server/routes/invoicePdfApi.ts
    ROUTES_INVOICE_PDF_API_TEXTS: {
        refNumberRequired: 'ref and number are required',
        notFound: 'not found',
        pdfLoadFailed: 'PDF konnte nicht geladen werden',
    },

    // src/server/routes/invoices.ts
    ROUTES_INVOICES_TEXTS: {
        lineTextAdminOnly: 'Nur ein Administrator kann den Text einer ausgestellten Rechnung ändern.',
        brandCustomerLineItemsRequired: 'brand, customerId and lineItems are required',
        customerNotFound: 'customer not found',
        customerIdRequired: 'customerId is required',
        notFound: 'not found',
        sendFailed: 'Der Versand ist fehlgeschlagen — bitte die E-Mail-Einstellungen prüfen.',
        finalizeFirst: 'finalize the invoice first',
        pdfLoadFailed: 'PDF konnte nicht geladen werden',
    },

    // src/server/routes/invoiceTemplates.ts
    ROUTES_INVOICE_TEMPLATES_TEXTS: {
        nameRequired: 'name is required',
        createFailed: 'Anlegen fehlgeschlagen',
        notFound: 'not found',
        xmlRequired: 'xml is required',
        saveFailed: 'Speichern fehlgeschlagen',
        error: 'Fehler',
        noImageFile: 'Keine Bilddatei (nur PNG/JPG)',
        imageNotFound: 'Bild nicht gefunden',
        deleteFailed: 'Löschen fehlgeschlagen',
        brandRequired: 'brand is required',
        previewFailed: 'Vorschau fehlgeschlagen',
    },

    // src/server/routes/mailFrames.ts
    ROUTES_MAIL_FRAMES_TEXTS: {
        noContent: 'Der Rahmen enthält kein {content} — ohne diese Stelle ginge die Mail ohne Text raus.',
        htmlRequired: 'html is required',
        unknownBrand: 'unknown brand',
        logoInvalid: 'Logo: bitte eine PNG- oder JPG-Datei (höchstens {mb} MB)',
        brandRequired: 'brand is required',
        sampleBody: '<p>Guten Tag Max Mustermann,</p><p>anbei erhalten Sie Ihre Rechnung {nr} als PDF.</p><p>Mit freundlichen Grüßen</p>',
        // Example values of the mail-text preview (placeholders {salutation}, {title}, {name}, {company}).
        sampleName: 'Max Mustermann',
        sampleTitle: 'Dr.',
        sampleCompany: 'Muster GmbH',
    },

    // src/server/routes/mollie.ts
    ROUTES_MOLLIE_TEXTS: {
        noMollieBrand: 'Keine Marke rechnet über Mollie ab.',
        invalidYearMonth: 'invalid year/month',
    },

    // src/server/routes/paypal.ts
    ROUTES_PAYPAL_TEXTS: {
        credentialsMissing: 'PayPal credentials not configured',
        invalidYearMonth: 'invalid year/month',
    },

    // src/server/routes/reports.ts
    ROUTES_REPORTS_TEXTS: {
        invalidYearMonth: 'invalid year/month',
    },

    // src/server/routes/roles.ts
    ROUTES_ROLES_TEXTS: {
        keyLabelRequired: 'key und label sind erforderlich',
        adminReserved: 'Die Rolle „admin" ist reserviert.',
        roleExists: 'Rolle „{key}" existiert bereits.',
        notFound: 'not found',
        systemRoleNoEdit: 'Die System-Rolle kann nicht bearbeitet werden.',
        systemRoleNoDelete: 'Die System-Rolle kann nicht gelöscht werden.',
        roleInUse: 'Rolle wird von {users} Benutzer(n) verwendet und kann nicht gelöscht werden.',
    },

    // src/server/routes/settings.ts
    ROUTES_SETTINGS_TEXTS: {
        ibanError: 'IBAN: {fehler}',
        notASecretField: '\'{field}\' ist kein Geheimnisfeld',
        unknownBrand: 'unbekannte Marke \'{brand}\'',
    },

    // src/server/routes/stripe.ts
    ROUTES_STRIPE_TEXTS: {
        invalidYearMonth: 'invalid year/month',
    },

    // src/server/routes/subscriptions.ts
    ROUTES_SUBSCRIPTIONS_TEXTS: {
        brandCustomerItemsRequired: 'brand, customerId and items are required',
        notFound: 'not found',
        updateFailed: 'update failed',
        purgeBlocked: 'Abo hat {invoices} Rechnung(en) und kann nicht endgültig gelöscht werden.',
    },

    // src/server/routes/todos.ts
    ROUTES_TODOS_TEXTS: {
        textMissing: 'Text fehlt',
        notFound: 'not found',
    },

    // src/server/routes/transactions.ts
    ROUTES_TRANSACTIONS_TEXTS: {
        notFound: 'nicht gefunden',
    },

    // src/server/routes/users.ts
    ROUTES_USERS_TEXTS: {
        cannotDeleteSelf: 'Du kannst dein eigenes Konto nicht löschen.',
        lastAdminNoDelete: 'Der letzte aktive Administrator kann nicht gelöscht werden.',
        requiredFields: 'name, email, password und role sind erforderlich',
        roleNotExists: 'Rolle „{role}" existiert nicht.',
        emailInUse: 'E-Mail wird bereits verwendet.',
        notFound: 'not found',
        ownAccountReadOnly: 'Das eigene Konto kann hier nicht bearbeitet werden (nur Ansicht).',
        lastAdminNoDemote: 'Der letzte aktive Administrator kann nicht herabgestuft oder deaktiviert werden.',
    },

    // src/server/routes/vat.ts
    ROUTES_VAT_TEXTS: {
        countryRequired: 'country required',
    },

    // src/server/routes/webhook.ts
    ROUTES_WEBHOOK_TEXTS: {
        secretMissing: 'inbound webhook secret not configured',
        invalidSignature: 'invalid signature',
    },

    // src/server/routes/webhooks.ts
    ROUTES_WEBHOOKS_TEXTS: {
        requiredFields: 'name, url und eventType sind erforderlich',
        notFound: 'nicht gefunden',
        unknownBrand: 'unbekannte Marke \'{brand}\'',
    },

    // src/server/webhooks/events.ts
    WEBHOOKS_EVENTS_TEXTS: {
        invoiceFinalized: 'Rechnung finalisiert',
        invoiceSent: 'Rechnung versendet',
        subscriptionCreated: 'Abo angelegt',
        subscriptionUpdated: 'Abo aktualisiert',
        subscriptionCancelled: 'Abo beendet',
        customerCreated: 'Kunde angelegt',
        customerUpdated: 'Kunde aktualisiert',
    },

    // ══ Server: Zahlungen, Autoren, Altimport, Druck, Urkunden ══

    // Texts of the server services payments, authors, legacyImport, print, certificates.
    // Every entry yields exactly the string the code wrote before.

    // ── payments ────────────────────────────────────────────────────────────────────────

    // src/server/services/payments/mollieStatement.ts
    SERVICES_PAYMENTS_MOLLIE_STATEMENT_TEXTS: {
        balanceTestMode: 'Balance, fees and payouts are only provided by Mollie in live mode (test mode).',
        balanceUnavailable: 'Mollie balance data unavailable: {detail}',
        paymentReceived: 'Payment received',
        refund: 'Refund',
        // Abort messages for the operator (German) — never filed in the statement.
        noToken: 'Es ist kein Mollie API Access Token eingetragen. Mollie liefert Saldo, Gebühren und Auszahlungen nur mit diesem Token, nicht mit den API Keys der Marken. Eintragen unter System → Einstellungen → Allgemein → „Mollie (Saldo für den Monatsreport)“ (alle Profile, Recht balance-reports.read).',
        tokenRejected: 'Mollie hat den API Access Token abgewiesen (HTTP {status}). Ist er abgelaufen oder widerrufen, fehlt ihm das Recht balance-reports.read, oder passt er nicht zur Instanz (Produktion: Token nur für Live, Testinstanz: Token nur für Test)? Neu eintragen unter System → Einstellungen → Allgemein → „Mollie (Saldo für den Monatsreport)“. Antwort von Mollie: {detail}',
        statementFailed: 'Mollie {year}-{month}: {note}',
        providerName: 'Mollie',
        reportTitle: 'Mollie {monthName} {year}',
        noMollieBrand: 'Keine Marke rechnet über Mollie ab.',
        // In the statement only when the month has a rounding compensation (53b, 03.11.2026).
        feeRoundingNote: 'Mollie rounds up the fees it withholds from the balance (rounding this month: {amount} {currency}). Mollie\'s invoice rounds per line, so it can differ by a cent; the difference is settled on the balance with the invoice in the following month.',
    },

    // src/server/services/payments/pspStatementPdf.ts
    SERVICES_PAYMENTS_PSP_STATEMENT_PDF_TEXTS: {
        months: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        brandLine: 'Brand: {brandName} ({prefix})',
        brandsLine: 'Brands: {list}',
        brandsItem: '{brandName} ({prefix})',
        colBrand: 'Brand',
        accountStatement: 'Account statement {monthName} {year}',
        summary: 'Summary ({currency})',
        openingBalance: 'Opening balance',
        paymentsReceived: 'Payments received',
        refunds: 'Refunds',
        fees: 'Fees',
        payouts: 'Payouts to bank account',
        closingBalance: 'Closing balance',
        // Summary built from the PSP's own balance (Mollie): opening/closing split into pending and available.
        openingBalanceSplit: 'Opening balance (pending {pending} · available {available})',
        closingBalanceSplit: 'Closing balance (pending {pending} · available {available})',
        chargebacks: 'Chargebacks',
        otherBookings: 'Other bookings',
        topups: 'Top-ups of the balance (may be a debit from the bank account)',
        balanceDifference: 'Difference: opening balance + movements do not equal the closing balance',
        ledgerDifference: 'Difference: the sum of all {provider} bookings does not equal the balance {provider} reports',
        paymentsSent: 'Payments sent',
        reconcile: '{label} in the detail below: {detail}; in the {provider} balance of this period: {psp}; difference {diff}. {provider} books it in another period or has not booked it yet.',
        totalExclFees: 'Total (excl. fees/payouts)',
        balanceNotRetrieved: 'Balance, fees and payouts could not be retrieved.',
        colDate: 'Date',
        colDescription: 'Description',
        colInvoice: 'Invoice',
        colPayment: 'Payment',
        total: 'Total',
        noPayments: 'No payments were processed in this period.',
        footer: 'This account statement is not an invoice within the meaning of the EU VAT Directive 2006/112/EC. It may serve as a receipt for accounting and tax related purposes.',
    },

    // src/server/services/payments/stripeStatement.ts
    SERVICES_PAYMENTS_STRIPE_STATEMENT_TEXTS: {
        paymentReceived: 'Payment received',
        refund: 'Refund',
        providerName: 'Stripe',
    },

    // src/server/services/payments/MollieProvider.ts
    SERVICES_PAYMENTS_MOLLIE_PROVIDER_TEXTS: {
        noKey: 'Kein Mollie-Key für Marke {brand} hinterlegt.',
        descriptionMore: '{first} (+{more} weitere)',
        subscriptionFallback: 'Abo',
        orderFallback: 'Bestellung',
        noCheckoutUrl: 'Mollie payment has no checkout url',
        parseWebhookUnsupported: 'MollieProvider.parseWebhook: use the per-brand /api/webhook/mollie/:brand route',
        subscriptionIdMissing: 'mollie action: providerSubscriptionId missing',
        customerIdMissing: 'mollie action: providerCustomerId missing',
        upgradeNoPeriod: 'mollie upgrade: no paid-through date (mirror and Mollie) — cannot credit the unused days',
    },

    // src/server/services/payments/upgradeProration.ts — the two lines of an upgrade invoice
    // (operator, 27.10.2026, „104a"). The description is the tier's own, as WP / Mollie send it.
    SERVICES_PAYMENTS_UPGRADE_PRORATION_TEXTS: {
        newPeriodLine: '{description} {from}–{to}',
        creditLine: 'Gutschrift {description}, {days} ungenutzte Tage',
    },

    // src/server/services/payments/StripeProvider.ts
    SERVICES_PAYMENTS_STRIPE_PROVIDER_TEXTS: {
        positionFallback: 'Position',
        taxRateReverseCharge: 'Reverse charge',
        taxRateVat: 'VAT',
        signatureNotVerified: 'stripe webhook signature did not verify against any brand',
        noKey: 'Kein Stripe-Key für Marke {brand} hinterlegt.',
        noRedirectUrl: 'Stripe checkout session has no redirect url',
        invoiceWithoutPayment: 'Stripe-Rechnung {paymentId} hat keine Zahlung zum Erstatten.',
        subscriptionWithoutItem: 'subscription {subId} has no item to change',
        subscriptionIdMissing: 'subscription action: providerSubscriptionId missing',
    },

    // src/server/services/payments/refundInvoice.ts
    SERVICES_PAYMENTS_REFUND_INVOICE_TEXTS: {
        invoiceNotFound: 'invoice not found',
        isCreditNote: 'Das ist ein Storno-Beleg — er kann nicht selbst erstattet werden.',
        noPsp: 'Diese Rechnung lief nicht über einen Zahlungsdienstleister — stornieren statt erstatten.',
        draftNeverPaid: 'Entwürfe wurden nie bezahlt.',
        notPositive: 'Diese Rechnung hat keinen positiven Betrag.',
        chargedBack: 'Die Zahlung wurde von der Bank zurückbelastet — eine Erstattung würde doppelt zahlen.',
        alreadyRequested: 'Eine Erstattung über {amount} {symbol} wurde bereits angefordert{when} und ist noch nicht bestätigt. Der Zahlungsdienstleister braucht dafür einige Minuten.',
        alreadyRequestedWhen: ' ({when})',
        fullyRefunded: 'Bereits vollständig erstattet.',
        noPayment: 'Zu dieser Rechnung ist keine Zahlung beim Zahlungsdienstleister auffindbar.',
        feeUnset: 'Die Stornogebühr ist für Marke {brandPrefix} nicht gesetzt — erstattet wird ohne Gebühr. Einstellungen → Marke.',
        amountMissing: 'Betrag fehlt oder ist nicht positiv.',
        atMost: 'Höchstens {max} {symbol} erstattbar.',
        feeEqualsFull: 'Die Gebühr entspricht dem vollen Betrag — es bliebe nichts zu erstatten.',
        pspFailed: 'Erstattung beim Zahlungsdienstleister fehlgeschlagen: {message}',
    },

    // src/server/services/payments/processPaymentEvent.ts
    SERVICES_PAYMENTS_PROCESS_PAYMENT_EVENT_TEXTS: {
        chargebackFeeUnset: 'Rückbelastung zu Rechnung {label}: Die Rückbelastungsgebühr ist für Marke {brandPrefix} nicht gesetzt — dem Dozenten wurde KEINE Gebühr berechnet. Pauschale in Einstellungen → Marke eintragen und die Gebühr nachbuchen.',
        msbcSubscription: 'MS-BC Subscription {users}/{projects}',
        msbcSubscriptionFallback: 'MS-BC Subscription',
        serviceFallback: 'Leistung',
        noSubscriptionMirror: 'no subscription mirror for {subId} (invoice {externalId}); retry',
    },

    // ── authors ─────────────────────────────────────────────────────────────────────────

    // src/server/services/authors/statementPdf.ts
    SERVICES_AUTHORS_STATEMENT_PDF_TEXTS: {
        months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
            'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        taxNoteDomestic: 'Umsatzsteuer {vatPercent} % wird im Gutschriftverfahren für Sie abgerechnet.',
        taxNoteReverseCharge: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge) — die Umsatzsteuer wird von uns geschuldet und ist von Ihnen nicht abzuführen.',
        yourVatId: 'Ihre USt-IdNr.: {vatId}',
        noVatId: 'Keine USt-IdNr. hinterlegt.',
        ourVatId: 'Unsere USt-IdNr.: {vatId}',
        transferTo: 'Überweisung auf {iban}',
        noBankAccount: 'Keine Bankverbindung hinterlegt — bitte nachreichen.',
        title: 'Gutschrift',
        number: 'Nr. {documentNumber}',
        date: 'Datum {date}',
        referenceNumber: 'Referenznummer {referenceNumber}',
        period: 'Abrechnungszeitraum {periodLabel}',
        selfBilling: 'Abrechnung im Gutschriftverfahren (Selbstfakturierung, Art. 224 MwStSystRL) — Sie erhalten diesen Beleg von uns; eine eigene Rechnung ist nicht erforderlich.',
        colDate: 'Datum',
        colItem: 'Artikel',
        colDocument: 'Beleg',
        colListPrice: 'Listenpreis',
        colFee: 'Gebühr',
        colPayout: 'Vergütung',
        chargeback: 'Rückbelastung',
        reversal: 'Storno',
        reversalTitle: '{kind}: {title}',
        catchUpTitle: 'Nachvergütung: {title}',
        noSales: 'Keine Verkäufe in diesem Zeitraum.',
        totalListPrices: 'Summe Listenpreise',
        lessFee: 'abzgl. Vermittlungsgebühr',
        lessChargebackFees: 'abzgl. Rückbelastungsgebühren',
        payoutNet: 'Vergütung netto',
        plusVat: 'zzgl. Umsatzsteuer {vatPercent} %',
        gross: 'Bruttobetrag',
        carriedIn: 'Vortrag aus Vormonat',
        toPay: 'Auszahlungsbetrag',
        belowMinimum: 'Der Betrag liegt unter Ihrer Mindestauszahlung und wird mit {amount} in den Folgemonat vorgetragen.',
        negativeBalance: 'Offener Saldo zu Ihren Lasten in Höhe von {amount} — wird mit der nächsten Abrechnung verrechnet.',
        freeAccessOne: '1 Gratis-Bezug',
        freeAccessMany: '{count} Gratis-Bezüge',
        freeAccessNote: 'Zusätzlich {free} Ihrer Artikel — nicht vergütungspflichtig, nur zur Information.',
        footerTaxNumber: 'Steuernummer {taxNumber}',
        footerVatId: 'USt-IdNr. {vatId}',
    },

    // src/server/services/authors/statements.ts
    SERVICES_AUTHORS_STATEMENTS_TEXTS: {
        reverseChargeNote: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge) — die Umsatzsteuer wird von uns geschuldet.',
        domesticNote: 'Umsatzsteuer {rate} % wird im Gutschriftverfahren für Sie abgerechnet.',
        periodNotClosed: 'Monat {period} ist noch nicht abgeschlossen — abgerechnet wird erst ab dem {from}',
        payoutMinUnsetPrefix: 'Mindestauszahlung nicht gesetzt',
        payoutMinUnsetTodo: 'Dozentenabrechnung {brandPrefix} {period}: Die Mindestauszahlung ist weder beim Dozenten noch bei der Marke gesetzt — {count} Abrechnung(en) nicht festgeschrieben, nichts überwiesen ({names}). Einstellungen → Marke eintragen, dann den Lauf wiederholen.',
        blockerEmail: 'E-Mail',
        blockerIban: 'IBAN',
        blockerAddress: 'Anschrift',
        blockerPayoutMinUnset: 'Mindestauszahlung nicht gesetzt — weder beim Dozenten noch bei der Marke (Einstellungen → Marke)',
        blockerCountryMissing: 'Land fehlt — ohne Land ist der Steuerfall nicht entscheidbar',
        blockerVatIdMissing: 'USt-IdNr.',
        blockerVatIdUnreadable: 'USt-IdNr. nicht lesbar (Format) — bitte Eintrag prüfen',
        blockerVatIdInvalid: 'USt-IdNr. laut VIES ungültig',
        blockerCountryVatMismatch: 'Land und USt-IdNr. passen nicht zusammen — bitte beides prüfen',
        blockerNonEu: 'Dozent außerhalb der EU — für diesen Fall gibt es keine Abrechnung',
        blockerVatRateMissing: 'Kein Umsatzsteuersatz für das Sitzland {country} hinterlegt — bitte in services/vatRates.ts ergänzen',
        statementNotFound: 'Abrechnung nicht gefunden',
        incomplete: 'Abrechnung unvollstaendig — es fehlt: {blockers}',
        draftNumber: 'ENTWURF',
        draftPreviewNumber: 'Entwurf {periodLabel}',
        stillDraftMarkPaid: 'Noch ein Entwurf — erst „Monat abrechnen", dann als bezahlt markieren',
        notMarkedPaid: 'Diese Abrechnung ist nicht als bezahlt markiert',
        stillDraftNoTransfer: 'Noch ein Entwurf — hier gibt es keine Überweisung',
        notInTransferFile: 'Diese Abrechnung war in keiner Überweisungsdatei',
    },

    // src/server/services/authors/sepa.ts
    SERVICES_AUTHORS_SEPA_TEXTS: {
        ownBankMissing: 'Eigene Bankverbindung fehlt — Einstellungen → Allgemein → Dozenten-Einstellungen',
        wrongCurrency: 'SEPA überweist nur in {sepaCurrency}, die Abrechnungen laufen in {currency} — Einstellungen → Allgemein → Währung.',
        noName: '(ohne Namen)',
        noPayoutAmount: 'kein Auszahlungsbetrag',
        noIban: 'keine IBAN hinterlegt',
        ibanUnusable: 'IBAN unbrauchbar ({error})',
        ibanOutsideSepa: 'IBAN ausserhalb des SEPA-Raums ({country})',
        ibanChanged: 'IBAN seit dem Festschreiben geändert — erst nach Bestätigung überwiesen',
        ibanNotRecorded: 'IBAN beim Festschreiben nicht festgehalten (Beleg vor dem 25.09.2026) — bitte einmal bestätigen',
        remittance: '{documentNumber} Dozentenverguetung {period}',
    },

    // src/server/services/authors/vies.ts
    SERVICES_AUTHORS_VIES_TEXTS: {
        authorNotFound: 'Dozent nicht gefunden',
    },

    // src/server/services/authors/registry.ts
    SERVICES_AUTHORS_REGISTRY_TEXTS: {
        feeNegotiated: 'Honorarsatz wird im Backoffice verhandelt',
        payoutThresholdMaintained: 'Auszahlungsschwelle wird im Backoffice gepflegt',
        blockDecided: 'Sperre und Freigabe entscheidet das Backoffice',
        blockedAtOnlyHere: 'der Zeitpunkt der Sperre entscheidet über Geld und wird nur hier gesetzt',
        internalNote: 'interne Notiz',
        trashBelongsHere: 'der Papierkorb gehört dem Backoffice',
        viesOnlyByCheck: 'der VIES-Befund entsteht nur durch eine Prüfung, nicht durch eine Meldung',
        notSetFromOutside: 'wird von außen nicht gesetzt — {reason}',
        missing: 'fehlt',
        countryFormat: 'erwartet wird ein Länderkürzel aus zwei Buchstaben (ISO 3166-1, z. B. DE)',
        invalidEmail: 'keine gültige E-Mail-Adresse',
        vatIdRequired: 'fehlt — für {country} ist die USt-IdNr. Pflicht (nur im Sitzland nicht)',
        vatIdUnreadable: 'nicht lesbar — erwartet werden zwei Buchstaben Länderkürzel und die Nummer',
        salutationInvalid: 'erwartet wird {allowed} (oder leer)',
        titleTooLong: 'höchstens {max} Zeichen',
    },

    // src/server/services/authors/sales.ts
    SERVICES_AUTHORS_SALES_TEXTS: {
        feeUnsetTodo: 'Dozentengebühr für Marke {brandPrefix} ist nicht gesetzt — Verkauf {orderExternalId}, Artikel {items}: mit 0 % gebucht, der Dozent erhält den vollen Listenpreis. Gebühr in Einstellungen → Marke eintragen und die Zeilen prüfen.',
        feeUnsetTodoNoOrder: '(ohne Bestellung)',
        chargebackReversedTitle: 'Rückbelastung aufgehoben: {title}',
        chargebackFeeTitle: 'Rückbelastungsgebühr zu {reference}',
    },

    // src/server/services/authors/catalog.ts
    SERVICES_AUTHORS_CATALOG_TEXTS: {
        authorFallbackName: 'Dozent {externalRef}',
    },

    // ── legacyImport ────────────────────────────────────────────────────────────────────

    // src/server/services/llmExtract.ts
    // The instruction to the local model (Ollama). `text` is the invoice text, already cut to
    // LLM_INPUT_MAX_CHARS. · Status: offen (the operator decides whether it becomes a setting)
    SERVICES_LLM_EXTRACT_TEXTS: {
        prompt: 'Du bist ein präziser Extraktor für Lieferanten-Rechnungen (Sprachen: Deutsch, Englisch, Griechisch).\nGib aus dem folgenden Rechnungstext NUR diese Felder als JSON zurück:\n- issueDate: Rechnungsdatum im Format yyyy-mm-dd\n- amount: Brutto-Gesamtbetrag (zu zahlender Endbetrag / Gesamtbetrag / Total / amount due), als Zahl mit Punkt als Dezimaltrenner\n- currency: 3-Buchstaben-Währungscode (z.B. EUR)\n- seller: Name des rechnungsstellenden Unternehmens (Aussteller/Lieferant), NICHT des Empfängers\n- confidence: deine Sicherheit 0..1\nBei mehreren Beträgen nimm den Endbetrag inklusive Steuer, nicht Zwischensumme oder Steueranteil.\n\nRechnungstext:\n"""\n{text}\n"""',
    },

    // src/server/services/legacyImport/v1.ts
    SERVICES_LEGACY_IMPORT_V1_TEXTS: {
        skipOperatorTestPurchase: 'Testkauf des Betreibers (Nutzer 1) — Entscheidung des Betreibers vom 23.09.2026: nicht importieren.',
        fileNotObject: 'Die Datei ist kein JSON-Objekt.',
        wrongSchema: 'schema ist "{schema}", erwartet "{expected}".',
        wrongVersion: 'version ist {version}, dieses Skript kennt nur {known}.',
        wrongTimezone: 'zeitzone_date ist "{timezone}", erwartet "UTC" — die Datumsregel gilt nur dafür.',
        invoicesNotList: 'rechnungen fehlt oder ist keine Liste.',
        countMismatch: 'anzahl sagt {count}, die Liste hat {length} Einträge — abgeschnittene oder falsch erzeugte Datei.',
        addressLineCount: 'Anschrift hat {count} Zeilen — V1 baut drei bis fünf.',
        addressNoCountry: 'Letzte Anschriftzeile endet nicht auf „/ LL" (Länderkürzel).',
        addressNoPlace: '„{place}" lässt sich nicht in PLZ und Ort teilen.',
        addressAmbiguous: 'Vierzeilige Anschrift ist zweideutig — der Kontoname „{accountName}" passt auf keine Zeile, und die Hausnummer entscheidet nicht.',
        taxWithoutPercent: 'Steuer {tax} {sign} ausgewiesen, aber tax_percent ist {percent}.',
        noTaxDomestic: '0,00 {sign} Steuer bei einem Kunden im Sitzland {country} — kein erkennbarer Grund.',
        noTaxEuWithoutVatId: '0,00 {sign} Steuer bei einem EU-Kunden ({country}) ohne USt-IdNr. — kein erkennbarer Grund.',
        noNumber: '(ohne Nummer)',
        badInvoiceNumber: 'Rechnungsnummer "{invoiceNumber}" hat nicht die Form NN/NN/NN/NN.',
        badReference: 'Referenznummer "{reference}" ist keine positive ganze Zahl — der Abruf könnte die Rechnung nie finden.',
        badDate: 'Datum "{date}" fehlt oder ist ungültig — ohne Datum druckte der Beleg das heutige.',
        userIdMissing: 'user_id fehlt — sie ist die Kundennummer des Belegs.',
        emailMissing: 'E-Mail fehlt — Pflichtfeld der Rechnung.',
        badPaymentMethod: 'Zahlungsart "{method}" ist weder paypal noch stripe.',
        badPaymentEvidence: 'Herkunft der Zahlungsart "{evidence}" ist unbekannt.',
        wrongCurrency: 'Währung {currency} — das backoffice rechnet in {backofficeCurrency}.',
        wrongStatus: 'Status "{status}" statt paid.',
        noPosition: 'Keine Position.',
        taxNotAmount: 'Steuerbetrag "{tax}" ist kein Betrag.',
        totalNotAmount: 'Gesamtbetrag "{total}" ist kein Betrag.',
        positionNoDescription: 'Position {n}: Bezeichnung fehlt.',
        positionBadQuantity: 'Position {n}: Menge "{quantity}" ist keine positive ganze Zahl.',
        positionBadNet: 'Position {n}: Nettobetrag "{net}" ist kein Betrag (Punkt, höchstens zwei Stellen).',
        positionNotDivisible: 'Position {n}: {net} {sign} lässt sich nicht auf {quantity} Stück zu vollen Cent verteilen.',
        badRefundDate: 'Erstattungsdatum "{date}" ist ungültig.',
        badRefundAmount: 'Erstattungsbetrag "{amount}" ist kein positiver Betrag.',
        refundExceedsTotal: 'Erstattet {refund} {sign} — mehr als die Rechnung ({total} {sign}).',
        refundBeforeInvoice: 'Erstattet vor dem Rechnungsdatum.',
        grossMismatch: 'Aus der Position ergeben sich {gross} {sign}, gezahlt wurden {paid} {sign}.',
        headerTaxDiffers: 'Kopfzeile nennt {headerTax} {sign} Steuer, gerechnet {computedTax} {sign} — die Summe stimmt, übernommen wird die Rechnung aus der Position.',
        headerNetDiffers: 'Kopfzeile nennt {headerNet} {sign} netto, die Position {positionNet} {sign}.',
        refundedNote: 'Erstattet am {date}: {amount} {sign} (nur im Datensatz, nicht auf dem Beleg).',
    },

    // src/server/services/legacyImport/run.ts
    SERVICES_LEGACY_IMPORT_RUN_TEXTS: {
        duplicateNumber: 'Rechnungsnummer steht mehrfach in der Datei.',
        duplicateReference: 'Referenznummer {reference} steht mehrfach in der Datei.',
        severalEmails: 'Konto {userId} hat in der Datei mehrere E-Mail-Adressen.',
        numberTaken: 'Die Rechnungsnummer gehört schon einem anderen Beleg (Referenz {reference}{origin}).',
        numberTakenImported: ', importiert',
        numberTakenNotImported: ', kein Import',
        referenceTaken: 'Referenznummer {reference} gehört schon {owner}.',
        referenceTakenNoNumber: 'einem Beleg ohne Nummer',
        fileFaulty: 'Die Datei ist fehlerhaft — es wird nichts geschrieben.',
        collisions: '{count} Nummernkollision(en) — es wird nichts geschrieben.',
    },

    // ── print ───────────────────────────────────────────────────────────────────────────

    // src/server/services/print/onlinebrief24.ts
    SERVICES_PRINT_ONLINEBRIEF24_TEXTS: {
        unreachable: 'onlinebrief24 nicht erreichbar: {message}',
        httpError: 'onlinebrief24: HTTP {status}{text}',
        httpErrorText: ' – {text}',
        dispatchDisabled: 'Postversand ist nicht freigegeben (PRINT_DISPATCH_ENABLED)',
        devSafe: 'DEV_SAFE ist aktiv — kein Postversand aus dieser Instanz',
        noCredentials: 'onlinebrief24: keine Zugangsdaten hinterlegt',
        noAddressInPdf: 'onlinebrief24 liest die Anschrift aus dem PDF — das Dokument traegt keine. Fuer Urkunden ist dieser Anbieter ohnehin nicht vorgesehen (nur 80-g-Papier).',
        pdfTooLarge: 'PDF zu gross ({bytes} Bytes, erlaubt sind {max})',
        noJobNumber: 'onlinebrief24: Antwort ohne Auftragsnummer ({message})',
        noText: 'kein Text',
        cancelledAtOnce: 'onlinebrief24: Auftrag {id} wurde sofort storniert',
    },

    // src/server/services/print/dispatch.ts
    SERVICES_PRINT_DISPATCH_TEXTS: {
        noUsableAddress: 'Keine verwendbare Lieferanschrift (Straße, PLZ oder Ort fehlt)',
        pdfUnavailable: 'PDF nicht verfügbar: {message}',
        failedMailSubject: 'Zertifikat-Versand fehlgeschlagen: {certificateNumber}',
        failedMailIntro: 'Der Postversand des Zertifikats {certificateNumber} ist endgültig fehlgeschlagen.',
        failedMailCourse: 'Kurs:      {courseTitle}',
        failedMailRecipient: 'Empfänger: {name} <{email}>',
        failedMailOrder: 'Bestellung: {order}',
        failedMailAttempts: 'Versuche:  {count}',
        failedMailLastError: 'Letzter Fehler: {error}',
        failedMailAddressHeading: 'Lieferanschrift:',
        failedMailNoAddress: '(keine hinterlegt)',
        failedMailClosing1: 'Der Auftrag bleibt gespeichert und kann wiederholt werden — es ist nichts verloren,',
        failedMailClosing2: 'aber es geht auch nichts raus, bis jemand sich darum kümmert.',
    },

    // src/server/services/print/mockProvider.ts
    SERVICES_PRINT_MOCK_PROVIDER_TEXTS: {
        unreachable: 'Mock-Druckdienst nicht erreichbar (Modus {mode}, Versuch {attempt})',
        rejected: 'Mock-Druckdienst lehnt den Auftrag ab (Modus reject)',
    },

    // ── certificates ────────────────────────────────────────────────────────────────────

    // src/server/services/certificates/pdf.ts
    SERVICES_CERTIFICATES_PDF_TEXTS: {
        completedOn: 'Abschlussdatum: {date}',
        certificateNumber: 'Zertifikatsnummer: {number}',
        issuedOn: 'Ausgestellt am {date}',
        notFound: 'certificate {certificateId} not found',
    },

    // src/server/services/certificates/layout.ts
    SERVICES_CERTIFICATES_LAYOUT_TEXTS: {
        headline: 'Zertifikat',
        intro: 'Hiermit wird bestätigt, dass',
        forLine: 'den Kurs erfolgreich abgeschlossen hat:',
    },

    // src/server/services/certificates/dispatch.ts
    SERVICES_CERTIFICATES_DISPATCH_TEXTS: {
        noAddress: '(keine Anschrift übermittelt)',
        subject: 'Zertifikat zu versenden: {holderName}',
        ordered: '{holderName} hat einen gedruckten Ausdruck bestellt und bezahlt.',
        course: 'Kurs:      {courseTitle}',
        number: 'Nummer:    {certificateNumber}',
        addressHeading: 'Anschrift:',
        dispatchList: 'Versandliste: {url}',
    },

    // src/server/services/certificates/addressPdf.ts
    SERVICES_CERTIFICATES_ADDRESS_PDF_TEXTS: {
        labelsTitle: 'Adressetiketten',
        endlessLabelsTitle: 'Adressetiketten (Endlos)',
        envelopesTitle: 'Umschläge {format}',
        noRecipients: 'Keine Empfänger ausgewählt.',
    },

    // ══ Server: Dienste, E-Mail, Modelle, Hilfen ══

    // Server texts, part B (services top level, notify, pdfTemplate, email, models, core, utils,
    // contracts, config).

    // src/server/services/report.ts
    SERVICES_REPORT_TEXTS: {
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        title: 'Sales for {monthName} {year}',
        colDate: 'Date',
        colInvoice: 'Invoice',
        colRef: 'Ref',
        colNet: 'Net',
        colTax: 'Tax',
        colGross: 'Gross',
        colCountry: 'Country',
        perCountryHeading: 'Sales with VAT per country',
        total: 'Total',
        ossHeading: 'One-Stop-Shop (OSS) — EU cross-border B2C, seat {seat}',
        ossNote: 'Subset of the table above: excludes the seat country, non-EU and reverse-charge (B2B) sales. Do not add to the totals above.',
        ossEmpty: 'No OSS-relevant sales this month.',
        colRate: 'Rate',
        colVat: 'VAT',
        colInvoices: 'Invoices',
        ossTotal: 'OSS total',
    },

    // src/server/services/export.ts
    SERVICES_EXPORT_TEXTS: {
        zipFileName: 'deja-{year}-{mm}.zip',
        mailBody: 'Monthly export {period}\n\n{invoiceCount} invoice(s), {documentCount} document(s) attached.',
        mailSubject: 'deja — exports {period}',
        noTaxAdvisor: 'Kein Steuerberater eingetragen (Einstellungen → Steuerberater E-Mail) — nichts gesendet, der Monat bleibt offen.',
    },

    // src/server/services/checklist.ts
    SERVICES_CHECKLIST_TEXTS: {
        defaultStripeReport: 'Stripe-Report',
        defaultPaypalReport: 'PayPal-Report',
        defaultMollieReport: 'Mollie-Report',
        defaultVendorInvoices: 'Lieferanten-Rechnungen (ChatGPT/Dropbox/GoToWebinar)',
        defaultVatReport: 'USt-Report (eigene Rechnungen)',
        bindingStripeReport: 'Stripe-Report vorhanden',
        bindingStripeTaxReport: 'Stripe-Tax-Rechnung vorhanden',
        bindingPaypalReport: 'PayPal-Report vorhanden',
        bindingMollieReport: 'Mollie-Report vorhanden',
        bindingOwnInvoices: 'Eigene Rechnungen vorhanden',
        bindingVendorInvoicesFolder: 'Ordner vendor-invoices hat Dateien',
        bindingPaymentReportsFolder: 'Ordner payment-reports hat Dateien',
        frozen: 'Der Monat ist verschickt; seine Checkliste ist eingefroren.',
        labelRequired: 'label required',
        bindingInUse: 'Diese Verknüpfung wird bereits verwendet.',
        itemNotFound: 'item not found',
        invalidRequirement: 'invalid requirement',
        mayBeEmptyOnlyReports: '„Kann leer sein“ gibt es nur für Stripe-, PayPal- und Mollie-Report.',
    },

    // src/server/services/paypal.ts
    SERVICES_PAYPAL_TEXTS: {
        months: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        reportTitle: 'PayPal {monthName} {year}',
        eventLabels: {
            T0003: 'Website Payment',
            T0006: 'Express Checkout Payment',
            T0007: 'Website Payment',
            T0300: 'Bank Deposit to PP Account',
            T0400: 'General Withdrawal - Bank Account',
        } as Record<string, string>,
        genericPayment: 'Payment',
        period: '01/{mm}/{year} – {monthName} {year}',
        headerBrand: 'PayPal',
        statementFor: 'Statement for {monthName} {year}',
        balanceSummary: 'Balance Summary',
        availableBeginning: 'Available beginning',
        availableEnding: 'Available ending',
        activitySummary: 'Activity Summary',
        beginningAvailableBalance: 'Beginning Available Balance',
        paymentsReceived: 'Payments received',
        paymentsSent: 'Payments sent',
        withdrawals: 'Withdrawals and Debits',
        deposits: 'Deposits and Credits',
        fees: 'Fees',
        endingAvailableBalance: 'Ending Available Balance',
        transactionHistory: 'Transaction History',
        colDate: 'Date',
        colDescription: 'Description',
        colNameEmail: 'Name / Email',
        colGross: 'Gross',
        colFee: 'Fee',
        colNet: 'Net',
        noTransactions: 'Keine Transaktionen in diesem Zeitraum.',
        httpError: 'PayPal {context}: HTTP {status}{code}{text}',
        httpErrorCode: ' {code}',
        httpErrorText: ' – {text}',
        transactionSearchHint: ' (Hinweis: im PayPal-Developer-Dashboard für diese App das Feature „Transaction Search" aktivieren; außerdem prüfen, ob der Sandbox-/Live-Modus zu den eingegebenen Zugangsdaten passt.)',
        notConfigured: 'PayPal not configured',
    },

    // src/server/services/pdf.ts
    SERVICES_PDF_TEXTS: {
        // Labels of the fallback invoice renderer (English, see pdf.ts).
        labels: {
            invoice: 'INVOICE', invoiceNumber: 'Invoice number', reference: 'Reference',
            date: 'Date', period: 'Period', billTo: 'Bill to', vatId: 'VAT ID', register: 'Company reg.',
            description: 'Description', qty: 'Qty', unitPrice: 'Unit price', net: 'Net',
            vatRate: 'VAT %', gross: 'Gross', totalNet: 'Total net', vat: 'VAT', totalGross: 'Total gross',
            reverseChargeNote: 'Reverse charge — the recipient of the service is liable for tax.',
        },
        fallbackFileStem: 'beleg',
        invoiceNotFound: 'invoice {id} not found',
        draftNotRenderable: 'cannot render PDF for a draft invoice (finalize first)',
    },

    // src/server/services/stripeReports.ts
    SERVICES_STRIPE_REPORTS_TEXTS: {
        months: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        // The two Reporting-API reports, as named in the file name and in `skipped`.
        reportBalanceChange: 'Balance Change',
        reportPayouts: 'Payouts',
        runFailed: 'Stripe-Report {reportType}: Status {status}{error}',
        runFailedError: ' ({error})',
        noResultFile: 'Stripe-Report {reportType}: keine Ergebnisdatei',
        // Brand in the name like the statements ('Stripe {brandName} {monthName} {year}'), 38a.
        csvFileName: 'Stripe {brandName} {report} {monthName} {year}.csv',
        // An entry in `skipped`: which brand, which report, why.
        skipped: '{brandName} {report}: {reason}',
        reasonNoData: 'für den Monat noch keine Daten bei Stripe',
        reasonNoType: 'Report-Typ bei Stripe nicht verfügbar',
    },

    // src/server/services/mailer.ts
    SERVICES_MAILER_TEXTS: {
        blockedDevSafe: 'Testinstanz ohne Test-Mailadresse (Einstellungen → E-Mail) — nicht versendet. Hier wird nie an den echten Empfänger geschickt.',
        blockedTestMode: 'Test-Mail-Modus ohne Test-Mailadresse (Einstellungen → E-Mail) — nicht versendet. Im Test-Mail-Modus wird nie an den echten Empfänger geschickt.',
        tagDev: 'DEV',
        tagTest: 'TEST',
        testSubject: '[{tag}->{to}] {subject}',
    },

    // src/server/services/dispatch.ts
    // src/server/services/mailTexts.ts — the placeholder {productlist}, a table of what the customer ordered
    // (operator, 2026-10-26: „Das wird als Table angezeigt mit Menge und Produkt als Header/Title"). Status: offen.
    SERVICES_MAIL_TEXTS_TEXTS: {
        productListQuantity: 'Menge',
        productListProduct: 'Produkt',
        // A mail with several documents: a line naming the document before its table.
        productListDocument: '{invoiceNumber}:',
        // The download link of the document ({invoiceUrl}); the name comes from constants.ts.
        documentUrlTip: 'Dazu {placeholder}: Link zum Herunterladen des Belegs (nur bei einem Beleg; nackt oder als Link-Adresse).',
        // {invoiceUrlList}: a table, every document of the mail with its own download link (operator, 27.10.2026, „142a").
        documentUrlListTip: '{placeholder}: Tabelle mit jedem Beleg der Mail und seinem Download-Link (auch bei mehreren Belegen; in einer eigenen Zeile).',
        documentUrlListNumber: 'Beleg',
        documentUrlListLink: 'Download',
        // Saving a mail text with a {name} that is no placeholder (operator, 27.10.2026, „144a"). It warns, it does not block.
        unknownPlaceholders: 'Unbekannter Platzhalter: {names} — er stünde so in der E-Mail. Bitte die Schreibweise prüfen (Groß-/Kleinschreibung zählt).',
        // Mail preview: stands in the address where the real key of a document will be.
        sampleLinkKey: 'beispiel',
    },

    SERVICES_DISPATCH_TEXTS: {
        subjectEmpty: 'Der Betreff darf nicht leer sein.',
        invoiceNotFound: 'Rechnung nicht gefunden.',
        draftNotSendable: 'Entwürfe können nicht versendet werden — bitte zuerst finalisieren.',
        noEmail: 'Für diese Rechnung ist keine E-Mail-Adresse hinterlegt.',
        nothingSelected: 'Keine Rechnung ausgewählt.',
        invalidSelection: 'Ungültige Auswahl.',
        someNotFound: 'Mindestens eine Rechnung wurde nicht gefunden.',
        draftsInSelection: 'Entwürfe können nicht versendet werden ({count} in der Auswahl) — bitte zuerst finalisieren.',
        onlyFinalizedOrSent: 'Nur finalisierte oder versendete Rechnungen können versendet werden ({numbers}).',
        severalCustomers: 'Die Auswahl enthält Rechnungen mehrerer Kunden — versendet wird je Kunde, an seine Adresse.',
        cancelledNotSent: 'Stornierte Rechnungen werden nicht versendet ({numbers}).',
        someWithoutEmail: 'Für mindestens eine Rechnung ist keine E-Mail-Adresse hinterlegt.',
    },

    // src/server/services/documents.ts
    SERVICES_DOCUMENTS_TEXTS: {
        monthsEn: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        invalidFileName: 'invalid file name',
        invalidFolder: 'invalid folder',
        pathEscapes: 'path escapes month directory',
        notAFile: 'not a file',
    },

    // src/server/services/aboBilling.ts
    SERVICES_ABO_BILLING_TEXTS: {
        tooManyPeriods: 'Mehr als {max} offene Perioden (nächstes Abrechnungsdatum {nextDay}). Bitte das Datum im Abo prüfen.',
        noNextBillingDate: 'Kein nächstes Abrechnungsdatum — bitte im Abo setzen.',
        noProductName: 'Das Abo hat keinen Produktnamen.',
        customerNotFound: 'Kunde {customerId} nicht gefunden.',
        viesUnreachable: 'Die USt-IdNr. konnte nicht geprüft werden (VIES nicht erreichbar) — der nächste Lauf versucht es erneut.',
    },

    // src/server/services/auth.ts
    SERVICES_AUTH_TEXTS: {
        passwordTooShort: 'Passwort muss mindestens {min} Zeichen haben.',
        passwordTooWeak: 'Passwort zu schwach: bitte verschiedene Zeichenarten verwenden (Groß-/Kleinbuchstaben, Ziffern, Sonderzeichen).',
    },

    // src/shared/countries.ts
    SHARED_COUNTRIES_TEXTS: {
        unknown: 'Unbekanntes Land „{code}": bitte ein Land aus der Liste wählen (ISO-Code) oder leer lassen.',
    },

    // src/server/services/brandConfig.ts
    SERVICES_BRAND_CONFIG_TEXTS: {
        smtpPartial: 'SMTP-Zugang der Marke unvollständig: Host, Benutzer und Passwort gehören zusammen — alle drei oder keins (dann gilt der allgemeine Zugang).',
        smtpPartialClearFirst: '{smtpPartial} Zum Zurückfallen auf den allgemeinen Zugang zuerst Host und Benutzer leeren.',
        smtpPartialNotSent: '{smtpPartial} Marke {brand} — nicht versendet.',
        devSafeLiveMode: 'DEV-SAFE: Marke {brand} steht bei {psp} auf LIVE. Auf einer Nicht-Produktions-Instanz wird nicht live abgerechnet. Marke auf Testmodus stellen oder DEV_SAFE=false setzen.',
        devSafeLiveKeyInTestSlot: 'DEV-SAFE: Im Test-Schlüsselfeld von Marke {brand} ({psp}) liegt ein LIVE-Schlüssel. Abgelehnt — auf einer Nicht-Produktions-Instanz wird nicht live abgerechnet.',
    },

    // src/server/services/bulkTrash.ts
    SERVICES_BULK_TRASH_TEXTS: {
        idsExpected: 'ids: nicht leere Liste von Kennungen erwartet',
        atMost: 'Höchstens {max} auf einmal',
        unknownId: 'Unbekannte Kennung',
        notFound: 'Nicht gefunden',
        alreadyInTrash: 'Liegt schon im Papierkorb',
    },

    // src/server/services/ingest.ts
    SERVICES_INGEST_TEXTS: {
        unnamedPdf: '(unbenannt).pdf',
        stripeTaxInvoiceFileName: 'Stripe Tax Invoice {name}',
        fallbackFileName: 'beleg-{stamp}.pdf',
        reasonNoAttachment: 'E-Mail ohne PDF-Anhang',
        reasonLinkNoPdf: 'Link-Rechnung: PDF konnte nicht geladen werden',
        // Mollie fee invoice (mail → Invoices API, 03.11.2026)
        reasonMollieNoToken: 'Mollie-Rechnung: kein Mollie-API-Zugriffstoken in den Einstellungen',
        reasonMolliePermission: 'Mollie-Rechnung: dem Mollie-Token fehlt das Recht invoices.read',
        reasonMollieNotFound: 'Mollie-Rechnung: Rechnung bei Mollie nicht gefunden',
        reasonMolliePeriod: 'Mollie-Rechnung: Leistungsmonat nicht eindeutig (keiner oder mehrere Monate in den Rechnungszeilen)',
        reasonMolliePdf: 'Mollie-Rechnung: PDF konnte nicht geladen werden',
        reasonMollieApi: 'Mollie-Rechnung: Abruf bei Mollie fehlgeschlagen',
        mollieInvoiceFileName: 'Mollie Rechnung {reference}.pdf',
        mollieVendor: 'Mollie',
        reasonNoInvoicePdf: 'Keine Rechnung erkannt (PDF ohne Datum/Betrag)',
        reasonNeedsReview: 'Beleg nicht automatisch lesbar (evtl. Scan) — bitte prüfen',
        reasonDefault: 'Import fehlgeschlagen',
        // from/subject arrive HTML-escaped.
        problemMailHtml: '<p><strong>Beleg-Eingang: {reason}</strong></p><p>Von: {from}<br>Betreff: {subject}</p><p><a href="{retryUrl}">Import wiederholen</a> · <a href="{ignoreUrl}">Ignorieren</a> · <a href="{openUrl}">Im Backoffice öffnen</a></p>',
        problemMailText: 'Beleg-Eingang: {reason}\nVon: {from}\nBetreff: {subject}\n\nImport wiederholen: {retryUrl}\nIgnorieren: {ignoreUrl}\nIm Backoffice öffnen: {openUrl}',
        problemMailSubject: 'Beleg-Eingang: {reason}',
        imapNotConfigured: 'IMAP nicht konfiguriert',
        mailGone: 'mail nicht mehr vorhanden',
        notFound: 'not found',
    },

    // src/server/services/settings.ts
    SERVICES_SETTINGS_TEXTS: {
        currencyNotIso: 'Währung: „{code}“ ist kein Währungscode nach ISO 4217.',
        currencyFractionDigits: 'Währung: „{code}“ hat {digits} Nachkommastellen — das backoffice rechnet mit den Zahlungsdiensten in Cent und braucht zwei.',
        currencyLocked: 'Die Währung ist festgelegt, seit es Belege gibt — sie lässt sich nicht mehr ändern.',
        testMailAddressRequired: 'Test-Mail-Modus: Bitte eine gültige Test-Mailadresse eintragen.',
        pdfNameUnknownPlaceholder: 'Dateiname der Rechnungs-PDF: unbekannter Platzhalter {detail}. Möglich: {placeholders}.',
        pdfNameForbiddenCharacter: 'Dateiname der Rechnungs-PDF: Die Zeichen / \\ : * ? " < > | gehen in Dateinamen nicht.',
        pdfNameNotUnique: 'Dateiname der Rechnungs-PDF: Das Muster braucht {ref} oder {number}, sonst heißen zwei Rechnungen gleich.',
        devSafePaypalLive: 'DEV-SAFE: PayPal steht auf LIVE. Auf einer Nicht-Produktions-Instanz wird nicht mit dem Live-Konto gearbeitet. Auf Testmodus stellen oder DEV_SAFE=false setzen.',
    },

    // src/server/services/stripe.ts
    SERVICES_STRIPE_TEXTS: {
        months: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        sheetTitle: 'Stripe {monthName} {year}',
        reportTitle: 'Stripe {brandName} {monthName} {year}',
        // charge.status → the label Stripe's Payments export uses.
        statusRefunded: 'Refunded',
        statusPaid: 'Paid',
        statusFailed: 'Failed',
        statusPending: 'Pending',
        keyNotConfigured: 'Stripe secret key not configured',
        noBrandKey: 'Für keine Marke ist ein Stripe-Schlüssel hinterlegt: {brands}.',
        // One entry in `skipped` / in noBrandKey's list (38a: a brand without a key does not fail the others).
        skippedBrand: '{brandName}: {reason}',
        reasonNoKey: 'kein Stripe-{mode}-Schlüssel',
        reasonNoMovement: 'keine Bewegung, kein Statement',
        modeTest: 'Test',
        modeLive: 'Live',
    },

    // src/server/services/stripeWebhook.ts
    SERVICES_STRIPE_WEBHOOK_TEXTS: {
        months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        monthLabel: '{monthName} {year}',
        modeTest: 'Test',
        modeLive: 'Live',
        noKey: 'Kein Stripe-{mode}-Key für Marke {brand} hinterlegt.',
        baseUrlNotReachable: 'Öffentliche Backoffice-URL muss eine von Stripe erreichbare https-Adresse sein (aktuell: {base}). Lokale Adressen wie backoffice.local kann Stripe nicht erreichen — für lokale Tests „stripe listen" nutzen.',
        endpointUpdated: 'Bestehender Endpoint aktualisiert.',
        endpointAdopted: 'Vorhandenen Endpoint übernommen.',
        endpointAdoptedNoSecret: 'Vorhandenen Endpoint übernommen — Signing-Secret bitte einmalig manuell eintragen (Stripe gibt es bei bestehenden Endpoints nicht erneut heraus).',
        // Description of the endpoint in the Stripe dashboard.
        endpointDescription: 'MSBC backoffice {brand} ({mode})',
        endpointCreated: 'Webhook angelegt und Signing-Secret gespeichert.',
    },

    // src/server/services/subscriptions.ts
    SERVICES_SUBSCRIPTIONS_TEXTS: {
        // Period labels are German (all customers are German).
        months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        periodLabel: '{monthName} {year}',
        onePosition: 'Ein Abo hat genau eine Position mit Produktnamen.',
        pspStatusReadOnly: 'PSP-Abo-Status nur über /api/subscription/{cancel,pause,resume} änderbar',
        nextBillingDateInvalid: 'Nächstes Abrechnungsdatum fehlt oder ist ungültig.',
    },

    // src/server/services/vitalpilzeWebhook.ts
    SERVICES_VITALPILZE_WEBHOOK_TEXTS: {
        noDetail: 'ohne Angabe',
    },

    // src/server/services/vpDeliveryQueue.ts
    SERVICES_VP_DELIVERY_QUEUE_TEXTS: {
        noDetail: 'ohne Angabe',
        gaveUpMailText: 'Eine Meldung an vitalpilze konnte {days} Tage lang nicht zugestellt werden und wurde aufgegeben.\n\nEreignis:  {event}\nVorgang:   {label}\nKennung:   {deliveryId}\nVersuche:  {attempts}\nZuletzt:   {lastError}\n\nWas das bedeutet:\nHandelt es sich um eine Freischaltung (access.grant), fehlt auf vitalpilze das\nbezahlt-bis-Datum. Das dortige Sicherheitsnetz uebergeht solche Zugaenge - dieser\nZugang laeuft also NICHT von selbst aus und muss von Hand geprueft werden.\n\nDer Eintrag wurde nicht geloescht und steht weiterhin in der Warteschlange.',
        gaveUpMailSubject: 'Zustellung an vitalpilze aufgegeben: {event}',
    },

    // src/server/services/vpTransport.ts
    SERVICES_VP_TRANSPORT_TEXTS: {
        notConfigured: 'nicht eingerichtet',
        wrongEnvironment: 'HTTP 421 — falsche Umgebung, endgültig abgelehnt: {body}',
        httpError: 'HTTP {status}: {body}',
        timeout: 'Zeitueberlauf ({seconds}s)',
        transportError: 'Transportfehler',
    },

    // src/server/services/webhooks.ts
    SERVICES_WEBHOOKS_TEXTS: {
        wrongEnvironment: 'HTTP 421 — falsche Umgebung (dev ↔ prod), abgelehnt',
        converterWebhookName: 'Converter (MSBC) — Provisioning',
    },

    // src/server/services/ninoxAboImport.ts
    SERVICES_NINOX_ABO_IMPORT_TEXTS: {
        columnMissing: 'Spalte „{name}" fehlt in der CSV (Kopf: {head})',
        rowUnreadable: 'Zeile {nr}: unlesbar (Interval „{interval}", Start „{start}", Nächstes „{next}", Preis „{price}")',
        customerNotUnique: 'Kunde „{company}": {count} Treffer im Kundenstamm (erwartet genau 1) — Abbruch, nichts geschrieben.',
    },

    // src/server/services/goLiveCut.ts
    SERVICES_GO_LIVE_CUT_TEXTS: {
        artTestAuthorSales: 'Verkäufe der Testdozenten',
        artTestAuthorStatements: 'Abrechnungen der Testdozenten',
        artTestAuthorChanges: 'Änderungsprotokoll der Testdozenten',
        artTestAuthors: 'Testdozenten',
        artTestCatalogItems: 'Test-Katalogeinträge',
        artSubscriptionBillings: 'Abo-Verknüpfungen (subscriptionbillings)',
        artInvoices: 'Rechnungen und Storno-Belege',
        artTransactions: 'Transaktionen',
        artPendingCheckouts: 'Offene Checkouts (pendingcheckouts)',
        artVpDeliveries: 'vitalpilze-Warteschlange (vpdeliveries)',
        artShopSubscriptions: 'Abos aus dem Shop (Stripe, Mollie)',
        artCustomers: 'Kunden (auch im Papierkorb)',
        artStatementRecords: 'Kontoauszüge (Einträge)',
        artNoMovementNotes: 'Vermerke „keine Bewegung“',
        artCertificates: 'Urkunden',
        artInvoicePdfs: 'Rechnungs-PDFs',
        artStatementFiles: 'Kontoauszüge (Dateien)',
        artMonthZips: 'Monats-ZIPs (exports/)',
        artSalesReports: 'Umsatzberichte',
        artCertificatePdfs: 'Urkunden-PDFs',
        artCatalogRest: 'Katalog (übrige Einträge)',
        artAuthorsRest: 'Dozenten (übrige)',
        artVendorInvoices: 'Lieferantenrechnungen',
        artSetup: 'Einrichtung',
        artCounters: 'Zähler',
        artLogsSessions: 'Logs und Sitzungen',
        reasonCatalog: 'Preise, Gebühren, Dozentenzuordnung: gesicherte Arbeit, dev wird zu www (05.09.2026)',
        reasonAuthors: 'echte Dozenten',
        reasonVendorInvoices: 'echte Belege aus dem Postfach',
        reasonSetup: 'Einstellungen, Marken, Benutzer, Rollen, Vorlagen, Mail-Rahmen, Webhooks, Ausgabentypen, Zuordnungen, Prüfpunkte, Monats-Checklisten',
        reasonCounters: 'Schritt 3: scripts/reset-counters.ts',
        reasonLogsSessions: 'Betrieb, kein Bestand; verfallen von selbst',
        abortLegacyFound: '{n} importierte Altrechnung(en) gefunden — der Import (Schritt 5) ist schon gelaufen. Der Schnitt gehört davor.',
        abortManualAbos: '{n} manuelle(s) Abo(s) gefunden — echte Kundenabos, der Schnitt gehört vor ihren Import.',
        abortForeignSales: '{n} Verkauf/Verkäufe echter Dozenten hängen an Rechnungen, die gingen — nicht entschieden, was damit geschieht.',
        testAuthorsMissing: 'Testdozent(en) nicht vorhanden: {refs}',
        abort: 'Abbruch: {reasons}',
    },

    // src/server/services/invoices.ts
    SERVICES_INVOICES_TEXTS: {
        // Position text of an issued invoice (admin, 03.11.2026, 62b/63a)
        lineTextOnlyIssued: 'Nur finalisierte oder versendete Rechnungen — einen Entwurf über die normale Maske bearbeiten.',
        lineTextNotCreditNote: 'Der Text eines Storno-Belegs wird nicht geändert.',
        lineTextNotImported: 'Importierte Rechnungen (Altsystem) werden nicht geändert.',
        lineTextNotCancelled: 'Diese Rechnung ist storniert — der Text wird nicht mehr geändert.',
        lineTextBadIndex: 'Diese Position gibt es nicht.',
        lineTextEmpty: 'Der neue Text ist leer.',
        lineTextTooLong: 'Der neue Text ist zu lang (höchstens {max} Zeichen).',
        lineTextUnchanged: 'Der Text ist unverändert.',
        lineTextReasonRequired: 'Bitte eine Begründung angeben.',
        lineTextReasonTooLong: 'Die Begründung ist zu lang (höchstens {max} Zeichen).',
        lineTextConcurrent: 'Die Rechnung wurde inzwischen geändert — bitte neu laden.',
        lineTextNoPdf: 'Das bisherige PDF der Rechnung ist nicht da — ohne es wird nichts geändert.',
        invoiceNotFoundId: 'invoice {id} not found',
        invoiceNotFound: 'invoice not found',
        onlyDraftsEditable: 'Nur Entwürfe können bearbeitet werden.',
        onlyDraftsDeletable: 'Nur Entwürfe können gelöscht werden.',
        viesUnreachable: 'Die USt-IdNr. konnte nicht geprüft werden (VIES nicht erreichbar). Ob Reverse Charge gilt, ist offen — bitte später erneut finalisieren.',
        reverseChargeNeedsZero: 'Die USt-IdNr. {vatId} ist gültig, es gilt Reverse Charge — die Positionen müssen 0 % tragen (im Formular „Steuersatz übernehmen").',
        noReverseChargeNoZero: `Für diesen Kunden gilt kein Reverse Charge — eine Position mit 0 % passt nicht (im Formular „Steuersatz übernehmen").`,
        invoiceVoid: 'invoice {id} is void',
        // rates: the sorted rates joined with ' % und '.
        mixedRates: 'Diese Rechnung mischt die Steuersätze {rates} %. Der Beleg kann nur einen Satz ausweisen — bitte je Steuersatz eine eigene Rechnung anlegen.',
        mixedRatesJoin: ' % und ',
        cancelLine: 'Storno: {description}',
        processingFee: 'Bearbeitungsgebühr',
        partialRefundLine: 'Teilerstattung zu Rechnung {invoiceNumber}',
        cancelPeriodLabel: 'Storno zu {invoiceNumber}',
        creditNoteNotCancellable: 'Das ist ein Storno-Beleg — er kann nicht selbst storniert werden.',
        pspRefundNotCancel: 'Diese Rechnung lief über einen Zahlungsdienstleister — dort wird erstattet, nicht storniert.',
        onlyFinalizedCancellable: 'Nur finalisierte oder versendete Rechnungen können storniert werden — einen Entwurf löschen.',
        alreadyCancelled: 'Zu dieser Rechnung gibt es bereits einen Storno-Beleg.',
        creditNoteNotReissued: 'Ein Storno-Beleg wird nicht neu ausgestellt.',
        creditNoteNotDuplicated: 'Ein Storno-Beleg wird nicht dupliziert.',
        cancelledNotDuplicated: 'Diese Rechnung ist storniert — dafür gibt es „Neu ausstellen“.',
    },

    // src/server/services/zugferdGenerate.ts
    SERVICES_ZUGFERD_GENERATE_TEXTS: {
        dataMissing: 'cannot build ZUGFeRD XML, missing: {missing}',
        // The terms checkZugferdData reports as missing.
        missingInvoiceNumber: 'BT-1 Rechnungsnummer',
        missingIssueDate: 'BT-2 Rechnungsdatum',
        missingCurrency: 'BT-5 Währung',
        missingSellerName: 'BT-27 Name des Verkäufers',
        missingSellerEmail: 'BT-34 elektronische Adresse des Verkäufers',
        missingSellerCountry: 'BT-40 Land des Verkäufers',
        missingBuyerName: 'BT-44 Name des Käufers',
        missingBuyerEmail: 'BT-49 elektronische Adresse des Käufers',
        missingBuyerCountry: 'BT-55 Land des Käufers',
        missingLineItems: 'BG-25 mindestens eine Position',
        missingSellerTaxId: 'BT-31/BT-32 USt-IdNr. oder Steuernummer des Verkäufers',
        // Texts inside the e-invoice XML (values arrive XML-escaped).
        exemptionReasonReverseCharge: 'Reverse charge — the recipient of the service is liable for tax / Der Leistungsempfänger ist Steuerschuldner (Art. 196 MwStSystRL)',
        noteReferenceNumber: 'Reference number / Referenznummer: {ref}',
        noteCreditNoteFor: 'Credit note for invoice / Stornorechnung zu Rechnung {invoiceNumber}',
    },

    // src/server/services/pdfTemplate/interpreter.ts
    SERVICES_PDF_TEMPLATE_INTERPRETER_TEXTS: {
        pageheadWithoutProducts: '<pagehead> ohne <products> — der Kopf wird gezeichnet, aber nach einem Seitenumbruch nicht wiederholt. Die Tabelle gehoert INNERHALB der Klammer.',
        productsOnlyInInvoices: '<products> steht nur in Rechnungsvorlagen zur Verfuegung',
        unknownCommand: 'Unbekannter Befehl <{tag}>',
        groupContainsProducts: '<group> enthaelt <products> — eine Positionsliste soll umbrechen duerfen. Die Gruppe wird normal gezeichnet, sie haelt hier nichts zusammen.',
        groupTooTall: '<group> ist {heightMm} mm hoch und passt damit auf keine leere Seite ({pageInnerMm} mm) — der Block fliesst normal und darf umbrechen.',
        imageNotFound: 'Bild nicht gefunden: {src}',
        imageError: 'Bild-Fehler ({src}): {error}',
        pageCountOnlyInHeaderFooter: '{{key}} steht nur in <header> und <footer> zur Verfuegung — im <content> ist die Gesamtseitenzahl noch nicht bekannt',
        unknownPlaceholder: 'Unbekannter Platzhalter {{key}}',
        invalidColor: 'Ungueltige Farbe "{value}" — es wird schwarz gezeichnet (erwartet: #rgb oder #rrggbb)',
        inListSpaces: '<if operator="in">: "{value}" steht in der Liste "{list}" nur mit umgebenden Leerzeichen — die Bedingung greift NICHT. Die Liste ohne Leerzeichen nach dem Komma schreiben.',
        unknownOperator: '<if>: unbekannter Operator "{op}" — die Bedingung gilt als nicht erfuellt und der Block wird NICHT gezeichnet. Erlaubt sind ==, !=, <, <=, >, >=, in sowie die Wortformen equal, not equal, lower, lower equal, higher, higher equal.',
    },

    // src/server/services/pdfTemplate/xml.ts
    SERVICES_PDF_TEMPLATE_XML_TEXTS: {
        invalidXml: 'XML ungültig (Zeile {line}): {msg}',
        unreadableXml: 'XML konnte nicht gelesen werden: {error}',
        noRootElement: 'Kein Wurzelelement gefunden (erwartet z. B. <deja-invoice>).',
    },

    // src/server/services/pdfTemplate/fromInvoice.ts
    SERVICES_PDF_TEMPLATE_FROM_INVOICE_TEXTS: {
        // Headings are bilingual because the templates are (all three print "Invoice / Rechnung").
        titles: {
            invoice: 'Invoice / Rechnung',
            credit_note: 'Credit Note / Stornorechnung',
        },
        documentReference: 'for invoice / zu Rechnung {invoiceNumber}',
    },

    // src/server/services/pdfTemplate/render.ts
    SERVICES_PDF_TEMPLATE_RENDER_TEXTS: {
        invalidTemplatePath: 'Ungültiger Vorlagen-Pfad',
        templateNotFound: 'Vorlage nicht gefunden',
        invalidName: 'Bitte einen gültigen Namen angeben',
        templateExists: 'Eine Vorlage mit diesem Namen existiert bereits',
        onlyPngJpg: 'Nur PNG/JPG erlaubt',
        // Sample address a new template shows instead of the real one of the deja template.
        newTemplatePlaceholderAddress: 'Meine Firma, Musterstraße 1, 12345 Musterstadt',
        // Description of the e-invoice XML attached to the PDF.
        zugferdAttachmentDescription: 'Invoice data / Rechnungsdaten (ZUGFeRD, EN 16931)',
    },

    // src/server/email/frames.ts
    EMAIL_FRAMES_TEXTS: {
        noContentPlaceholder: 'Der Rahmen enthält kein {content} — ohne diese Stelle ginge die Mail ohne Text raus.',
        logoWidthInvalid: 'Logobreite: ganze Zahl zwischen {min} und {max}',
        footerColorLabel: 'Farbe der Fußleiste',
        footerTextColorLabel: 'Schriftfarbe der Fußleiste',
        colorInvalid: '{label}: Farbwert wie #1f2430',
        logoOnlyPngJpg: 'Logo: nur PNG oder JPG',
    },

    // src/server/utils/iban.ts
    UTILS_IBAN_TEXTS: {
        missing: 'IBAN fehlt',
        invalidForm: 'IBAN hat eine ungültige Form (erwartet: zwei Buchstaben, zwei Ziffern, dann Kontokennung)',
        lengthOutOfRange: 'IBAN hat {length} Stellen — möglich sind 15 bis 34',
        wrongCountryLength: 'Eine {country}-IBAN hat {expected} Stellen, diese hat {actual}',
        checksumWrong: 'Prüfziffer stimmt nicht — vermutlich ein Tippfehler',
    },

    // src/server/utils/envPairing.ts
    UTILS_ENV_PAIRING_TEXTS: {
        outboundBlocked: 'DEJA_ENV fehlt oder ist ungültig ("{raw}") — Maschinenverbindungen gesperrt',
    },

    // src/server/core/seed.ts
    CORE_SEED_TEXTS: {
        // Labels of the seeded roles.
        roleAdmin: 'Administrator',
        roleSales: 'Vertrieb',
        roleAccounting: 'Buchhaltung',
        roleReadonly: 'Nur Lesen',
    },

    // src/server/core/stale.ts
    CORE_STALE_TEXTS: {
        changedSinceLoaded: '{what} changed since it was loaded',
    },

    // src/server/models/brandConfig.ts
    MODELS_BRAND_CONFIG_TEXTS: {
        contactEmailInvalid: 'Kontakt-Mail ist keine E-Mail-Adresse',
        impressumUrlInvalid: 'Impressum-Adresse muss mit http:// oder https:// beginnen',
    },

    // src/server/models/settings.ts — what a text field in Einstellungen holds until the operator
    // sets it. The other defaults of the settings are values: shared/constants.ts, SETTINGS_DEFAULTS.
    SETTINGS_TEXT_DEFAULTS: {
        // Rechnungs-Mail: globaler Default-Betreff + -Text (pro Kunde überschreibbar).
        invoiceMailSubject: 'Ihre Rechnungen für {month} {year}',
        // HTML-Body (wird im Backoffice mit einem WYSIWYG-Editor bearbeitet).
        invoiceMailBody: [
            '<p>Hallo {name},</p>',
            '<p>anbei die Rechnungen für {month} {year}.</p>',
            '<p>LG Detlef</p>',
        ].join(''),
        // Einzelversand. Der Sammeltext („Ihre Rechnungen für August 2026") ist bei einem einzelnen
        // Kauf gleich doppelt falsch: Plural, und ein Monatsbezug, den ein Kauf am 3. September nicht
        // hat. Entscheidend ist NICHT Abo vs. Einmalkauf, sondern wie viele Belege in der Mail liegen —
        // siehe sendGroup. Leer lassen = der Sammeltext gilt weiterhin, es gibt also nie eine leere Mail.
        singleInvoiceMailSubject: 'Ihre Rechnung {invoiceNumber}',
        singleInvoiceMailBody: [
            '<p>Hallo {name},</p>',
            '<p>anbei Ihre Rechnung {invoiceNumber} als PDF.</p>',
            '<p>LG Detlef</p>',
        ].join(''),
        // Storno-/Gutschriftmail. Eigener Text, weil eine Rechnungsmail („anbei die Rechnungen")
        // für eine Erstattung schlicht falsch klingt — und der Beleg negative Beträge enthält.
        creditNoteMailSubject: 'Ihre Stornorechnung',
        creditNoteMailBody: [
            '<p>Hallo {name},</p>',
            '<p>anbei die Stornorechnung zu Ihrer Erstattung. Der Betrag wurde Ihnen zurückerstattet.</p>',
            '<p>LG Detlef</p>',
        ].join(''),
        // Monthly export to the tax advisor. English, like the rest of that mail and of the statements.
        // A line per checklist item whose provider had no movement (and whose mail note is switched on).
        exportNoMovementLine: '{label}{brands}: no movement in {period}, no statement attached.',
        // In a PSP statement when the provider's balance could not be fetched — instead of a silent 0.00.
        pspBalanceUnavailableNote: 'The account balance could not be retrieved from {provider}; opening and closing balance are not shown.',
    },

    // ══ Client: Rahmen, Bausteine, Benutzer, Rollen, Protokolle ══

    // Client texts for the frame: layout, dashboard, hooks, shared components and the user/role/log pages.

    // src/client/layout/LayoutManager.tsx
    LAYOUT_LAYOUT_MANAGER_TEXTS: {
        nav: {
            dashboard: 'Dashboard',
            customers: 'Kunden',
            certificateDispatch: 'Zertifikat-Versand',
            finance: 'Finanzen',
            invoices: 'Rechnungen',
            subscriptions: 'Abos',
            transactions: 'Transaktionen',
            export: 'Monats-Export',
            expenseMapping: 'Beleg-Zuordnung',
            catalog: 'Artikel-Katalog',
            authors: 'Dozenten',
            authorStatements: 'Dozenten-Abrechnung',
            vatSettings: 'MwSt / OSS',
            system: 'System',
            users: 'Benutzer',
            roles: 'Rollen & Rechte',
            settings: 'Einstellungen',
            templates: 'Vorlagen',
            webhooks: 'Webhooks',
            logs: 'Protokolle',
            trash: 'Papierkorb',
        },
        saveFailed: 'Speichern fehlgeschlagen',
        saveError: '{name}: {error}',
        unsavedAria: 'ungespeichert',
        noAccess: 'Kein Zugriff auf dieses Modul.',
        unknownComponent: 'Unbekannt: {component}',
        logoAlt: 'deja',
        appTitle: 'Back office',
        dirtyCount: '{n} ungespeichert',
        dirtyListHead: 'Ungespeicherte Tabs',
        saveAll: 'Alle übernehmen',
        menuAria: 'Menü',
        fullscreenEnter: 'Vollbild',
        fullscreenExit: 'Vollbild beenden',
        myProfile: 'Mein Profil',
        logout: 'Abmelden',
        versionTitle: 'Backoffice Version {version}',
        version: 'Version {version}',
        closeDialog: {
            title: 'Ungespeicherte Änderungen',
            text: 'Dieser Tab hat ungespeicherte Änderungen. Was möchtest du tun?',
            save: 'Übernehmen',
            discard: 'Verwerfen',
            cancel: 'Abbrechen',
        },
        exitDialog: {
            title: { back: 'Seite verlassen?', logout: 'Abmelden?', reload: 'Neu laden?' },
            textOne: 'Dieser Tab hat ungespeicherte Änderungen:',
            textMany: 'Diese Tabs haben ungespeicherte Änderungen:',
            discard: { back: 'Verwerfen und verlassen', logout: 'Verwerfen und abmelden', reload: 'Verwerfen und neu laden' },
        },
    },

    // src/client/hooks/useServerList.ts
    HOOKS_USE_SERVER_LIST_TEXTS: {
        loadError: 'Fehler beim Laden',
    },

    // src/client/dashboard/widgets.tsx
    DASHBOARD_WIDGETS_TEXTS: {
        loadError: 'Fehler beim Laden',
        loading: 'lädt…',
        stats: {
            invoicesTotal: 'Rechnungen gesamt',
            drafts: 'Entwürfe',
            unsent: 'Finalisiert, unversendet',
            sent: 'Versendet',
            subscriptions: 'Abos',
        },
        invoicesTab: 'Rechnungen',
        toInvoices: 'Zu den Rechnungen',
        draft: 'Entwurf',
        noUnsentInvoices: 'Keine unversendeten Rechnungen.',
        noInvoicesYet: 'Noch keine Rechnungen.',
        noDueSubscriptions: 'Keine fälligen Abos.',
        subscriptionTab: 'Abo: {name}',
        openSubscription: 'Abo öffnen',
        monthsShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
        presets: {
            thisWeek: 'Diese Woche',
            thisMonth: 'Dieser Monat',
            thisYear: 'Dieses Jahr',
            lastYear: 'Vorjahr',
            lastYears: 'Letzte {n} Jahre',
        },
        grossTotal: 'gesamt (brutto)',
        income: 'Einnahmen',
        expense: 'Ausgaben',
        net: 'Saldo',
        noType: 'ohne Typ',
        balanceLine: 'Saldo · {income} ein / {expense} aus',
        expenseByType: 'Ausgaben nach Typ',
        todos: {
            createFailed: 'Konnte nicht angelegt werden',
            doneFailed: 'Konnte nicht abgehakt werden',
            nothingOpen: 'Nichts offen.',
            markDoneAria: '„{text}" als erledigt markieren',
            newPlaceholder: 'Neuer Punkt…',
            newAria: 'Neuer Punkt',
            add: 'Hinzufügen',
        },
        widgetTitles: {
            stats: 'Kennzahlen',
            todos: 'Arbeitsliste',
            unsent: 'Unversendete Rechnungen',
            recent: 'Letzte Rechnungen',
            dueSubs: 'Fällige Abos',
            revenueChart: 'Umsatz-Chart',
            balanceChart: 'Bilanz (Ein/Aus)',
        },
        revenueInstance: 'Umsatz · {preset}',
        balanceInstance: 'Bilanz · {preset}',
    },

    // src/client/pages/Dashboard.tsx
    PAGES_DASHBOARD_TEXTS: {
        title: 'Dashboard',
        info: 'Überblick über das Back-Office. Über „Ansicht anpassen“ stellst du dir die Widgets selbst zusammen.',
        customize: 'Ansicht anpassen',
        noWidgets: 'Keine Widgets ausgewählt. Über „Ansicht anpassen" kannst du Widgets einblenden.',
        dragToMove: 'Ziehen zum Verschieben',
        narrow: 'Schmal',
        wide: 'Breit',
        remove: 'Entfernen',
        system: 'System',
        database: 'Datenbank: ',
        connected: 'verbunden',
        disconnected: 'getrennt',
        statusAt: ' · Stand {time}',
        loading: 'lädt…',
        drawerTitle: 'Widgets',
        close: 'Schließen',
        drawerHint: 'Ein-/ausblenden bzw. hinzufügen; auf dem Board ziehen, Breite/Zeitraum wählen.',
        noWidgetsAvailable: 'Keine Widgets verfügbar.',
        addWidget: '+ {title} hinzufügen',
        reset: 'Zurücksetzen',
    },

    // src/client/App.tsx
    CLIENT_APP_TEXTS: {
        logoAlt: 'deja',
        bandLocal: 'lokale Entwicklungsumgebung',
        bandBuild: 'Entwicklungsumgebung · Nightly build ({date}){sha}',
        mailBandMissingAddress: 'Test-Mail-Modus ohne Test-Mailadresse: es wird keine Mail versendet',
        mailBandOn: 'Test-Mail-Modus: alle Mails gehen an {to}',
        mailBandDefaultTo: 'die Test-Mailadresse',
        builtAt: ' · gebaut {date}',
    },

    // src/client/pages/Login.tsx
    PAGES_LOGIN_TEXTS: {
        loginFailed: 'Anmeldung fehlgeschlagen',
        logoAlt: 'deja',
        title: 'Back office',
        email: 'E-Mail',
        password: 'Passwort',
        submit: 'Anmelden',
    },

    // src/client/pages/Profile.tsx
    PAGES_PROFILE_TEXTS: {
        canEditNow: 'Du kannst jetzt bearbeiten.',
        nameSaved: 'Name gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        passwordChanged: 'Passwort geändert.',
        passwordChangeFailed: 'Passwortänderung fehlgeschlagen',
        title: 'Mein Profil',
        lockedQueue: 'Dein Profil wird gerade an anderer Stelle bearbeitet (anderer PC / Tab). Du bist in der Warteschlange{position}. Sobald es freigegeben wird, kannst du bearbeiten.',
        lockedQueuePosition: ' (Position {position})',
        account: 'Konto',
        email: 'E-Mail',
        emailHint: 'E-Mail ist dein Login und kann hier nicht geändert werden.',
        role: 'Rolle',
        changeName: 'Name ändern',
        name: 'Name *',
        save: 'Speichern',
        changePassword: 'Passwort ändern',
        currentPassword: 'Aktuelles Passwort *',
        newPassword: 'Neues Passwort *',
        passwordWeak: 'Passwort zu schwach (min. {min} Zeichen, verschiedene Zeichenarten).',
    },

    // src/client/pages/UserForm.tsx
    PAGES_USER_FORM_TEXTS: {
        canEditNow: 'Du kannst jetzt bearbeiten.',
        loadError: 'Fehler beim Laden',
        saved: 'Gespeichert.',
        created: 'Benutzer angelegt.',
        saveFailed: 'Speichern fehlgeschlagen',
        titleSelf: 'Mein Konto (Ansicht)',
        titleReadOnly: 'Benutzer (Ansicht)',
        titleEdit: 'Benutzer bearbeiten',
        titleNew: 'Neuer Benutzer',
        apply: 'Übernehmen',
        create: 'Anlegen',
        selfNotice: 'Dein eigenes Konto kann hier nur angesehen werden. Änderungen am eigenen Profil folgen über das Profil-Menü.',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Benutzer.',
        lockedQueue: 'Dieser Benutzer wird gerade an anderer Stelle bearbeitet (Verwaltung oder eigenes Profil). Du bist in der Warteschlange{position}. Sobald er freigegeben wird, kannst du bearbeiten.',
        lockedQueuePosition: ' (Position {position})',
        name: 'Name *',
        email: 'E-Mail *',
        emailHint: 'E-Mail kann nicht geändert werden.',
        role: 'Rolle *',
        active: 'Aktiv',
        newPasswordOptional: 'Neues Passwort (optional)',
        password: 'Passwort *',
        passwordPlaceholder: 'leer lassen = unverändert',
    },

    // src/client/pages/UsersList.tsx
    PAGES_USERS_LIST_TEXTS: {
        newUserTab: 'Neuer Benutzer',
        userTab: 'Benutzer: {name}',
        movedToTrash: 'Benutzer in den Papierkorb verschoben.',
        deleteFailed: 'Löschen fehlgeschlagen',
        ownAccountNotDeletable: 'Das eigene Konto kann nicht gelöscht werden.',
        columns: {
            name: 'Name',
            email: 'E-Mail',
            role: 'Rolle',
            status: 'Status',
            created: 'Erstellt',
            actions: 'Aktionen',
        },
        active: 'aktiv',
        inactive: 'inaktiv',
        view: 'Ansehen',
        edit: 'Bearbeiten',
        delete: 'Löschen',
        title: 'Benutzer',
        info: 'Mitarbeiter-Konten + Rollenzuweisung',
        newUser: '+ Neuer Benutzer',
        loading: 'lädt…',
        noneFound: 'Keine Benutzer gefunden.',
        toTrash: 'In den Papierkorb',
        deleteQuestion: 'Benutzer „{name}" in den Papierkorb verschieben?',
        deleteNote: 'Nur Admins können den Eintrag dort wiederherstellen.',
    },

    // src/client/pages/RoleEditor.tsx
    PAGES_ROLE_EDITOR_TEXTS: {
        loadError: 'Fehler beim Laden',
        saved: 'Rolle gespeichert.',
        created: 'Rolle angelegt.',
        saveFailed: 'Speichern fehlgeschlagen',
        titleReadOnly: 'Rolle (Ansicht)',
        titleEdit: 'Rolle bearbeiten',
        titleNew: 'Neue Rolle',
        info: 'Lesen=false blendet das Modul aus. Schreiben/Löschen setzen Lesen automatisch voraus. Ein Strich (—) bedeutet: für dieses Modul nicht anwendbar.',
        apply: 'Übernehmen',
        create: 'Anlegen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Rollen & Rechte.',
        label: 'Bezeichnung *',
        key: 'Schlüssel * ',
        keyHint: '(klein, eindeutig)',
        keyPlaceholder: 'z.B. support',
        rightsPerModule: 'Rechte je Modul',
        module: 'Modul',
        notApplicable: 'für dieses Modul nicht anwendbar',
    },

    // src/client/pages/RolesList.tsx
    PAGES_ROLES_LIST_TEXTS: {
        newRoleTab: 'Neue Rolle',
        roleTab: 'Rolle: {label}',
        deleted: 'Rolle gelöscht.',
        deleteFailed: 'Löschen fehlgeschlagen',
        columns: {
            role: 'Rolle',
            key: 'Schlüssel',
            rights: 'Rechte',
            users: 'Benutzer',
            actions: 'Aktionen',
        },
        systemBadge: 'System',
        fullAccess: 'Vollzugriff',
        systemNotEditable: 'System-Rolle (nicht editierbar)',
        systemNotDeletable: 'System-Rolle (nicht löschbar)',
        edit: 'Bearbeiten',
        view: 'Ansehen',
        delete: 'Löschen',
        title: 'Rollen & Rechte',
        info: 'Rollen definieren und Modul-Rechte (Lesen/Schreiben/Löschen) zuweisen',
        newRole: '+ Neue Rolle',
        loading: 'lädt…',
        noneFound: 'Keine Rollen gefunden.',
        deleteTitle: 'Rolle löschen',
        deleteQuestion: 'Rolle „{label}" wirklich löschen?',
    },

    // src/client/pages/LogsViewer.tsx
    PAGES_LOGS_VIEWER_TEXTS: {
        modules: {
            system: 'System',
            auth: 'Anmeldung / Einstellungen',
            webhook: 'Webhooks',
            stripe: 'Stripe',
            paypal: 'PayPal',
            billing: 'Rechnungen / Abos',
            ingest: 'Beleg-Erkennung',
            export: 'Monats-Export',
            mail: 'E-Mail-Versand',
            scheduler: 'Automatisierung',
        },
        deleteFailed: 'Löschen fehlgeschlagen',
        columns: {
            time: 'Zeit',
            level: 'Level',
            module: 'Modul',
            message: 'Nachricht',
        },
        title: 'Protokolle',
        info: 'Anwendungs-Logs (neueste zuerst). Level je Modul unter Einstellungen → Protokollierung.',
        refresh: 'Aktualisieren',
        clearLogs: 'Logs leeren',
        moduleFilter: 'Modul',
        all: 'alle',
        levelFilter: 'Level',
        levelAll: 'ab debug (alle)',
        levelFrom: 'ab {level}',
        loading: 'lädt…',
        noneFound: 'Keine Logeinträge gefunden.',
        clearQuestion: 'Wirklich alle Logeinträge löschen? Das kann nicht rückgängig gemacht werden.',
        delete: 'Löschen',
    },

    // src/client/pages/TrashView.tsx
    PAGES_TRASH_VIEW_TEXTS: {
        relations: {
            invoices: 'Rechn.',
            subscriptions: 'Abos',
            statements: 'Abrechn.',
            sales: 'Verkäufe',
            items: 'Artikel',
        },
        kinds: { authors: 'Dozenten' },
        authorSummary: '{label} (WP {externalRef})',
        unexpectedResponse: 'Unerwartete Antwort vom Server — läuft die aktuelle Server-Version? (Server neu starten)',
        loadError: 'Fehler beim Laden',
        restored: 'Wiederhergestellt.',
        purged: 'Endgültig gelöscht.',
        actionFailed: 'Aktion fehlgeschlagen',
        title: 'Papierkorb',
        info: 'Gelöschte Datensätze aller Module — wiederherstellen oder endgültig löschen',
        loading: 'lädt…',
        empty: 'Der Papierkorb ist leer.',
        columns: {
            entry: 'Eintrag',
            deletedAt: 'Gelöscht am',
            deletedBy: 'Gelöscht von',
            relations: 'Beziehungen',
        },
        restore: 'Wiederherstellen',
        purge: 'Endgültig löschen',
        purgeBlocked: '{relations} verhindern das endgültige Löschen',
        restoreQuestion: '„{summary}" wiederherstellen?',
        purgeQuestion: '„{summary}" unwiderruflich löschen? Das kann nicht rückgängig gemacht werden.',
    },

    // src/client/components/ConfirmDialog.tsx
    COMPONENTS_CONFIRM_DIALOG_TEXTS: {
        confirm: 'OK',
        cancel: 'Abbrechen',
    },

    // src/client/components/Pagination.tsx
    COMPONENTS_PAGINATION_TEXTS: {
        rowsPerPage: 'Zeilen pro Seite:',
        navAria: 'Seitennavigation',
        previous: 'Zurück',
        next: 'Weiter',
    },

    // src/client/components/ListPanel.tsx
    COMPONENTS_LIST_PANEL_TEXTS: {
        elementOne: 'Element',
        elementMany: 'Elemente',
        countFiltered: '{count} von {grandTotal} {noun}',
        countAll: '{grandTotal} {noun}',
        search: 'Suche',
        filter: 'Filter',
    },

    // src/client/components/PasswordStrengthMeter.tsx
    COMPONENTS_PASSWORD_STRENGTH_METER_TEXTS: {
        levels: ['Sehr schwach', 'Schwach', 'Mittel', 'Stark', 'Sehr stark'],
        strength: 'Passwortstärke: {label}',
        minLength: 'mindestens {n} Zeichen',
        mixClasses: 'verschiedene Zeichenarten (Groß-/Kleinbuchstaben, Ziffern, Sonderzeichen)',
    },

    // src/client/components/Drawer.tsx
    COMPONENTS_DRAWER_TEXTS: {
        close: 'Schließen',
    },

    // src/client/components/PasswordInput.tsx
    COMPONENTS_PASSWORD_INPUT_TEXTS: {
        hide: 'Passwort verbergen',
        show: 'Passwort anzeigen',
    },

    // src/client/components/SecretInput.tsx
    COMPONENTS_SECRET_INPUT_TEXTS: {
        isSet: ' (gesetzt{hint})',
        isSetHint: ': {hint}',
        keepPlaceholder: 'unverändert lassen',
        clearTitle: 'Das gespeicherte Geheimnis aus der Datenbank entfernen. Ein leeres Feld beim Speichern tut das NICHT — es lässt den gespeicherten Wert stehen.',
        clear: 'Entfernen',
    },

    // src/client/components/BulkTrash.tsx
    COMPONENTS_BULK_TRASH_TEXTS: {
        entryOne: '1 Eintrag',
        entryMany: '{n} Einträge',
        truncated: 'Nur die ersten {n} ausgewählt.',
        selectFailed: 'Auswahl fehlgeschlagen',
        moved: 'In den Papierkorb verschoben: {moved}, übersprungen: {skipped}.',
        moveFailed: 'Verschieben fehlgeschlagen',
        selectPage: 'Alle auf dieser Seite auswählen',
        select: 'Auswählen',
        selectedCount: '{entries} ausgewählt',
        selectAllInFilter: 'Alle {total} im Filter auswählen',
        clearSelection: 'Auswahl aufheben',
        toTrashCount: 'In den Papierkorb ({n})',
        toTrash: 'In den Papierkorb',
        moveQuestion: '{entries} in den Papierkorb verschieben?',
        skipNote: 'Was nicht verschoben werden darf, wird übersprungen und danach genannt.',
        result: 'Ergebnis',
    },

    // src/client/components/RichTextEditor.tsx
    COMPONENTS_RICH_TEXT_EDITOR_TEXTS: {
        linkPrompt: 'Link-URL',
        bold: 'Fett',
        italic: 'Kursiv',
        heading: 'Überschrift',
        bulletListTitle: 'Aufzählung',
        bulletList: '• Liste',
        orderedListTitle: 'Nummerierte Liste',
        orderedList: '1. Liste',
        link: 'Link',
        insertTableTitle: 'Tabelle einfügen',
        insertTable: 'Tabelle',
        addRowTitle: 'Zeile hinzufügen',
        addRow: '+Zeile',
        addColumnTitle: 'Spalte hinzufügen',
        addColumn: '+Spalte',
        deleteRowTitle: 'Zeile löschen',
        deleteRow: '−Zeile',
        deleteColumnTitle: 'Spalte löschen',
        deleteColumn: '−Spalte',
        deleteTableTitle: 'Tabelle löschen',
        deleteTable: '⌫ Tabelle',
    },

    // src/client/components/SearchSelect.tsx
    COMPONENTS_SEARCH_SELECT_TEXTS: {
        noMatches: 'Keine Treffer',
        searching: 'Suche …',
    },

    // src/client/components/ColumnManager.tsx
    COMPONENTS_COLUMN_MANAGER_TEXTS: {
        columns: 'Spalten',
        reset: 'Zurücksetzen',
    },

    // ══ Client: Rechnungen, Abos, Kunden, Transaktionen ══

    // Texte der Verkaufsseiten im Client (Rechnungen, Abos, Transaktionen, Kunden).

    // src/client/pages/InvoiceForm.tsx
    PAGES_INVOICE_FORM_TEXTS: {
        // Position text of an issued invoice (admin only, 03.11.2026, 62b/63a/64b)
        lineTextEdit: '✎ Text ändern',
        lineTextEditHint: 'Positionstext dieser ausgestellten Rechnung ändern (nur Administrator, wird protokolliert)',
        lineTextTitle: 'Positionstext ändern — {invoiceNumber}',
        lineTextIntro: 'Geändert wird nur der Text. Nummer, Datum, Mengen, Preise, Steuer und Summen bleiben. PDF und E-Rechnung werden neu erzeugt; das bisherige PDF wird aufbewahrt.',
        lineTextPosition: 'Position',
        lineTextPositionOption: 'Position {n}',
        lineTextNew: 'Neuer Text',
        lineTextReason: 'Begründung (Pflicht)',
        lineTextSentWarning: 'Diese Rechnung ist schon versendet — der Kunde hat die alte Fassung. Es wird nichts automatisch neu versendet; erneut senden kannst du danach selbst.',
        lineTextSave: 'Text ändern',
        lineTextDone: 'Text geändert, PDF neu erzeugt.',
        lineTextFailed: 'Ändern fehlgeschlagen',
        textChangesHead: 'Textänderungen',
        textChangesNote: 'Nur hier sichtbar — nicht im PDF, nicht in der E-Rechnung, nicht im Monatsexport.',
        textChangeMeta: '{date} · {by} · Position {n}',
        textChangeSent: ' · nach Versand',
        textChangeFrom: 'Vorher',
        textChangeTo: 'Nachher',
        textChangeReason: 'Begründung',
        textChangeOldPdf: 'bisheriges PDF',
        // PSP payment states in plain German — the raw values come from the transaction mirror.
        paymentStatus: {
            open: 'offen',
            paid: 'bezahlt',
            failed: 'fehlgeschlagen',
            expired: 'abgelaufen',
            refunded: 'erstattet',
            partially_refunded: 'teilweise erstattet',
        } as Record<string, string>,
        refundPending: 'Eine Erstattung wurde bereits angefordert und ist noch nicht bestätigt.',
        refundChargedBack: 'Die Zahlung wurde von der Bank zurückbelastet — eine Erstattung würde doppelt zahlen.',
        refundAlreadyDone: 'Bereits erstattet ({amount} {currency}).',
        loadFailed: 'Fehler beim Laden',
        invoiceLoadFailed: 'Rechnung konnte nicht geladen werden',
        saveFailed: 'Speichern fehlgeschlagen',
        finalizeFailed: 'Finalisieren fehlgeschlagen',
        cancelCreated: 'Storno-Beleg {invoiceNumber} erstellt.',
        cancelFailed: 'Stornieren fehlgeschlagen',
        reissueFailed: 'Neu ausstellen fehlgeschlagen',
        sentRedirected: 'Testinstanz: umgeleitet an {redirectedTo} (nicht an {to}).',
        sentRedirectedFallback: 'den Kunden',
        sentTo: 'Versendet an {to}.',
        sentToFallback: 'den hinterlegten Empfänger',
        previewFailed: 'Vorschau fehlgeschlagen',
        refundDone: '{amount} {sign} erstattet (Gebühr {fee} {sign} einbehalten). Storno-Beleg und Zugangsentzug folgen, sobald der Zahlungsdienstleister die Erstattung bestätigt.',
        refundFailed: 'Erstattung fehlgeschlagen',
        titleNew: 'Neue Rechnung',
        titleEdit: 'Rechnung bearbeiten (Entwurf)',
        titleView: 'Rechnung {invoiceNumber}',
        titleViewNoNumber: '(Ansicht)',
        loading: 'lädt…',
        infoEdit: 'Entwürfe sind bearbeitbar; finalisierte Rechnungen sind unveränderlich (nur Ansicht + PDF).',
        infoNew: 'Manuell anlegen oder aus der letzten Rechnung des Kunden klonen.',
        createDraft: 'Entwurf anlegen',
        save: 'Speichern',
        saveFirst: 'Bitte zuerst speichern',
        finalizeHint: 'Vergibt die Rechnungsnummer (endgültig)',
        finalize: 'Finalisieren',
        pdfTabName: 'PDF {invoiceNumber}',
        pdfTabFallback: 'Rechnung-PDF',
        pdfFileName: 'Rechnung-{name}.pdf',
        openPdf: 'PDF öffnen',
        resendHint: 'Dokument erneut per E-Mail an den Kunden senden',
        sendHint: 'Dokument per E-Mail an den Kunden senden',
        resend: 'Erneut senden',
        send: 'Versenden',
        alreadyCancelled: 'Zu dieser Rechnung gibt es bereits einen Storno-Beleg.',
        cancelHint: 'Storno-Beleg (Gutschrift) über den vollen Betrag erstellen — ohne Zahlungsdienstleister',
        cancelled: 'Storniert',
        cancel: 'Stornieren',
        reissueHint: 'Neuen Entwurf mit den Positionen dieser Rechnung anlegen',
        reissue: 'Neu ausstellen',
        duplicate: 'Rechnung duplizieren',
        duplicateHint: 'Neuen Entwurf mit Kunde und Positionen dieser Rechnung anlegen — Zeitraum rückt auf den Folgemonat',
        duplicateSaveFirst: 'Erst speichern — dupliziert wird der gespeicherte Stand',
        duplicateFailed: 'Duplizieren fehlgeschlagen',
        refundHint: 'Betrag beim Zahlungsdienstleister zurückzahlen (abzüglich Stornogebühr)',
        refunded: 'Erstattet',
        refund: 'Erstatten',
        viewOnlyStatus: 'Diese Rechnung ist {statusLabel} und kann nicht mehr bearbeitet werden (nur Ansicht).',
        readOnly: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Rechnungen.',
        paymentHead: 'Zahlung ({provider}):',
        paymentRefunded: ' · davon erstattet: ',
        chargedBackOn: 'zurückbelastet am {date} ({amount} {currency})',
        chargebackReversed: ' · Rückbelastung aufgehoben am {date} — der Zugang wurde nicht wieder freigegeben',
        chargebackOpen: ' · Zugang gesperrt, kein Beleg an den Kunden',
        noPaymentData: 'Zu dieser Rechnung sind keine Zahlungsdaten hinterlegt (manuell angelegt oder vor der Transaktions-Erfassung entstanden).',
        noCustomers: 'Keine Kunden vorhanden — bitte zuerst unter „Kunden" einen anlegen.',
        basics: 'Eckdaten',
        customer: 'Kunde *',
        brand: 'Marke',
        periodLabel: 'Zeitraum-Label (optional)',
        periodTipBefore: 'Interne Kennzeichnung des Abrechnungszeitraums, z. B. „Mai 2026". Erscheint ',
        periodTipBold: 'nicht',
        periodTipAfter: ' auf dem PDF, sondern dient der Orientierung in der Rechnungsliste. Bei Abo-Rechnungen setzt der Abo-Lauf es automatisch und nutzt es als Schutz vor Doppelabrechnung: Existiert zu einem Abo bereits eine Rechnung mit demselben Label, wird keine zweite erzeugt. Storno-Belege tragen „Storno zu …".',
        periodPlaceholder: 'z. B. Mai 2026',
        reverseCharge: 'Reverse Charge, 0 %',
        withVat: 'mit MwSt., {percent} %',
        viesPending: 'Finalisieren ist erst möglich, wenn VIES antwortet.',
        rateContradiction: 'Die Steuersätze der Positionen passen nicht dazu — so wird nicht finalisiert.',
        applyRate: 'Steuersatz übernehmen',
        positions: 'Positionen',
        finalizeTitle: 'Rechnung finalisieren',
        finalizeMessage: 'Dabei wird die endgültige Rechnungsnummer vergeben. Danach ist die Rechnung unveränderlich. Fortfahren?',
        mixedRates: 'Diese Rechnung mischt die Steuersätze {rates} %. Ein Beleg kann nur einen Satz ausweisen — bitte je Steuersatz eine eigene Rechnung anlegen. Das Finalisieren wird abgelehnt.',
        mixedRatesJoin: ' % und ',
        ratesVsReverseCharge: 'Die Steuersätze passen nicht zur Reverse-Charge-Prüfung.',
        finalizeRefused: 'Das Finalisieren wird abgelehnt.',
        sendDrawerTitle: '{action} — {invoiceNumber}',
        cancelTitle: 'Rechnung stornieren',
        cancelMessage: 'Zu Rechnung {invoiceNumber} wird ein Storno-Beleg (Gutschrift) über den vollen Betrag erstellt, mit eigener Nummer. Es fließt dabei kein Geld — hat der Kunde schon gezahlt, überweist du die Rückzahlung selbst. Ein Storno lässt sich nicht zurücknehmen. Fortfahren?',
        refundTitle: 'Rechnung {invoiceNumber} erstatten',
        refundConfirm: '{amount} {sign} erstatten',
        refundIntro: 'Der Kauf wird vollständig storniert: Der Betrag geht abzüglich Gebühr zurück, es entsteht ein Storno-Beleg, und der gekaufte Zugang wird entzogen.',
        refundGross: 'Rechnungsbetrag: ',
        refundAlreadyPart: 'davon bereits erstattet: {amount} {sign} → offen: ',
        reason: 'Grund',
        reasonCancellation: 'Storno / Kulanz — Gebühr wird einbehalten',
        reasonWithdrawal: 'Gesetzlicher Widerruf — volle Erstattung',
        feeLabel: 'Stornogebühr ({sign})',
        withdrawalNoFee: 'Beim gesetzlichen Widerruf darf keine Gebühr einbehalten werden — der volle Betrag geht zurück.',
        feeFromItem: 'Vorgabe für Artikel {itemId}{label}.',
        feeFromItemLabel: ' ({label})',
        feeFromBrand: 'Vorgabe aus den Marken-Einstellungen.',
        feeNone: 'Für diese Marke ist keine Gebühr hinterlegt.',
        feeAdjustable: 'Der Betrag kann für diesen Einzelfall angepasst werden.',
        payout: 'Auszahlung an den Kunden: ',
        roundingHint: 'Der tatsächliche Betrag kann um einen Cent abweichen — er wird auf einen Wert gerundet, den der Storno-Beleg mit diesem Steuersatz exakt abbilden kann.',
    },

    // src/client/pages/InvoicesList.tsx
    PAGES_INVOICES_LIST_TEXTS: {
        pdfTabName: 'PDF {invoiceNumber}',
        pdfTabFallback: 'Rechnung-PDF',
        pdfFileName: 'Rechnung-{name}.pdf',
        deleteFailed: 'Löschen fehlgeschlagen',
        onlyFinalized: 'Nur finalisierte oder versendete Rechnungen',
        cancelledNotSent: 'Storniert — wird nicht versendet',
        sameCustomerOnly: 'Nur Rechnungen desselben Kunden — versendet wird an seine Adresse',
        sentRedirected: 'Testinstanz: umgeleitet an {redirectedTo} (nicht an {to}): {numbers}',
        sentTo: 'Gesendet an {to}: {numbers}',
        sentPartly: 'Nicht alles ging raus — gesendet: {numbers}',
        sentNothing: 'nichts',
        selectForSend: 'Zum Versand auswählen',
        colDate: 'Datum',
        colCustomer: 'Kunde',
        colInvoiceNumber: 'Rechnungsnummer',
        draftMarker: '— Entwurf —',
        colReferenceNumber: 'Referenznummer',
        colAmount: 'Betrag',
        status: 'Status',
        brand: 'Marke',
        colPdf: 'PDF',
        openPdf: 'PDF öffnen',
        pdfAfterFinalize: 'PDF erst nach dem Finalisieren',
        colActions: 'Aktionen',
        edit: 'Bearbeiten',
        view: 'Ansehen',
        delete: 'Löschen',
        onlyDraftsDeletable: 'Nur Entwürfe können gelöscht werden',
        duplicate: 'Rechnung duplizieren',
        duplicateFailed: 'Duplizieren fehlgeschlagen',
        title: 'Rechnungen',
        info: 'Alle Rechnungen, neueste zuerst',
        clearSelection: 'Auswahl aufheben',
        sendSelected: 'Ausgewählte versenden ({n})',
        newInvoiceTab: 'Neue Rechnung',
        newInvoice: '+ Neue Rechnung',
        all: 'alle',
        loading: 'lädt…',
        empty: 'Keine Rechnungen gefunden.',
        deleteTitle: 'Entwurf löschen',
        deleteMessage: 'Entwurf{company} wirklich löschen? Das kann nicht rückgängig gemacht werden.',
        deleteMessageCompany: ' von {company}',
    },

    // src/client/pages/SubscriptionsList.tsx
    PAGES_SUBSCRIPTIONS_LIST_TEXTS: {
        monthly: 'monatlich',
        yearly: 'jährlich',
        newTab: 'Neues Abo',
        editTab: 'Abo: {customer}',
        trashed: 'Abo in den Papierkorb verschoben.',
        deleteFailed: 'Löschen fehlgeschlagen',
        runDone: 'Fällige Abos abgerechnet: {count} Rechnung(en) erzeugt',
        runErrors: ', {n} Abo(s) mit Fehler — siehe Spalte „Nächste Abrechnung“.',
        runFailed: 'Lauf fehlgeschlagen',
        colCustomer: 'Kunde',
        brand: 'Marke',
        cadence: 'Rhythmus',
        colProduct: 'Produkt',
        colNetPrice: 'Preis (netto)',
        colMode: 'Modus',
        status: 'Status',
        colNextBilling: 'Nächste Abrechnung',
        viaPspPaidUntil: 'über {psp} · bezahlt bis {date}',
        viaPsp: 'über {psp}',
        pspMollie: 'Mollie',
        pspStripe: 'Stripe',
        runError: '{date} · Fehler',
        colActions: 'Aktionen',
        edit: 'Bearbeiten',
        view: 'Ansehen',
        delete: 'Löschen',
        title: 'Abos',
        info: 'Wiederkehrende Rechnungen',
        runDue: 'Fällige Abos abrechnen',
        newSubscription: '+ Neues Abo',
        all: 'alle',
        loading: 'lädt…',
        empty: 'Keine Abos gefunden.',
        trashTitle: 'In den Papierkorb',
        trashQuestion: 'Abo „{customer}" in den Papierkorb verschieben?',
        trashAdminsOnly: 'Nur Admins können den Eintrag dort wiederherstellen.',
    },

    // src/client/pages/SubscriptionForm.tsx
    PAGES_SUBSCRIPTION_FORM_TEXTS: {
        canEditNow: 'Du kannst jetzt bearbeiten.',
        loadFailed: 'Fehler beim Laden',
        saved: 'Gespeichert.',
        created: 'Abo angelegt.',
        saveFailed: 'Speichern fehlgeschlagen',
        titleView: 'Abo (Ansicht)',
        titleEdit: 'Abo bearbeiten',
        titleNew: 'Neues Abo',
        apply: 'Übernehmen',
        create: 'Abo anlegen',
        readOnly: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Abos.',
        lockedQueue: 'Dieses Abo wird gerade an anderer Stelle bearbeitet. Du bist in der Warteschlange',
        lockedPosition: ' (Position {position})',
        lastRunFailed: 'Letzter Rechnungslauf für dieses Abo fehlgeschlagen',
        lastRunAt: ' ({at})',
        lastRunNotAdvanced: 'Das nächste Abrechnungsdatum ist deshalb nicht weitergerückt; der nächste Lauf versucht es erneut.',
        createCustomerFirst: 'Bitte zuerst einen Kunden anlegen.',
        customer: 'Kunde *',
        brand: 'Marke',
        cadence: 'Rhythmus',
        monthly: 'monatlich',
        yearly: 'jährlich',
        mode: 'Modus',
        modeAuto: 'auto (direkt finalisieren)',
        modeDraft: 'Entwurf (zum Bearbeiten)',
        status: 'Status',
        statusActive: 'aktiv',
        statusPaused: 'pausiert',
        statusEnded: 'beendet',
        pspManaged: 'Vom Zahlungsanbieter verwaltet — über Kündigen/Pausieren ändern.',
        startDate: 'Startdatum',
        nextBillingDate: 'Nächstes Abrechnungsdatum *',
        billingExplained: 'Abgerechnet wird im Lauf am Monatsletzten, sobald das nächste Abrechnungsdatum erreicht ist — zusammen mit den übrigen fälligen Abos des Kunden auf einer Rechnung je Marke. Danach rückt das Datum um den Rhythmus vor. Beendet wird ein Abo über den Status.',
        productName: 'Produktname *',
        placeholderLabel: 'Platzhalter: ',
        placeholderStart: ' = Beginn, ',
        placeholderEnd: ' = Ende der abgerechneten Periode.',
        nextInvoice: ' Nächste Rechnung: ',
        netPrice: 'Preis netto',
        netPriceHint: 'Menge 1. Die MwSt. ergibt sich aus dem Kunden.',
        pspRunsBefore: 'Dieses Abo führt ',
        pspMollie: 'Mollie',
        pspStripe: 'Stripe',
        pspRunsAfter: '. Wir rechnen es nicht im Monatslauf ab — der Einzug erfolgt beim Zahlungsanbieter, gerechnet ab dem Kauftag.',
        paidUntilBefore: ' Bezahlt bis ',
        paidUntilAfter: ' (Stand des letzten Abgleichs).',
        noNextCollection: ' Ein nächster Einzugstermin liegt uns hier nicht vor — er steht beim Zahlungsanbieter.',
        positions: 'Positionen',
    },

    // src/client/pages/TransactionsList.tsx
    PAGES_TRANSACTIONS_LIST_TEXTS: {
        statusLabels: {
            open: 'offen',
            paid: 'bezahlt',
            failed: 'fehlgeschlagen',
            expired: 'abgelaufen',
            refunded: 'erstattet',
            partially_refunded: 'teilerstattet',
        },
        kindLabels: {
            first: 'Erstzahlung',
            recurring: 'Folgezahlung',
            oneoff: 'Einmalkauf',
            unknown: '—',
        } as Record<string, string>,
        providerMollie: 'Mollie',
        providerStripe: 'Stripe',
        providerPaypal: 'PayPal',
        date: 'Datum',
        brand: 'Marke',
        psp: 'PSP',
        transaction: 'Transaktion',
        kind: 'Art',
        amount: 'Betrag',
        refunded: 'Erstattet',
        status: 'Status',
        customer: 'Kunde',
        reference: 'Referenz',
        title: 'Transaktionen',
        info: 'Zahlungen beim Zahlungsanbieter — eine Zeile je Zahlung, mit Verlauf. Suche nach Transaktions-ID, Referenz oder E-Mail.',
        all: 'alle',
        from: 'von',
        to: 'bis',
        loading: 'lädt…',
        empty: 'Keine Transaktionen gefunden.',
        sum: 'Summe: ',
        sumRefunded: ' · davon erstattet: ',
        refundedInline: ' (erstattet: ',
        brandPsp: 'Marke / PSP',
        paidAt: 'Zahlung am',
        received: 'Eingegangen',
        pspCustomer: 'PSP-Kunde',
        pspSubscription: 'PSP-Abo',
        history: 'Verlauf',
        refund: 'Erstattung',
        linked: 'Verknüpft',
        invoiceLink: 'Rechnung {invoiceNumber}',
        noInvoice: 'Keine Rechnung',
        subscriptionLink: 'Abo {externalId}',
        logsTab: 'Protokolle',
        logEntries: 'Log-Einträge zu dieser Transaktion',
    },

    // src/client/pages/CustomerForm.tsx
    PAGES_CUSTOMER_FORM_TEXTS: {
        taxReverseCharge: 'Mit gültiger USt-IdNr. eines anderen EU-Landes geht die Rechnung ohne Umsatzsteuer raus (Reverse Charge).',
        taxBusinessNoVatId: 'Ohne USt-IdNr. wird Umsatzsteuer berechnet — auch bei Geschäftskunden.',
        taxNoVatId: 'Ohne USt-IdNr. wird Umsatzsteuer berechnet.',
        canEditNow: 'Du kannst jetzt bearbeiten.',
        loadFailed: 'Fehler beim Laden',
        saved: 'Gespeichert.',
        created: 'Kunde angelegt.',
        saveFailed: 'Speichern fehlgeschlagen',
        titleView: 'Kunde (Ansicht)',
        titleEdit: 'Kunde bearbeiten',
        titleNew: 'Neuer Kunde',
        salutation: 'Anrede',
        salutationNone: '— keine —',
        title: 'Titel',
        titlePlaceholder: 'z. B. Dr.',
        apply: 'Übernehmen',
        create: 'Anlegen',
        readOnly: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Kunden.',
        lockedQueue: 'Dieser Datensatz wird gerade an anderer Stelle bearbeitet. Du bist in der Warteschlange',
        lockedPosition: ' (Position {position})',
        lockedAfter: '. Sobald er freigegeben wird, kannst du bearbeiten.',
        name: 'Name / Ansprechpartner *',
        company: 'Firma',
        email: 'E-Mail *',
        street: 'Straße',
        postalCode: 'PLZ',
        city: 'Ort',
        country: 'Land',
        vatId: 'USt-IdNr.',
        vatIdTip: 'Entscheidet allein über die Besteuerung: Mit gültiger USt-IdNr. eines anderen EU-Landes geht die Rechnung ohne Umsatzsteuer raus (Reverse Charge), sonst mit. Ohne Trennzeichen eingeben — Punkte und Bindestriche werden entfernt.',
        vatIdPlaceholder: 'DE123456789',
        mailSubject: 'Rechnungs-Mail-Betreff (optional)',
        mailBody: 'Rechnungs-Mail-Text (optional)',
        mailTipBefore: 'Leer = globaler Standard aus den Einstellungen. Platzhalter: ',
        mailTipProductList: '{productlist} (Bestellte Artikel (Menge × Produkt), in einer eigenen Zeile)',
    },

    // src/client/pages/CustomersList.tsx
    PAGES_CUSTOMERS_LIST_TEXTS: {
        newTab: 'Neuer Kunde',
        editTab: 'Kunde: {name}',
        trashed: 'Kunde in den Papierkorb verschoben.',
        deleteFailed: 'Löschen fehlgeschlagen',
        colCompany: 'Firma',
        colName: 'Name',
        colPostalCode: 'PLZ',
        colCity: 'Ort',
        colCountry: 'Land',
        colEmail: 'E-Mail',
        colVatId: 'UID',
        colOpen: 'OP',
        openAmount: '{amount} offen',
        colInvoices: 'Rechn.',
        colSubscriptions: 'Abos',
        colActions: 'Aktionen',
        edit: 'Bearbeiten',
        view: 'Ansehen',
        delete: 'Löschen',
        title: 'Kunden',
        info: 'Stammdaten',
        newCustomer: '+ Neuer Kunde',
        loading: 'lädt…',
        empty: 'Keine Kunden gefunden.',
        trashTitle: 'In den Papierkorb',
        trashQuestion: 'Kunde „{name}" in den Papierkorb verschieben?',
        trashAdminsOnly: 'Nur Admins können den Eintrag dort wiederherstellen.',
    },

    // src/client/components/LineItemsEditor.tsx
    COMPONENTS_LINE_ITEMS_EDITOR_TEXTS: {
        description: 'Beschreibung',
        quantity: 'Menge',
        unitPrice: 'Einzelpreis',
        taxRate: 'MwSt. %',
        empty: 'Noch keine Positionen.',
        add: '+ Position',
        net: 'Netto: ',
        tax: 'zzgl. MwSt.: ',
        gross: 'Gesamt (brutto): ',
    },

    // src/client/components/CustomerPicker.tsx
    COMPONENTS_CUSTOMER_PICKER_TEXTS: {
        customerNumber: ' · Nr. {n}',
        placeholder: 'Kunde suchen (Name, Firma, E-Mail, Nr.) …',
    },

    // src/client/components/CountryPicker.tsx
    COMPONENTS_COUNTRY_PICKER_TEXTS: {
        unknown: '{code} (unbekannt)',
        notSet: '— nicht gesetzt —',
    },

    // src/client/components/SendMailDrawer.tsx
    COMPONENTS_SEND_MAIL_DRAWER_TEXTS: {
        previewFailed: 'Vorschau fehlgeschlagen',
        sendFailed: 'Versand fehlgeschlagen',
        send: 'Senden',
        cancel: 'Abbrechen',
        loading: 'lädt…',
        mailCreditNotes: 'Mail mit Storno-Belegen',
        mailInvoices: 'Mail mit Rechnungen',
        to: 'An',
        testMailTo: 'Test-Mail-Modus: geht an {to}',
        testMailBlocked: 'Test-Mail-Modus ohne Test-Mailadresse — es wird nichts versendet (Einstellungen → E-Mail).',
        from: 'Absender',
        attachments: 'Anlagen',
        alreadySent: 'Schon versendet, geht erneut mit: {numbers}',
        subject: 'Betreff',
        text: 'Text',
        textHint: '(gilt nur für diese Mail — die Vorlage bleibt)',
        discardTitle: 'Änderungen verwerfen',
        discardMessage: 'Betreff oder Text wurden geändert. Ohne Senden schließen und die Änderungen verwerfen?',
        discard: 'Verwerfen',
    },

    // src/client/vatMark.tsx
    CLIENT_VAT_MARK_TEXTS: {
        domesticNoVatId: 'Für Zypern nicht nötig — hier wird mit Mehrwertsteuer abgerechnet.',
        domesticRejected: 'VIES weist diese Nummer zurück. Für Zypern ohne Folge — hier wird mit Mehrwertsteuer abgerechnet, die Nummer wird nicht gebraucht.',
        domesticConfirmed: 'Von VIES bestätigt{when} — für Zypern nicht nötig.',
        domesticUnconfirmed: 'Noch nicht bestätigt — für Zypern auch nicht nötig.',
        noVatId: 'Ohne USt-IdNr. ist keine Abrechnung möglich (Reverse Charge).',
        confirmed: 'Von VIES bestätigt{when}.',
        confirmedWhen: ' am {when}',
        unreadable: 'Das ist keine lesbare USt-IdNr. — VIES wurde nicht gefragt. Bitte den Eintrag prüfen (Ländercode + Nummer, z. B. DE123456789).',
        rejected: 'VIES weist diese Nummer als ungültig zurück — keine Auszahlung möglich.',
        unconfirmed: 'Noch nicht bestätigt — bitte prüfen.',
    },

    // src/client/api.ts
    CLIENT_API_TEXTS: {
        previewFailed: 'Vorschau fehlgeschlagen',
        saveFailed: 'Speichern fehlgeschlagen',
        uploadFailed: 'Hochladen fehlgeschlagen',
    },

    // ══ Client: Autoren, Katalog, Urkunden, Monats-Export ══

    // Client texts (author pages, catalog, monthly export, certificates, file viewer).

    // src/client/pages/FileViewer.tsx
    PAGES_FILE_VIEWER_TEXTS: {
        noFile: 'Keine Datei.',
        documentTitle: 'Dokument',
        httpError: 'HTTP {status}',
        loadError: 'Fehler beim Laden',
        loading: 'lädt…',
        emptyFile: 'Leere Datei.',
    },

    // src/client/pages/AuthorCatalog.tsx
    PAGES_AUTHOR_CATALOG_TEXTS: {
        feeLevel: {
            item: 'Artikel', author: 'Dozent', global: 'Marke', none: 'keine',
        } as Record<string, string>,
        loadFailed: 'Laden fehlgeschlagen',
        tabName: 'Artikel: {title}',
        salesReleased: 'Verkauf freigegeben.',
        salesReleaseWithdrawn: 'Verkaufsfreigabe zurückgenommen.',
        failed: 'Fehlgeschlagen',
        republished: 'Erneut an vitalpilze gesendet.',
        title: 'Artikel-Katalog',
        info: {
            part1: 'Die Artikel meldet vitalpilze; Titel, Typ und die ',
            bold1: 'inhaltliche',
            part2: ' Freigabe gehören dorthin. Hier werden Verkaufspreis, Gebühr und Dozent festgelegt und der Verkauf freigegeben. Verkauft wird ein Artikel erst, wenn ',
            bold2: 'beide',
            part3: ' Freigaben gesetzt sind.',
        },
        search: 'Suche',
        searchPlaceholder: 'Titel oder Artikel-ID',
        release: 'Freigabe',
        filterAll: 'alle',
        sellable: 'verkaufsfähig',
        notSellable: 'noch nicht verkaufsfähig',
        emptyBefore: 'Noch keine Artikel gemeldet. vitalpilze meldet sie über ',
        emptyAfter: '.',
        colItem: 'Artikel',
        colAuthor: 'Dozent',
        colSuggestion: 'Vorschlag',
        colSalesPrice: 'Verkaufspreis',
        colFee: 'Gebühr',
        colReleases: 'Freigaben',
        freeAccess: { one: ' · {n} Gratis-Zugang', other: ' · {n} Gratis-Zugänge' },
        noAuthor: '— keiner —',
        onlyCoursesTitle: 'Nur Kurse werden mit einem Dozenten abgerechnet',
        onlyCourses: 'kein Dozent (nur Kurse)',
        suggestionDiffersTitle: 'Der Dozent wünscht einen anderen Preis',
        suggestionDiffers: 'abweichend',
        inherit: '— erben —',
        feeApplies: 'gilt: {fee} ({level})',
        feeNoEffect: 'ohne Wirkung — keine Dozentenabrechnung',
        content: 'Inhalt: ',
        contentReleased: '✓ freigegeben',
        contentOpen: 'offen (vitalpilze)',
        lockSales: 'Verkauf sperren',
        releaseSales: 'Verkauf freigeben',
        edit: 'Bearbeiten',
        view: 'Ansehen',
        republishTitle: 'Preis und Freigabe erneut an vitalpilze senden',
    },

    // src/client/pages/AuthorsList.tsx
    PAGES_AUTHORS_LIST_TEXTS: {
        movedToTrash: 'Dozent in den Papierkorb verschoben.',
        deleteFailed: 'Löschen fehlgeschlagen',
        tabName: 'Dozent: {name}',
        colName: 'Name',
        colCompany: 'Firma',
        colWpId: 'WP-ID',
        colEmail: 'E-Mail',
        colCity: 'Ort',
        colCountry: 'Land',
        colVatId: 'USt-IdNr.',
        colItems: 'Artikel',
        colFee: 'Gebühr',
        feeDefaultTitle: 'Es gilt die Gebühr-Vorgabe der Marke',
        feeDefault: 'Vorgabe',
        colStatus: 'Status',
        statusActive: 'aktiv',
        blockedNoDateTitle: 'Geblockt, aber ohne Zeitpunkt — aus der Zeit vor der Umbenennung. Es wird noch abgerechnet. Einmal speichern setzt den Zeitpunkt.',
        blockedNoDate: 'geblockt ⚠',
        blockedSinceTitle: 'Geblockt seit {date} — Verkäufe davor werden noch abgerechnet.',
        blocked: 'geblockt',
        colMissing: 'Fehlt noch',
        complete: 'vollständig',
        colActions: 'Aktionen',
        edit: 'Bearbeiten',
        delete: 'Löschen',
        title: 'Dozenten',
        info: 'Dozenten verkaufen ihre Kurse über unseren Shop. Wir behalten eine Gebühr und rechnen monatlich ab — der Lauf wird von Hand angestoßen (Dozenten-Abrechnung → „Monat abrechnen“), er startet nicht von selbst. Ein Dozent entsteht automatisch, sobald vitalpilze einen Artikel mit seiner Benutzer-ID meldet — Anschrift, Steuermerkmale und Bankverbindung müssen hier ergänzt werden, sonst ist keine Auszahlung möglich.',
        searchPlaceholder: 'Name, Firma, E-Mail, WP-ID, USt-IdNr.',
        loading: 'lädt…',
        emptyNone: 'Noch keine Dozenten — sie entstehen aus den gemeldeten Artikeln.',
        emptyFiltered: 'Keine Dozenten gefunden.',
        trashTitle: 'In den Papierkorb',
        deleteQuestion: 'Dozent „{name}" in den Papierkorb verschieben?',
        deleteInactiveBefore: 'Er gilt danach als ',
        deleteInactiveBold: 'inaktiv',
        deleteInactiveAfter: ': Verkäufe bis jetzt werden noch abgerechnet, spätere nicht mehr. Seine Artikel und bisherigen Abrechnungen bleiben, wo sie sind.',
        deleteAdminsRestore: 'Nur Admins können den Eintrag im Papierkorb wiederherstellen.',
    },

    // src/client/pages/CatalogItemForm.tsx
    PAGES_CATALOG_ITEM_FORM_TEXTS: {
        feeLevel: {
            item: 'Artikel', author: 'Dozent', global: 'Marke', none: 'keine',
        } as Record<string, string>,
        notInCatalog: 'Dieser Artikel ist nicht mehr im Katalog.',
        loadFailed: 'Laden fehlgeschlagen',
        noItemLoaded: 'Kein Artikel geladen',
        saved: '{title} gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        item: 'Artikel',
        loading: 'lädt…',
        titleView: 'Artikel (Ansicht)',
        titleEdit: 'Artikel bearbeiten',
        info: {
            part1: 'Titel, Typ und die ',
            bold1: 'inhaltliche',
            part2: ' Freigabe meldet vitalpilze. Hier werden Verkaufspreis, Gebühr und Dozent festgelegt. Verkauft wird ein Artikel erst, wenn ',
            bold2: 'beide',
            part3: ' Freigaben gesetzt sind.',
        },
        apply: 'Übernehmen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für den Katalog.',
        freeAccess: { one: ' · {n} Gratis-Zugang', other: ' · {n} Gratis-Zugänge' },
        content: 'Inhalt: ',
        released: '✓ freigegeben',
        contentOpen: 'offen (vitalpilze)',
        contentSep: ' ·',
        sales: 'Verkauf: ',
        salesLocked: 'gesperrt',
        sellableSuffix: ' · verkaufsfähig',
        salesPriceLabel: 'Verkaufspreis ({sign})',
        suggestionLabel: 'Vorschlag des Dozenten',
        suggestionDiffersTitle: 'Der Dozent wünscht einen anderen Preis',
        suggestionDiffers: ' · abweichend',
        feeTypeLabel: 'Gebührenart ',
        feeTypeTip: '„Erben" nimmt die Gebühr des Dozenten, sonst die der Marke.',
        inherit: '— erben —',
        percent: 'Prozent',
        fixed: 'Festbetrag',
        feeValueLabel: 'Gebührenwert',
        effectiveLabel: 'Es gilt',
        feeNoEffect: 'ohne Wirkung — keine Dozentenabrechnung',
        authorLabel: 'Dozent',
        noAuthor: '— keiner —',
        onlyCoursesTitle: 'Nur Kurse werden mit einem Dozenten abgerechnet',
        onlyCourses: 'kein Dozent (nur Kurse)',
    },

    // src/client/pages/CertificateDispatchList.tsx
    PAGES_CERTIFICATE_DISPATCH_LIST_TEXTS: {
        loadFailed: 'Laden fehlgeschlagen',
        noUsableSelected: 'Keine Zeile mit verwendbarer Anschrift ausgewählt.',
        addressesTabName: 'Adressen ({n})',
        addressSavedComplete: 'Anschrift gespeichert — {num} kann versandt werden.',
        addressSavedIncomplete: 'Anschrift gespeichert, aber noch unvollständig (Straße, PLZ und Ort nötig).',
        saveFailed: 'Speichern fehlgeschlagen',
        markedSent: '{num} als versandt markiert.',
        backOnList: '{num} zurück auf die Liste.',
        actionFailed: 'Aktion fehlgeschlagen',
        selectAria: '{num} auswählen',
        colHolder: 'Empfänger',
        colCourse: 'Kurs',
        colNumber: 'Nummer',
        colOrdered: 'Bestellt',
        address: 'Anschrift',
        addressIncomplete: 'Anschrift unvollständig: ',
        changedByTitle: 'Zuletzt geändert von {by} am {date}',
        handCorrected: '· von Hand korrigiert',
        colActions: 'Aktionen',
        printCertificateTitle: 'Zertifikat drucken',
        pdf: 'PDF',
        completeAddressFirst: 'Erst die Anschrift vervollständigen',
        sent: 'Versandt',
        colSentAt: 'Versandt am',
        colSentBy: 'Durch',
        undo: 'Zurücknehmen',
        title: 'Zertifikate — Versand',
        info: 'Bezahlt und noch nicht in der Post. Zeilen auswählen, Adressen drucken, Zertifikate drucken, kuvertieren — und dann den Haken setzen. Ob Etikett oder Umschlag und in welcher Größe, steht in den Einstellungen.',
        deselectAll: 'Auswahl aufheben',
        selectAll: 'Alle auswählen',
        startLabel: 'Erstes Etikett auf dem Bogen',
        printAddresses: 'Adressen drucken ({n})',
        reload: 'Neu laden',
        loading: 'lädt…',
        nothingToSend: 'Nichts zu versenden.',
        drawerTitle: 'Anschrift — {holder} ({num})',
        save: 'Speichern',
        cancel: 'Abbrechen',
        drawerIntro: 'Was hier steht, geht auf das Etikett beziehungsweise den Umschlag. Die Bestellung im Shop bleibt unverändert — korrigiert wird der Versand, nicht die Historie des Shops. Jede Änderung wird mit Name und Zeitpunkt festgehalten.',
        changedNotice: 'Bereits {count}× von Hand geändert, zuletzt von {by} am {date}.',
        company: 'Firma',
        firstName: 'Vorname',
        lastName: 'Nachname',
        street: 'Straße und Hausnummer',
        line2: 'Adresszusatz',
        postalCode: 'PLZ',
        city: 'Ort',
        country: 'Land',
        requiredHint: 'Zum Verschicken müssen mindestens Straße, PLZ und Ort ausgefüllt sein. Das Land steht nur dann auf dem Etikett, wenn es nicht Deutschland ist.',
        recentTitle: 'Zuletzt versandt',
        recentHint: 'Für den Fall, dass sich jemand verklickt hat. Zurücknehmen stellt die Zeile wieder oben ein.',
    },

    // src/client/pages/AuthorForm.tsx
    PAGES_AUTHOR_FORM_TEXTS: {
        statusActiveHint: 'Wird normal abgerechnet.',
        statusBlockedSince: 'Geblockt seit {date} — Umsätze davor werden noch abgerechnet, spätere nicht mehr.',
        statusBlockedNoDate: '⚠ Geblockt, aber ohne Zeitpunkt (Altbestand) — es wird noch abgerechnet. Einmal speichern setzt den Zeitpunkt auf jetzt.',
        statusBlockFuture: 'Ab dem Speichern werden künftige Umsätze nicht mehr vergütet; bereits aufgelaufene schon.',
        lockGranted: 'Du kannst jetzt bearbeiten.',
        loadError: 'Fehler beim Laden',
        noAuthorLoaded: 'Kein Dozent geladen',
        savedVatConfirmed: 'Dozent gespeichert. USt-IdNr. {vatId} von VIES bestätigt.',
        savedViesUnreachable: 'Dozent gespeichert. VIES war nicht erreichbar — USt-IdNr. noch unbestätigt.',
        savedVatInvalid: 'Dozent gespeichert, aber VIES weist {vatId} als UNGÜLTIG zurück — so ist keine Auszahlung möglich.',
        saved: 'Dozent gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        viesUnreachable: 'VIES ist gerade nicht erreichbar — Status unverändert.',
        vatValidApply: '{vatId} ist gültig — bitte „Übernehmen", damit es erhalten bleibt.',
        vatInvalid: '{vatId} ist laut VIES ungültig.',
        vatValid: '{vatId} ist gültig.',
        checkFailed: 'Prüfung fehlgeschlagen',
        formatValid: 'Format gültig ({iban}) — Prüfziffer und Länge stimmen. Ob das Konto existiert und wem es gehört, sagt das nicht.',
        formatInvalid: 'Format ungültig: {reason}',
        author: 'Dozent',
        loading: 'lädt…',
        titleView: 'Dozent (Ansicht): {name}',
        titleEdit: 'Dozent: {name}',
        apply: 'Übernehmen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Dozenten.',
        lockedBefore: 'Dieser Datensatz wird gerade an anderer Stelle bearbeitet. Du bist in der Warteschlange',
        lockedPosition: ' (Position {position})',
        lockedAfter: '. Sobald er freigegeben wird, kannst du bearbeiten.',
        name: 'Name *',
        company: 'Firma (optional)',
        salutation: 'Anrede',
        salutationNone: '— keine —',
        title: 'Titel (optional)',
        titlePlaceholder: 'z. B. Dr., HP, THP',
        wpId: 'WP-ID ',
        wpIdTip: 'Die Benutzer-ID in WordPress — der Schlüssel, auf den beide Seiten sich beziehen. Kommt aus der Artikelmeldung und wird hier nicht geändert.',
        email: 'E-Mail',
        status: 'Status',
        statusActive: 'aktiv',
        statusBlocked: 'geblockt',
        address: 'Anschrift',
        street: 'Straße',
        line2: 'Adresszusatz (optional)',
        postalCode: 'PLZ',
        city: 'Ort',
        country: 'Land',
        tax: 'Steuer ',
        taxTip: 'Wir rechnen aus Zypern ab: Ihre Leistung wird dort besteuert, wo wir sitzen — die Umsatzsteuer schulden wir (Reverse Charge). Das setzt eine gültige USt-IdNr. des Dozenten voraus. Ohne sie ist keine Abrechnung möglich; eine Steuernummer kann sie nicht ersetzen.',
        vatId: 'USt-IdNr. ',
        vatIdRequired: '(Pflicht für die Auszahlung)',
        vatIdPlaceholder: 'DE123456789',
        vatCheckTitle: 'Bei VIES prüfen — auch eine noch nicht übernommene Nummer',
        vatCheck: 'Prüfen',
        noNumber: '(keine Nummer)',
        vatNotCheckedTitle: 'Noch nicht geprüft. Der gespeicherte Befund gilt für {stored}: {verdict}',
        vatUnreachableTitle: '{vatId}: VIES war nicht erreichbar — nicht geprüft.',
        vatConfirmedPendingTitle: '{vatId} ist von VIES bestätigt. Noch nicht übernommen — mit „Übernehmen" speichern.',
        vatRejectedTitle: '{vatId} weist VIES als ungültig zurück.',
        bank: 'Bankverbindung',
        iban: 'IBAN',
        ibanPlaceholder: 'DE…',
        ibanCheckTitle: 'Prüft Länge, Länderkürzel und Prüfziffer der eingetippten IBAN. Sagt NICHT, ob das Konto existiert oder wem es gehört.',
        ibanCheck: 'Format prüfen',
        ibanNone: 'Keine IBAN hinterlegt.',
        ibanChangedSinceCheck: 'Seit der Prüfung geändert — der alte Befund gilt nicht mehr.',
        ibanNotChecked: 'Noch nicht geprüft.',
        ibanFormatOk: 'Format in Ordnung — Prüfziffer und Länge stimmen. Ob das Konto existiert und wem es gehört, sagt das NICHT.',
        ibanNoRight: 'Die IBAN darf nur mit Schreibrecht angezeigt werden — ohne das kann sie hier nicht geändert werden.',
        ibanLoadFailed: 'Die IBAN konnte nicht geladen werden.',
        retry: 'Erneut versuchen',
        ibanHint: 'Wird geprüft (Prüfziffer und Länge) — ein Tippfehler wird beim Speichern abgewiesen, „Format prüfen" sagt es sofort. Das prüft nur die Nummer selbst, nicht ob es das Konto gibt. Leeren und speichern entfernt sie.',
        bic: 'BIC (optional)',
        accountHolder: 'Kontoinhaber',
        settlement: 'Abrechnung',
        feeType: 'Gebühr-Art ',
        feeTypeTip: 'Leer = Vorgabe der Marke. Eine Artikel-Gebühr sticht diese hier.',
        feeBrandDefault: '— Marken-Vorgabe —',
        percent: 'Prozent',
        fixed: 'Festbetrag',
        feeValue: 'Wert ',
        payoutThreshold: 'Mindestauszahlung ({sign}) ',
        payoutThresholdTip: 'Darunter wird nicht überwiesen, sondern in den Folgemonat vorgetragen. Leer = es gilt der Wert der Marke (Einstellungen → Marke). Ein Wert hier ist eine Abweichung für diesen Dozenten.',
        payoutThresholdPlaceholder: 'folgt der Marke',
        items: 'Artikel ',
        itemsTip: 'Wie viele Artikel im Katalog diesem Dozenten zugeordnet sind.',
        notes: 'Notiz (intern)',
        releasedTitle: 'Dozent freigegeben',
        released: {
            quoteOpen: '„',
            activeSince: '" ist wieder aktiv. Aus der Sperrzeit seit',
            remainVerb: { one: 'bleibt', other: 'bleiben' },
            salesCount: { one: 'ein Verkauf', other: '{count} Verkäufe' },
            over: ' über',
            dash: ' — ',
            pronoun: { one: 'er wird', other: 'sie werden' },
            notPaid: ' nicht vergütet.',
            hintBefore: 'Falls doch etwas davon ausgezahlt werden soll: ',
            hintBold: 'Dozenten-Abrechnung → Reiter „Verkäufe"',
            hintAfter: ', den Dozenten wählen, die betroffenen Zeilen ankreuzen und „Nachabrechnung erstellen". Sie erscheinen dann in der Abrechnung des laufenden Monats, sobald dieser Monat abgerechnet wird (Reiter „Abrechnungen" → Monat und Marke wählen → „Monat abrechnen").',
        },
        understood: 'Verstanden',
    },

    // src/client/pages/MonthlyExport.tsx
    PAGES_MONTHLY_EXPORT_TEXTS: {
        ingestReason: {
            'no-attachment': 'Kein PDF-Anhang',
            'no-invoice-pdf': 'Keine Rechnung erkannt (PDF ohne Datum/Betrag)',
            'needs-review': 'Nicht automatisch lesbar (evtl. Scan)',
            'link-no-pdf': 'Link-Rechnung: PDF nicht geladen',
            'mollie-invoice-no-token': 'Mollie-Rechnung: kein Token',
            'mollie-invoice-permission': 'Mollie-Rechnung: Token ohne invoices.read',
            'mollie-invoice-not-found': 'Mollie-Rechnung: nicht gefunden',
            'mollie-invoice-period': 'Mollie-Rechnung: Leistungsmonat nicht eindeutig',
            'mollie-invoice-pdf': 'Mollie-Rechnung: PDF nicht geladen',
            'mollie-invoice-api': 'Mollie-Rechnung: Abruf fehlgeschlagen',
        } as Record<string, string>,
        ingestStatus: {
            imported: 'importiert',
            problem: 'Problem',
            ignored: 'ignoriert',
        },
        type: 'Typ',
        year: 'Jahr',
        month: 'Monat',
        fileCount: '{n} Datei(en)',
        cancel: 'Abbrechen',
        save: 'Speichern',
        delete: 'Löschen',
        loadError: 'Fehler beim Laden',
        error: 'Fehler',
        uploaded: '{n} Datei(en) nach {month} {year} hochgeladen.',
        movedTo: '„{name}" nach {target} verschoben.',
        invalidAmount: 'Ungültiger Betrag',
        amountSaveFailed: 'Betrag speichern fehlgeschlagen',
        yearLabel: 'Jahr {year}',
        deletedToast: '{label} gelöscht.',
        deleteFailedDot: 'Löschen fehlgeschlagen.',
        deleteFailed: 'Löschen fehlgeschlagen',
        expenseTypeSaved: 'Ausgaben-Typ gespeichert.',
        fileDeleted: 'Datei gelöscht.',
        skippedNote: ' · übersprungen: {list}',
        stripeDone: 'Stripe-Report erstellt: {files} Datei(en), {count} Zahlungen{note}',
        paypalDone: 'PayPal-Report erstellt: {count} Transaktionen.',
        paypalNone: 'PayPal: keine Bewegung, kein Statement angelegt.',
        mollieDone: 'Mollie-Report erstellt: {count} Vorgänge.',
        mollieNone: 'Mollie: keine Bewegung, kein Statement angelegt.',
        smtpMissing: 'SMTP nicht konfiguriert — Versand übersprungen.',
        zipSent: 'ZIP an Steuerberater gesendet.',
        monthClosedToast: 'Monat abgeschlossen — später eintreffende Belege landen im nächsten offenen Monat.',
        monthReopenedToast: 'Abschluss aufgehoben — Belege landen wieder in diesem Monat.',
        pollRunning: 'Abruf läuft bereits…',
        polled: 'Abgerufen: {imported} importiert · {problems} Problemfälle · {skipped} übersprungen.',
        ingestIgnored: 'Ignoriert — Mail in den Papierkorb.',
        ingestGone: 'E-Mail ist nicht mehr im Postfach.',
        ingestFiled: 'Beleg abgelegt.',
        ingestStillNoInvoice: 'Weiterhin keine Rechnung erkannt.',
        actionFailed: 'Aktion fehlgeschlagen',
        reportTitle: 'Report {month}/{year}',
        sentOn: 'Am {date} an Steuerberater versendet',
        closedOn: 'Manuell abgeschlossen am {date}',
        title: 'Monats-Export',
        info: 'Jahr → Monat → Typ wählen, Belege per Drag & Drop ablegen, Checkliste abarbeiten, ZIP an den Steuerberater senden.',
        pollTitle: 'Belege aus dem Postfach invoices@deja.ltd abrufen',
        poll: 'E-Mails abrufen',
        tabExport: 'Ablage & Aktionen',
        tabInbox: 'Beleg-Eingang',
        tagNew: 'neu',
        createMonth: 'Monat anlegen',
        pickMonth: 'Monat wählen',
        files: 'Dateien',
        addFiles: '+ Dateien',
        pickType: 'Typ wählen',
        dropHere: 'Dateien hierher ziehen',
        empty: 'leer',
        expenseTypeTitle: 'Ausgaben-Typ',
        amountTitle: 'Rechnungsbetrag (für die Bilanz)',
        amountPlaceholder: 'Betrag {sign}',
        untrackedTitle: 'Vor dem Tracking abgelegt',
        actions: 'Aktionen',
        checklistTitle: 'Checkliste {month}/{year}',
        doneCount: ' · {done}/{total} erledigt',
        frozenTitle: 'Stand beim Versand an den Steuerberater; spätere Änderungen an den Prüfpunkten gelten hier nicht',
        frozen: ' · eingefroren',
        noBinding: 'ohne Verknüpfung',
        bindingUsed: ' — bereits verwendet',
        appliesToAllMonths: 'Gilt für alle Monate, bis du es änderst',
        required: 'muss da sein',
        optional: 'optional',
        mayBeEmpty: 'kann leer sein',
        mailNote: 'Hinweis in der Begleitmail',
        editItemTitle: 'Punkt bearbeiten',
        edit: 'Bearbeiten',
        removeItemTitle: 'Punkt entfernen',
        remove: 'Entfernen',
        newItemPlaceholder: 'Neuer Punkt…',
        add: '+ Hinzufügen',
        sendLocked: 'Senden ist gesperrt, bis alle erforderlichen Checklisten-Punkte erledigt sind.',
        openListSentence: 'Offen: {items}.',
        openList: 'Offen: {items}',
        showReport: 'Report anzeigen',
        reportPdf: 'Report als PDF',
        stripeTitle: 'Zahlungen (XLSX) + Itemized Payouts/Balance Change über die Stripe Reporting-API',
        stripe: 'Stripe-Report',
        paypal: 'PayPal-Report',
        mollieTitle: 'Kontoauszug je Mollie-Marke (Zahlungen, Erstattungen; Saldo/Gebühren erst im Live-Betrieb)',
        mollie: 'Mollie-Report',
        send: 'An Steuerberater senden',
        reopenTitle: 'Monat wieder öffnen — neue Belege landen dann wieder in diesem Monat',
        closeTitle: 'Monat als an den Steuerberater übergeben markieren — später eintreffende Belege landen im nächsten offenen Monat',
        reopen: 'Abschluss aufheben',
        close: 'Monat abschließen',
        recipient: 'Empfänger: {to}',
        net: 'Netto',
        tax: 'USt',
        gross: 'Brutto',
        colCountry: 'Land',
        colCount: 'Anzahl',
        ossTitle: 'One-Stop-Shop (OSS)',
        ossTip: 'Meldezahlen für den One-Stop-Shop (EU-Regelung): EU-Fernverkäufe an Privatkunden je Bestimmungsland und Steuersatz. Ohne Sitzland ({baseCountry}), ohne Nicht-EU und ohne Reverse-Charge (B2B). Teilmenge der Ländertabelle darüber — nicht addieren.',
        ossNone: 'Keine OSS-relevanten Umsätze in diesem Monat.',
        colRate: 'Satz',
        colInvoices: 'Rechnungen',
        ossSum: 'Summe OSS',
        inboxTitle: 'Beleg-Eingang (E-Mail)',
        inboxTip: 'Belege aus invoices@deja.ltd. Nur PDFs mit Rechnungsdatum und -betrag werden als Rechnung abgelegt; andere Anhänge (z. B. Anleitungen) werden übersprungen. Problemfälle hier prüfen.',
        inboxEmpty: 'Noch keine E-Mail-Belege verarbeitet.',
        colStatus: 'Status',
        colReceived: 'Eingang',
        colFrom: 'Absender',
        colSubject: 'Betreff',
        colInvoice: 'Rechnung',
        colFiles: 'Dateien',
        savedCount: '{n} abgelegt',
        skippedCount: '{n} übersprungen',
        duplicateCount: '{n} doppelt',
        retry: 'Wiederholen',
        acceptTitle: 'Alle PDFs trotzdem als Rechnung ablegen (z. B. Scan)',
        accept: 'Trotzdem ablegen',
        ignore: 'Ignorieren',
        deleteFileTitle: 'Datei löschen',
        deleteFileQuestion: '„{name}" wirklich löschen?',
        deleteYearTitle: 'Jahr löschen',
        deleteMonthTitle: 'Monat löschen',
        deleteDirQuestion: '{label} wirklich löschen? Nur möglich, wenn leer.',
        uploadTitle: 'Dateien hochladen',
        upload: 'Hochladen',
        moveTitle: '„{name}" verschieben',
        move: 'Verschieben',
        assignExpenseTypeMenu: 'Ausgaben-Typ zuordnen…',
        hintHasMonths: 'enthält noch Monate',
        hintNotEmpty: 'ist nicht leer',
        deleteLabelBlocked: '{label} löschen — {hint}',
        deleteLabel: '{label} löschen',
        assignExpenseType: 'Ausgaben-Typ zuordnen',
        noType: '— kein —',
    },

    // src/client/pages/AuthorStatements.tsx
    PAGES_AUTHOR_STATEMENTS_TEXTS: {
        months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
            'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        status: {
            pending: 'steht an', draft: 'Entwurf', finalized: 'festgeschrieben', paid: 'bezahlt',
        },
        payment: {
            open: 'offen',
            submitted: 'zur Zahlung eingereicht',
            paid: 'bezahlt',
        },
        loadFailed: 'Laden fehlgeschlagen',
        salesLoadFailed: 'Verkäufe nicht ladbar',
        periodsLoadFailed: 'Zeiträume nicht ladbar',
        failed: 'Fehlgeschlagen',
        runFinalized: { one: '{n} Abrechnung festgeschrieben', other: '{n} Abrechnungen festgeschrieben' },
        runPending: '{n} als Entwurf liegen geblieben: ',
        runFailed: '{n} fehlgeschlagen',
        runNothing: 'nichts zu tun — es war schon alles abgerechnet',
        runError: 'Lauf fehlgeschlagen',
        previewFailed: 'Vorschau fehlgeschlagen',
        ibanConfirmed: 'IBAN bestätigt.',
        confirmFailed: 'Bestätigen fehlgeschlagen',
        sepaDone: { one: 'Eine Überweisung über {total} — Datei {msgId} heruntergeladen. Noch NICHT eingereicht.', other: '{count} Überweisungen über {total} — Datei {msgId} heruntergeladen. Noch NICHT eingereicht.' },
        exportFailed: 'Export fehlgeschlagen',
        linesLoadFailed: 'Die Verkäufe konnten nicht geladen werden.',
        title: 'Dozenten-Abrechnung',
        info: {
            part1: 'Monatliche Abrechnung mit den Dozenten. Der Lauf erzeugt je Dozent einen Entwurf aus den Verkäufen des Monats und schreibt fest, was vollständig ist. Dabei bekommt der Beleg seine Nummer und wird zur Gutschrift (Selbstfakturierung) — danach ist er nicht mehr änderbar.',
            part2: 'Die Überweisung selbst machen Sie in der Bank. Eine erzeugte SEPA-Datei ist deshalb noch ',
            bold: 'kein geflossenes Geld',
            part3: ' — sie gilt als eingereicht, bis Sie unten in der Dateiliste bestätigen, dass die Bank ausgeführt hat.',
        },
        tabStatements: 'Abrechnungen',
        tabSales: 'Verkäufe',
        rechargeDone: '{updated} Zeile(n) über {payout} für {period} vorgemerkt.',
        rechargeSkipped: ' {n} übersprungen.',
        rechargeVisible: ' Sichtbar wird das, sobald {period} abgerechnet wird.',
        rechargeFailed: 'Nachabrechnung fehlgeschlagen',
        period: 'Zeitraum',
        brand: 'Marke',
        runNothingTitle: 'Es steht nichts an — alle Dozenten mit Umsatz sind bereits abgerechnet.',
        runTitle: 'Erzeugt die Abrechnungen und schreibt sie fest.',
        running: 'läuft…',
        nothingToSettle: 'Nichts abzurechnen',
        runButton: { one: 'Monat abrechnen — 1 Dozent, {sum}', other: 'Monat abrechnen — {authors} Dozenten, {sum}' },
        runTip: {
            part1: 'Der Lauf erzeugt für jeden Dozenten mit Umsatz eine Abrechnung, baut vorhandene Entwürfe neu auf und ',
            bold1: 'schreibt fest, was vollständig ist',
            part2: '. Festgeschrieben heißt: Der Beleg bekommt eine Nummer und ist',
            bold2: ' nicht mehr änderbar',
            part3: '.',
            part4: '⚠️ Eine Zeile, die ',
            bold3: 'später',
            part5: ' zur Nachabrechnung vorgemerkt wird, erscheint in einer festgeschriebenen Abrechnung ',
            bold4: 'nie',
            part6: ' — sie landet dann im Monat der Entscheidung.',
            part7: 'Wem etwas fehlt (IBAN, USt-IdNr., Anschrift, E-Mail), der bleibt Entwurf und wird nach dem Lauf genannt. Trägt man es nach, holt der nächste Klick das Festschreiben nach.',
        },
        sepaNothingTitle: 'Nichts zur Überweisung — es ist nichts festgeschrieben, das noch in keiner Datei war.',
        sepaTitle: 'Zeigt zuerst, was in die Datei käme.',
        sepaCreateEllipsis: 'SEPA-Datei erzeugen…',
        noTransfer: 'Keine Überweisung',
        runsTitle: 'Überweisungsdateien dieses Monats',
        runsTip: {
            part1: 'Jede Zeile ist eine erzeugte SEPA-Datei. Solange sie nicht bestätigt ist, gilt das Geld als ',
            bold: 'eingereicht, nicht geflossen',
            part2: ' — die Datei muss ja erst in die Bank hochgeladen werden, und die Bank kann sie abweisen. Bestätigen Sie hier, wenn die Bank ausgeführt hat: Das setzt alle Gutschriften dieser Datei auf einmal auf bezahlt.',
        },
        colCreated: 'Erzeugt',
        colFile: 'Datei',
        colCreditNotes: 'Gutschriften',
        colSum: 'Summe',
        colState: 'Zustand',
        partlyPaid: 'teilweise bezahlt ({paid} von {count})',
        markAllPaid: 'Alle als bezahlt markieren',
        markRestPaid: 'Restliche {n} als bezahlt markieren',
        done: 'erledigt',
        noSales: 'Für {period} hat kein Dozent Umsätze — es gibt nichts abzurechnen.',
        thisMonth: 'diesen Monat',
        plan: {
            before: '„Monat abrechnen" erzeugt ',
            count: { one: 'eine Abrechnung', other: '{n} Abrechnungen' },
            mergeAt: ' Bei ',
            mergeOne: ' werden',
            mergeMany: '{n} Dozenten werden jeweils',
            mergeText: 'Entwurf und anstehende Verkäufe zu ',
            mergeBold: 'einer',
            mergeEnd: ' Abrechnung zusammengeführt.',
            tail: 'Wem Angaben fehlen, der bleibt Entwurf. Der Lauf startet nicht von selbst.',
        },
        overTotal: 'über zusammen ',
        colAuthor: 'Dozent',
        colSales: 'Verkäufe',
        colPayoutNet: 'Vergütung netto',
        colPayout: 'Auszahlung',
        colStatus: 'Status',
        colDocument: 'Beleg',
        showSales: 'Verkäufe anzeigen',
        hideSales: 'Verkäufe verbergen',
        addsToDraft: 'kommt zum Entwurf oben hinzu',
        missing: 'fehlt: ',
        freeAccess: { one: '1 Gratis-Bezug', other: '{n} Gratis-Bezüge' },
        carriedOut: '{amount} vorgetragen',
        owedToUs: '{amount} offen zu unseren Gunsten',
        fileFrom: 'Datei vom {date}',
        viewDraft: 'Entwurf ansehen',
        waitingForData: 'wartet auf Angaben',
        finalizedNextRun: 'wird beim nächsten Lauf festgeschrieben',
        markPaid: 'Als bezahlt markieren',
        undoMark: 'Markierung zurücknehmen',
        transferReturned: 'Überweisung kam zurück',
        loading: 'lädt…',
        colSold: 'Verkauft',
        colItem: 'Artikel',
        colKind: 'Art',
        colListPrice: 'Listenpreis',
        colFee: 'Gebühr',
        colPayoutLine: 'Vergütung',
        addedNextRun: 'kommt beim nächsten Lauf dazu',
        chargebackFee: 'Rückbelastungsgebühr',
        catchUp: 'nachvergütet',
        earlierMonth: 'aus früherem Monat',
        noLines: 'keine Verkäufe',
        sumLabel: 'Summe {period}',
        notYetSettled: '{amount} noch nicht abgerechnet',
        draftReady: '{amount} im Entwurf — kommt mit „Monat abrechnen"',
        draftBlocked: '{amount} im Entwurf — wartet auf fehlende Angaben',
        toTransfer: '{amount} zur Überweisung',
        nothingToTransfer: 'nichts zu überweisen',
        submittedSum: '{amount} zur Zahlung eingereicht',
        sepaCreate: 'SEPA-Datei erzeugen',
        sepaConfirm: { one: 'Eine Überweisung — Datei herunterladen', other: '{n} Überweisungen — Datei herunterladen' },
        download: 'Herunterladen',
        skippedHead: { one: 'Eine Gutschrift kommt NICHT in die Datei:', other: '{n} Gutschriften kommen NICHT in die Datei:' },
        newIban: '. Neue IBAN ',
        confirmIban: 'IBAN bestätigen',
        nothingLeft: 'Es bleibt nichts zu überweisen.',
        transfersCount: { one: '1 Überweisung', other: '{n} Überweisungen' },
        sepaNote: {
            part1: 'Die Datei wird ',
            bold: 'heruntergeladen, nicht eingereicht',
            part2: ' — Sie laden sie im Online-Banking hoch. „Bezahlt" wird dadurch nichts; das vermerken Sie danach selbst.',
        },
        runConfirmTitle: 'Monat abrechnen',
        runConfirmLabel: 'Abrechnen und festschreiben',
        fix: {
            count: { one: '1 Abrechnung wird', other: '{n} Abrechnungen werden' },
            together: 'festgeschrieben, zusammen ',
            sie: '. Sie',
            get: { one: 'bekommt', other: 'bekommen' },
            number: ' eine Belegnummer und',
            is: { one: 'ist', other: 'sind' },
            after: ' danach ',
            bold: 'nicht mehr änderbar',
        },
        nothingFixedBold: 'Es wird nichts festgeschrieben',
        nothingFixed: ' — allen fehlen noch Angaben. Der Lauf baut nur die Entwürfe neu auf.',
        leftBefore: 'Als Entwurf ',
        leftVerb: { one: 'bleibt', other: 'bleiben' },
        leftAfter: ' liegen:',
        leftEntry: '{name} (fehlt: {missing})',
        paidConfirm: 'Als bezahlt vermerken',
        settlementFallback: 'Abrechnung',
        over: ' über',
        paidNote1: {
            part1: 'Das löst ',
            bold: 'keine Überweisung aus',
            part2: '. Es hält fest, dass Sie sie in der Bank bereits veranlasst haben.',
        },
        paidNote2: 'Der Dozent sieht die Abrechnung danach als ausbezahlt. Zurücknehmen können Sie es — dafür gibt es ',
        paidDone: 'Als bezahlt vermerkt.',
        run2Title: 'Überweisungsdatei als bezahlt bestätigen',
        run2File: 'Datei ',
        run2From: ' vom',
        run2Count: { one: '1 Gutschrift wird', other: '{n} Gutschriften werden' },
        run2Marked: 'als bezahlt markiert, zusammen ',
        run2Note: {
            part1: 'Bestätigen Sie das erst, wenn die Bank die Datei ',
            bold: 'ausgeführt',
            part2: 'hat — nicht schon beim Hochladen. Die Dozenten sehen die Abrechnung danach als ausbezahlt.',
        },
        run2Done: 'Überweisungsdatei bestätigt.',
        unpayNote1: {
            part1: 'Für den Fall, dass hier ',
            bold: 'versehentlich',
            part2: ' auf bezahlt gesetzt wurde. Der Dozent sieht die Abrechnung danach wieder als offen.',
        },
        unpayNote2: {
            part1: 'Es wird ',
            bold: 'nicht erneut überwiesen',
            part2: ': Die Gutschrift war schon in einer Überweisungsdatei und kommt nicht noch einmal hinein. Ist das Geld zurückgekommen, nehmen Sie ',
        },
        unpayDone: 'Markierung zurückgenommen.',
        payAgain: 'Erneut auszahlen',
        returnedNote1: {
            part1: 'Für den Fall, dass die Bank die Überweisung ',
            bold: 'zurückgegeben',
            part2: ' hat — das Geld ist also nicht angekommen.',
        },
        returnedNote2: {
            part1: 'Die Gutschrift kommt damit in die ',
            bold: 'nächste Überweisungsdatei',
            part2: '. Der Beleg selbst bleibt, wie er ist; seine Nummer ändert sich nicht.',
            part3: '⚠️ Prüfen Sie vorher die ',
            bold2: 'IBAN',
            part4: ' — meist ist sie der Grund.',
        },
        returnedDone: 'Kommt in die nächste Überweisungsdatei.',
        pickPlaceholder: '— wählen —',
        wpRef: ' (WP {ref})',
        recharge: 'Nachabrechnung erstellen',
        pickAuthor: 'Bitte einen Dozenten wählen.',
        noSalesForAuthor: 'Für diesen Dozenten sind keine Verkäufe erfasst.',
        nothingToRecharge: 'Nichts zum Nachabrechnen — es ist keine Zeile ',
        selectLine: 'Zeile {title} auswählen',
        catchUpFor: ' für {period}',
    },

    // ══ Client: Einstellungen, Vorlagen, Webhooks, Beleg-Zuordnung ══

    // Client texts of the settings and template pages.

    // src/client/pages/Settings.tsx
    PAGES_SETTINGS_TEXTS: {
        logModules: {
            system: 'System',
            auth: 'Anmeldung / Einstellungen',
            webhook: 'Webhooks',
            stripe: 'Stripe',
            paypal: 'PayPal',
            billing: 'Rechnungen / Abos',
            ingest: 'Beleg-Erkennung',
            export: 'Monats-Export',
            mail: 'E-Mail-Versand',
            scheduler: 'Automatisierung',
        },
        loadFailed: 'Fehler beim Laden',
        general: 'Allgemein',
        saveOrDiscardPane: 'Bitte zuerst speichern oder die Änderungen an „{pane}" verwerfen.',
        saveBeforeClear: 'Bitte zuerst speichern oder verwerfen — das Entfernen lädt die Einstellungen neu.',
        cleared: '„{label}" entfernt.',
        clearFailed: 'Entfernen fehlgeschlagen',
        saved: 'Gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        saveBeforeWebhook: 'Bitte zuerst speichern, dann den Webhook einrichten.',
        webhookSetupFailed: 'Webhook-Einrichtung fehlgeschlagen',
        title: 'Einstellungen',
        titleReadOnly: 'Einstellungen (Ansicht)',
        loading: 'lädt…',
        apply: 'Übernehmen',
        discard: 'Verwerfen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Einstellungen.',
        paneBlocked: 'Erst speichern oder die Änderungen an „{pane}" verwerfen',

        // Shared field labels
        smtpHost: 'SMTP Host',
        port: 'Port',
        tlsSecure: 'TLS/SSL (secure)',
        smtpUser: 'SMTP Benutzer',
        smtpPass: 'SMTP Passwort',
        nameCompany: 'Name / Firma',
        postalCode: 'PLZ',
        city: 'Ort',
        country: 'Land',
        test: 'Test',
        live: 'Live',
        stripeName: 'Stripe',
        mollieName: 'Mollie',
        feeKind: 'Art',
        feeValue: 'Wert',
        percentOption: 'Prozentsatz (%)',
        fixedOption: 'Festbetrag ({sign})',
        notSet: 'nicht gesetzt',
        or: 'oder',
        emptyEquals: 'leer = {value}',

        checkout: {
            title: 'Checkout-Hub (API-Zugang)',
            intro1: 'Bearer-Schlüssel, den die Shop-Fronts (msbc, vitalpilze) an ',
            intro2: ' senden. Muss auf den Fronts identisch hinterlegt sein. Ersetzt die Umgebungsvariable',
            intro3: ' (die wirkt nur noch als Fallback).',
            apiKey: 'Checkout-API-Key',
        },
        sepa: {
            title: 'Dozenten-Einstellungen',
            enabledHeading: 'Dozenten-Abrechnung aktiv',
            enabledIntro: 'Je Marke. Ist sie aus, zeigt der Markenreiter das Panel „Dozenten-Abrechnung“ nicht, und Gebühr, Mindestauszahlung und Rückbelastungsgebühr zählen nicht als Mangel. Gespeicherte Werte bleiben erhalten.',
            bankHeading: 'SEPA-Überweisung an Dozenten',
            intro:'Von diesem Konto gehen die Dozenten-Gutschriften. Die Angaben stehen als Auftraggeber in der SEPA-Sammeldatei, die in der Dozenten-Abrechnung erzeugt und im Online-Banking hochgeladen wird.',
            holder: 'Kontoinhaber',
            bic: 'BIC',
            bicTip: 'Optional. Fehlt er, trägt die Datei „NOTPROVIDED" — im SEPA-Raum genügt die IBAN.',
            iban: 'IBAN',
            ibanPlaceholder: 'mit Ländercode vorn, z. B. DE…',
            ibanHint: 'Wird geprüft (Prüfziffer und Länge) — ein Tippfehler wird beim Speichern abgewiesen. Leeren und speichern entfernt sie.',
        },
        paypal: {
            title: 'PayPal',
            testmode: 'Testmodus (Sandbox-Zugang verwenden)',
            sandboxClientId: 'Sandbox Client ID',
            clientIdPlaceholder: 'A…',
            sandboxSecret: 'Sandbox Secret',
            liveClientId: 'Live Client ID',
            liveSecret: 'Live Secret',
        },
        mollieBalance: {
            title: 'Mollie (Saldo für den Monatsreport)',
            intro: 'Ein Token für alle Mollie-Marken: Der Saldo gehört zum ganzen Mollie-Konto, nicht zur Marke. Zahlungen laufen weiter über die API Keys der Marken. Auf einer Testinstanz fragt der Report Mollie immer im Testmodus.',
            token: 'Mollie API Access Token',
            tokenPlaceholder: 'access_…',
            tokenInfo: 'Anlegen im Mollie-Dashboard unter „API access tokens“ (Typ „Advanced access token“, früher „Organization access token“): alle Profile, nur das Recht balance-reports.read. Für die Produktion einen Token nur für Live, für die Testinstanz einen nur für Test.',
        },
        smtp: {
            title: 'Steuerberater & E-Mail (SMTP)',
            taxAdvisorEmail: 'Steuerberater E-Mail',
            bcc: 'Blindkopie (BCC) der Monatsmail',
            bccTip: 'Bekommt die Mail an den Steuerberater samt ZIP als Blindkopie — der Steuerberater sieht diese Adresse nicht. Mehrere Adressen mit Komma trennen. Leer = keine Kopie. Auf einer Testinstanz geht keine Kopie hinaus.',
            from: 'Absender (From)',
            testMailMode: 'Test-Mail-Modus',
            testMailForced: 'Auf dieser Testinstanz immer an: Ihre Daten sind eine Kopie der Produktion, ein dort gespeichertes „aus" gilt hier nicht.',
            testMailOn: 'An: Jede Mail geht an die Test-Mailadresse statt an den Empfänger (der steht im Betreff). Gilt auch auf der Produktion — oben steht dann ein Band.',
            testMailTo: 'Test-Mailadresse',
            testMailToHint: 'Ist der Test-Mail-Modus an und das Feld leer, wird keine Mail versendet.',
        },
        imap: {
            title: 'Beleg-Postfach (IMAP)',
            enabled: 'E-Mail-Abruf aktiv (Intervall unter „Automatisierung")',
            host: 'IMAP Host',
            user: 'IMAP Benutzer',
            pass: 'IMAP Passwort',
            mailbox: 'Postfach',
            trash: 'Papierkorb-Ordner',
            notify: 'Info-Mail bei Problemfällen an',
            notifyTip: 'Adresse, an die Hinweise zu Mails ohne Anhang / Importfehlern mit Aktions-Links gehen.',
            adminNotify: 'Info-Mail bei Betriebsmeldungen an',
            adminNotifyTip: 'Adresse für Meldungen, die NICHT aus dem Beleg-Postfach kommen — etwa eine Zustellung an vitalpilze, die endgültig gescheitert ist. Leer = es gilt die Adresse darüber.',
            sameAsAbove: 'wie oben',
            publicUrl: 'Öffentliche Backoffice-URL',
            publicUrlTip: 'Basis-URL für die Links in den Info-Mails und die Stripe-Webhook-Endpoints (z. B. https://backoffice.deja.ltd). Leer = aus .env bzw. localhost. In der Live-Umgebung unbedingt setzen.',
            useCurrentOrigin: 'aktuelle Adresse übernehmen ({origin})',
        },
        automation: {
            title: 'Automatisierung / Zeitpläne',
            aboRunHour: 'Abo-Rechnungslauf — Uhrzeit',
            aboRunHourTip: 'Stunde (0–{max}), zu der täglich fällige Abo-Rechnungen erzeugt werden.',
            pollHours: 'Beleg-Abruf — Intervall (Std.)',
            pollHoursTip: 'Abstand zwischen den automatischen Beleg-Postfach-Abrufen in Stunden (nur wenn der E-Mail-Abruf oben aktiv ist).',
            autoImport: 'Zahlungsanbieter automatisch monatlich importieren',
            autoImportTip: 'Einmal pro Monat, ab dem gewählten Tag zur gewählten Uhrzeit, werden die Kontoauszüge von Stripe, PayPal und Mollie für den VORMONAT geholt und danach der Umsatzbericht unter Zahlungs-Reports abgelegt. Ein verpasster Termin wird nachgeholt; jede Periode läuft nur einmal.',
            importDay: 'Import-Tag im Monat (für den Vormonat)',
            importDayTip: 'Am wievielten des Monats der Vormonat gezogen wird. Auf 1–{max} begrenzt, damit der Lauf in jedem Monat fällt.',
            importTime: 'Uhrzeit (UTC)',
            importTimeTip: 'Uhrzeit des Imports am Import-Tag, in UTC wie alle Zeitpläne (im Sommer zwei Stunden vor deutscher Zeit, im Winter eine). Nicht zu früh nach Monatsende: PayPal meldet Zahlungen bis zu drei Stunden verspätet.',
            lastImported: 'Zuletzt automatisch importiert: {period}',
        },
        llm: {
            title: 'LLM-Erkennung (lokal)',
            enabled: 'Lokales LLM zur Beleg-Erkennung nutzen',
            enabledTip: 'Datum/Betrag/Lieferant werden per lokalem LLM (Ollama) extrahiert — nach ZUGFeRD, vor der Heuristik. On-prem (kein externer Dienst). Aus, leer oder nicht erreichbar → automatischer Rückfall auf die Heuristik.',
            url: 'Ollama-URL',
            urlTip: 'Endpoint des Ollama-Servers, z. B. http://localhost:11434 (lokal/SSH-Tunnel) oder die Container-Adresse auf dem Server.',
            urlPlaceholder: 'http://localhost:11434',
            model: 'Modell',
            timeout: 'Timeout (ms)',
        },
        cert: {
            title: 'Zertifikat-Versand',
            intro: 'Urkunden verschickt ein Mensch — kein Druckdienst kann Urkundenkarton und Direktversand zugleich. Was hier steht, ist seine Ausstattung: die Absenderangabe für Rückläufer und die Form, in der die Anschrift gedruckt wird. Die Arbeitsliste selbst steht unter „Zertifikat-Versand" in der Seitenleiste.',
            sender: 'Absender',
            senderTip: 'Nicht die Adresse des Betreibers — der sitzt in Zypern. Hier gehört die Anschrift der Person hin, die die Briefe wegbringt: Ein Rückläufer muss dort ankommen, wo ihn jemand öffnet. Leer lassen heißt: kein Absender auf Etikett oder Umschlag.',
            street: 'Straße',
            addition: 'Zusatz',
            printOn: 'Anschrift drucken auf',
            outputLabel: 'Adressetikett (A4-Bogen)',
            outputEndless: 'Endlosetikett (Rollendrucker)',
            outputEnvelope: 'direkt auf den Umschlag',
            label: 'Etikett',
            labelTip: 'Packung zur Hand? Nummer ablesen und auswählen — dann stimmen Maße, Spalten, Zeilen, Ränder und Abstände auf einen Griff. Steht die Ware nicht in der Liste, „Eigene Maße" wählen und von der Verpackung übertragen. Die eigenen Werte bleiben gespeichert, auch während eine Vorlage aktiv ist.',
            customPreset: 'Eigene Maße …',
            endlessIntro: 'Beim Rollendrucker bekommt jedes Etikett eine eigene Seite in Etikettengröße — es gibt kein Raster, keine Startposition und keine Füllrichtung. Verwendet werden nur Breite und Höhe',
            endlessSize: ' ({widthMm} × {heightMm} mm)',
            fillDirection: 'Füllrichtung',
            fillDirectionTip: 'Wie der Bogen aufgefüllt wird. ⚠️ „Erstes Etikett" zählt in derselben Richtung: Bei spaltenweiser Füllung ist Nummer 2 das Etikett DARUNTER, nicht das daneben.',
            fillRow: 'zeilenweise (links nach rechts)',
            fillColumn: 'spaltenweise (oben nach unten)',
            presetSummary: ' — {columns} × {rows} Etiketten, Rand oben {marginTopMm} mm, links {marginLeftMm} mm, Abstände {gapXMm} / {gapYMm} mm. Maße aus der Vorlagensammlung von glabels, gegen A4 nachgerechnet. Vor einem größeren Lauf bitte trotzdem einen Bogen probedrucken — die Ware kaufen Sie, nicht wir.',
            customIntro: 'Eigene Maße. Die Werte stehen auf der Verpackung; hier sind zuletzt {w} × {h} mm, {columns} × {rows}, randlos hinterlegt — das ist die Geometrie von {name}.',
            width: 'Breite (mm)',
            height: 'Höhe (mm)',
            columns: 'Spalten',
            rows: 'Zeilen',
            marginTop: 'Rand oben (mm)',
            marginLeft: 'Rand links (mm)',
            gapX: 'Abstand waagerecht (mm)',
            gapY: 'Abstand senkrecht (mm)',
            startPosition: 'Erstes Etikett (Vorgabe)',
            startPositionTip: 'Nur der Startwert. Beim Drucken lässt sich die Position in der Versandliste direkt ändern — ein angebrochener Bogen ist der Normalfall, und ein Wert, für den man erst hierher müsste, wird nicht gesetzt.',
            envelopeFormat: 'Umschlagformat',
            errors: 'Störmeldungen',
            errorsTip: 'Wohin die Meldung geht, wenn ein Versand endgültig scheitert oder eine Anschrift unbrauchbar ist. Leer lassen ist in Ordnung — dann gilt die Benachrichtigungsadresse des Beleg-Postfachs.',
            email: 'E-Mail',
            errorsPlaceholder: 'Adresse des Beleg-Postfachs',
        },
        certNotify: {
            title: 'Benachrichtigung bei neuem Versandauftrag',
            intro: 'Wird ausgelöst, sobald ein bezahltes Zertifikat auf der Versandliste erscheint. Beides lässt sich gleichzeitig einschalten; sind beide aus, geht keine Meldung raus — die Liste stimmt trotzdem, sie ist die verbindliche Quelle.',
            byMail: 'Per E-Mail benachrichtigen',
            recipients: 'Empfänger',
            recipientsTip: 'Mehrere durch Komma getrennt. Sie bekommen eine gemeinsame Mail, keine je Person — sie arbeiten an derselben Sache, und eine Antwort soll alle erreichen.',
            recipientsPlaceholder: 'anna@example.org, papa@example.org',
            byTelegram: 'Per Telegram benachrichtigen',
            telegramAdvice: 'Empfehlung: eine Gruppe statt einer Personenliste.',
            telegramIntro1: ' Betreiber und versendende Person in eine Gruppe, den Bot dazu, und hier nur diese eine Chat-ID eintragen. Dann pflegt niemand IDs nach — wer mitlesen soll, wird eingeladen.',
            telegramIntro2: 'Chat-ID herausfinden: Bot in die Gruppe holen, dort eine Nachricht schreiben, dann',
            telegramUrl: 'https://api.telegram.org/bot<TOKEN>/getUpdates',
            telegramIntro3: ' öffnen und',
            telegramIntro4: ' ablesen. Gruppen-IDs sind negativ, das ist richtig so.',
            botToken: 'Bot-Token',
            chatIds: 'Chat-ID(s)',
            chatIdsPlaceholder: '-1001234567890',
        },
        logging: {
            title: 'Protokollierung',
            defaultLevel: 'Standard-Loglevel',
            defaultLevelTip: 'Mindest-Schweregrad, der protokolliert wird (Datei + Datenbank). debug = am ausführlichsten, fatal = nur schwerste Fehler. Änderung wirkt sofort, ohne Neustart.',
            retention: 'Aufbewahrung (Tage)',
            retentionTip: 'Wie lange Logeinträge in der Datenbank aufbewahrt werden, bevor sie automatisch gelöscht werden (TTL).',
            perModule: 'Loglevel je Modul',
            perModuleTip: 'Optional pro Modul ein abweichendes Level — z. B. nur „Webhooks“ auf debug, um gezielt Fehlern nachzugehen, während der Rest auf dem Standard bleibt.',
            standard: 'Standard',
        },
        provider: {
            title: 'Zahlungsanbieter — {brand}',
            intro: 'Aktiver PSP dieser Marke. Der Checkout nutzt den gewählten Anbieter; die Schlüssel stehen in den Panels darunter.',
            active: 'Aktiver Zahlungsanbieter',
        },
        mollie: {
            title: 'Mollie — {brand}',
            intro: 'Eigenes Mollie-Konto dieser Marke. Webhook-Endpoint:',
            introEnd: '(wird beim Checkout automatisch gesetzt).',
            testmode: 'Testmodus (Test-API-Key verwenden)',
            testKey: 'Test API Key',
            testKeyPlaceholder: 'test_…',
            liveKey: 'Live API Key',
            liveKeyPlaceholder: 'live_…',
        },
        stripe: {
            title: 'Stripe — {brand}',
            intro: 'Eigenes Stripe-Konto dieser Marke. Webhook-Endpoint:',
            testmode: 'Testmodus (Sandbox-Key verwenden)',
            testKey: 'Test Secret Key',
            testKeyPlaceholder: 'sk_test_…',
            liveKey: 'Live Secret Key',
            liveKeyPlaceholder: 'sk_live_…',
            testWebhookSecret: 'Test Webhook-Secret',
            webhookSecretPlaceholder: 'whsec_…',
            testWebhookSecretInfo: 'Signing-Secret des Stripe-Webhook-Endpoints dieser Marke (Test). Beginnt mit whsec_…',
            liveWebhookSecret: 'Live Webhook-Secret',
            liveWebhookSecretInfo: 'Signing-Secret des Stripe-Webhook-Endpoints dieser Marke (Live). Aktiv, wenn der Testmodus aus ist.',
            setupWebhook: 'Webhook automatisch einrichten',
            setupWebhookTip: 'Legt den Webhook-Endpoint dieser Marke für den aktiven Modus (Test/Live) per Stripe-API an und speichert das Signing-Secret automatisch. Benötigt eine öffentliche https-Backoffice-URL (unter „Allgemein → Beleg-Postfach“). Vorher speichern.',
            endpointSet: 'Endpoint eingerichtet ({mode}).',
        },
        vitalpilze: {
            title: 'Vitalpilze-Webhook — {brand}',
            intro1: 'Ziel für die Fulfillment-Events an vitalpilze (Zugang, Rechnung, Mitgliedschaft). Nur die Site-Root angeben — der Pfad ',
            intro2: ' wird automatisch angehängt. Leer = deaktiviert. HMAC-signiert mit dem Secret.',
            url: 'Webhook-URL (Site-Root)',
            urlPlaceholder: 'nur die Adresse der Site, z. B. https://www.example.com',
            secret: 'Webhook-Secret',
            secretPlaceholder: 'Secret…',
            secretInfo: 'Gemeinsames HMAC-Secret. Muss mit dem in WP hinterlegten Secret übereinstimmen (dasselbe wie Live).',
        },
        author: {
            title: 'Dozenten-Abrechnung — {brand}',
            intro1:'Die Gebühr, die wir vom Listenpreis eines verkauften Artikels einbehalten.',
            introEither: ' Entweder',
            intro2: ' Prozentsatz ',
            intro3: ' Festbetrag, nie beides. Eine Gebühr am Dozenten oder am Artikel ',
            introReplaces: 'ersetzt',
            intro4: ' diese Vorgabe — sie kommt nie obendrauf.',
            minPayout: 'Mindestauszahlung ({sign})',
            minPayoutTip1: 'Die Mindestauszahlung der Dozenten dieser Marke (Dozentenvertrag § 8). Darunter wird nicht überwiesen, sondern vorgetragen. Ein Wert am Dozenten ist eine ',
            minPayoutTipDeviation: 'Abweichung',
            minPayoutTip2: '; leer dort heißt „folgt der Marke“. ',
            minPayoutTipEmptyHere: 'Leer hier',
            minPayoutTip3: ' heißt „nicht gesetzt“: Dann wird nichts überwiesen, und der Monatslauf meldet es.',
            chargebackFee: 'Rückbelastungsgebühr ({sign})',
            chargebackTip1: 'Pauschale je Rückbelastung (Chargeback). Sie wird dem Dozenten der Bestellung als eigene Zeile abgezogen, bei mehreren Dozenten anteilig nach ihrer Vergütung. ',
            chargebackTipEmpty: 'Leer',
            chargebackTip2: ' heißt „nicht gesetzt“: Die Rückbelastung wird trotzdem verarbeitet, aber es erscheint eine Warnung. ',
            chargebackTip3: ' heißt bewusst keine Gebühr.',
        },
        issuer: {
            heading: 'Aussteller dieser Marke',
            tip1: 'Wer diese Marke rechtlich ausstellt. Zwei Verbraucher: die monatliche Dozenten-Abrechnung ist eine Gutschrift, die wir im Namen des Dozenten ausstellen (Selbstfakturierung) und die uns vollständig nennen muss — sie liest den Anschriften-',
            tipBlock: 'Block',
            tip2: ' unten. Die maschinenlesbare Rechnung (ZUGFeRD/EN 16931) kann einen Textblock nicht verwenden und liest die ',
            tipFields: 'Einzelfelder',
            tip3: ' darunter. Die Rechnungsvorlagen tragen dieselben Angaben bis heute als festen Text in der XML-Vorlage; keiner der beiden Verbraucher kann sie von dort holen.',
            namePlaceholder: 'Firmierung laut Register',
            taxNumber: 'Steuernummer',
            register: 'Handelsregister',
            registerPlaceholder: 'z. B. HRB 12345',
            vatId: 'USt-IdNr.',
            addressBlock: 'Anschrift als Block',
            addressBlockHint: '(eine Zeile je Zeile — für die Dozenten-Gutschrift)',
            addressBlockPlaceholder: 'Straße und Hausnummer\nPLZ und Ort',
            fieldsHeading: 'Dieselbe Anschrift in Einzelfeldern',
            fieldsTip1: 'Für die maschinenlesbare Rechnung. Sie verlangt Straße, PLZ, Ort und vor allem das ',
            fieldsTipIso: 'Land als ISO-Code',
            fieldsTip2: ' getrennt voneinander; das Land ist dort eine Pflichtangabe (BT-40) und entscheidet mit, welches Steuerrecht der Beleg für sich beansprucht. Der Block darüber bleibt daneben bestehen und wird nicht daraus abgeleitet — aus „86405 Meitingen“ das Land zu erraten wäre auf einem Steuerbeleg schlimmer als ein leeres Feld. Bleibt hier etwas leer, entsteht für diese Marke ',
            fieldsTipNone: 'keine',
            fieldsTip3: ' maschinenlesbare Rechnung; das PDF ist davon unberührt.',
            street: 'Straße und Hausnummer',
            streetPlaceholder: 'z. B. Musterstraße 1',
            email: 'E-Mail des Ausstellers',
            emailHint: '(für die E-Rechnung)',
            emailPlaceholder: 'z. B. rechnung@example.com',
            phone: 'Telefon des Ausstellers',
            phoneTip: 'Die Telefonnummer der Gesellschaft — sie steht auf der Webseite im Impressum und in der Widerrufsbelehrung. Die Webseite liest sie von hier.',
            phonePlaceholder: 'z. B. +49 …',
        },
        // What the brand still lacks (server: services/brandRequirements.ts). The field names are
        // labels only — WHICH fields are required comes from the server.
        requirements: {
            zugferd: 'Für Rechnungen mit XML (ZUGFeRD) fehlt: {fields}.',
            conditions: 'Für die Konditionen (Dozentenvertrag, AGB) fehlt: {fields}.',
            fieldTitle: 'Pflichtangabe — noch leer',
            separator: ', ',
            alternative: ' oder ',
            fields: {
                issuerName: 'Name / Firma',
                issuerTaxNumber: 'Steuernummer',
                issuerRegister: 'Handelsregister',
                issuerVatId: 'USt-IdNr.',
                issuerStreet: 'Straße und Hausnummer',
                issuerPostalCode: 'PLZ',
                issuerCity: 'Ort',
                issuerCountry: 'Land',
                issuerEmail: 'E-Mail des Ausstellers',
                issuerPhone: 'Telefon des Ausstellers',
                brandDisplayName: 'Anzeigename der Marke',
                contactEmail: 'Kontakt-Mail',
                hotline: 'Hotline',
                impressumUrl: 'Impressum-Adresse',
                authorFeeValue: 'Dozentengebühr',
                authorPayoutThreshold: 'Mindestauszahlung',
                chargebackFeeFlat: 'Rückbelastungsgebühr',
                refundFeeValue: 'Stornogebühr',
            } as Record<string, string>,
        },
        contact: {
            heading: 'Kontakt dieser Marke',
            tip1: 'Was die Webseite der Marke über sie anzeigt: den Namen, unter dem sie auftritt, die Kontaktadresse (auch Antwortadresse ihrer Mails) und die Hotline-Nummer. Gepflegt wird es ',
            tipHere: 'hier',
            tip2: '; die Webseite zeigt es nur an. Die Sprechzeiten bleiben in WordPress. Kein Vertragswert: Eine Änderung steht im Änderungsprotokoll, ändert aber keinen Vertrag.',
            displayName: 'Anzeigename der Marke',
            displayNamePlaceholder: 'Name nach außen',
            email: 'Kontakt-Mail',
            emailPlaceholder: 'z. B. info@example.com',
            hotline: 'Hotline',
            hotlineHint: '(Nummer)',
            hotlinePlaceholder: 'z. B. +49 …',
            impressum: 'Impressum-Adresse',
            impressumHint: '(Link im Fuß jeder Mail dieser Marke)',
            impressumTip: 'Der Fuß der Mails zeigt nur einen Link auf das Impressum der Marke, keine Anschrift — die steht im Impressum und veraltet dort nicht unbemerkt. Leer heißt: Die Mails gehen ohne Link raus, und im Log steht eine Warnung.',
            impressumPlaceholder: 'z. B. https://www.example.com/impressum/',
            mailFrom: 'Absender der Mails',
            mailFromHint: '(Rechnungen, Gutschriften)',
            mailFromTip: 'Leer heißt: Es gilt der allgemeine Absender aus Einstellungen → E-Mail. Der Absendername gilt nur zusammen mit einer eigenen Adresse.',
            mailFromDefault: 'leer = allgemeiner Absender',
            mailFromName: 'Absendername',
        },
        mailAccount: {
            heading: 'Mailkonto dieser Marke',
            own: 'eigenes Konto',
            partial: 'unvollständig — es wird nicht versendet',
            empty: 'leer = allgemeines Konto',
            tip: 'Leer heißt: Die Mails dieser Marke gehen über das allgemeine Konto aus Einstellungen → E-Mail. Ein eigenes Konto braucht Host, Benutzer und Passwort zusammen — halb ausgefüllt wird nicht gespeichert. Zurück zum allgemeinen Konto: Host und Benutzer leeren. Der Test-Mail-Modus gilt für jedes Konto gleich.',
        },
        refund: {
            title: 'Stornogebühr — {brand}',
            intro1: 'Wird bei einer Erstattung einbehalten: Der Kunde bekommt den Rechnungsbetrag',
            introLess: ' abzüglich',
            intro2: ' dieser Gebühr zurück, der Storno-Beleg lautet nur über den ausgezahlten Betrag — die Gebühr bleibt Umsatz. Die Gebühr ist ',
            introEither: 'entweder',
            intro3: ' ein Prozentsatz ',
            intro4: ' ein Festbetrag, nie beides. Wert 0 = volle Erstattung. Im Erstatten-Dialog lässt sich die Gebühr für den Einzelfall überschreiben.',
            overrides: 'Abweichende Gebühr je Artikel',
            overridesTip: 'Artikel-ID = die vp_items-Nummer aus dem Shop. Eine Übersteuerung ERSETZT die Vorgabe oben (sie kommt nicht obendrauf). Sind mehrere gekaufte Artikel übersteuert, gilt die höchste Gebühr.',
            itemId: 'Artikel-ID',
            percent: 'Prozent',
            fixed: 'Festbetrag',
            internalLabel: 'Bezeichnung (nur intern)',
            internalLabelPlaceholder: 'z. B. Der Darm',
            remove: 'Entfernen',
            addItem: 'Artikel hinzufügen',
        },
        clear: {
            title: 'Geheimnis entfernen',
            ofBrand: ' der Marke „{brand}"',
            deleted: 'wird aus der Datenbank gelöscht.',
            irreversible: 'Das lässt sich hier nicht rückgängig machen — der Wert ist danach nur noch dort vorhanden, wo er herkommt (Stripe, Mollie, PayPal, das Postfach …).',
            confirm: 'Endgültig entfernen',
        },
    },

    // src/client/pages/VatSettings.tsx
    PAGES_VAT_SETTINGS_TEXTS: {
        loadFailed: 'Fehler beim Laden',
        saved: 'Gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        title: 'MwSt / OSS',
        titleReadOnly: 'MwSt / OSS (Ansicht)',
        loading: 'lädt…',
        apply: 'Übernehmen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Einstellungen.',
        calcPanel: 'MwSt-Berechnung',
        calcLabel: 'MwSt Berechnung',
        calcTip: 'Firmensitz = eigener MwSt-Satz (z. B. CY 19 %). Land des Kunden = Satz des Kundenlandes (OSS; erst nach OSS-Registrierung). Automatisch ab Umsatz = Kundenland-Satz, sobald der grenzüberschreitende B2C-Jahresumsatz die eingestellte Schwelle übersteigt.',
        companySeat: 'Firmensitz',
        modeManual: 'Land des Kunden',
        modeAuto: 'Automatisch ab Umsatz',
        baseTip: 'Bestimmt den eigenen MwSt-Satz (aus der Tabelle) und welches Land als „inländisch“ gilt. „Standard“ = Firmensitz aus der Server-Konfiguration (COMPANY_COUNTRY); wähle ein Land, um ihn fest vorzugeben.',
        baseDefault: 'Standard: {name} ({code})',
        thresholdLabel: 'Schwelle „Automatisch“ ({symbol})',
        thresholdTip: 'Ab diesem grenzüberschreitenden B2C-Jahresumsatz schaltet der Modus „Automatisch ab Umsatz“ auf die Kundenland-Sätze um. EU-Vorgabe: {amount}.',
        revenue: 'Grenzüberschr. B2C-Umsatz {year}: {revenue} / {threshold} ({pct} %)',
        ossActive: 'OSS aktiv',
        ossInactive: 'OSS inaktiv',
        registrationHint: 'Hinweis: Kundenland-Sätze erst nach erfolgter OSS-Registrierung berechnen.',
        thresholdExceeded: 'Schwelle überschritten — OSS ist automatisch aktiv.',
        ratesPanel: 'MwSt-Sätze je Land',
        ratesIntro: 'Standardwerte aus der EU-Tabelle (Platzhalter). Pro Land überschreibbar — leer = Standard. Diese Sätze gelten bei aktivem OSS für das jeweilige Kundenland.',
    },

    // src/client/pages/MailTemplates.tsx
    PAGES_MAIL_TEMPLATES_TEXTS: {
        loadFailed: 'Laden fehlgeschlagen',
        applyOrDiscardFirst: 'Bitte zuerst übernehmen oder die Änderungen verwerfen.',
        loading: 'lädt…',
        textsPanel: 'Mailtexte',
        invoiceHeading: 'Rechnungs-E-Mail',
        subject: 'Betreff',
        text: 'Text',
        invoiceSubjectTip: 'Betreff der Rechnungs-Mail. Platzhalter: {company}, {month}, {year}.',
        standardText: 'Standard-Text',
        invoiceBodyTip: 'Standard für alle Kunden; je Marke (Reiter der Marke) und pro Kunde überschreibbar. Platzhalter: {salutation}, {title}, {name}, {company}, {month}, {year}, {productlist} (Bestellte Artikel (Menge × Produkt), in einer eigenen Zeile).',
        singleHeading: 'Rechnungs-E-Mail bei einem einzelnen Beleg',
        singleIntro1: 'Wird verwendet, wenn die Mail ',
        singleIntroOne: 'genau eine',
        singleIntro2: ' Rechnung enthält — also beim Einzelkauf und beim erneuten Senden. Der Text oben spricht von „Rechnungen" und nennt einen Monat, was dort nicht passt. ',
        singleIntroBoth: 'Beide Felder leer lassen',
        singleIntro3: ', wenn der Text oben auch für Einzelbelege gelten soll.',
        singleSubjectTip: 'Leer = Betreff der Sammel-Mail oben. Platzhalter: {invoiceNumber}, {salutation}, {title}, {name}, {company}, {month}, {year}.',
        singleBodyTip: 'Leer = Text der Sammel-Mail oben. Ein Text pro Kunde sticht auch diesen hier. Platzhalter: {invoiceNumber}, {salutation}, {title}, {name}, {company}, {month}, {year}, {productlist} (Bestellte Artikel (Menge × Produkt), in einer eigenen Zeile).',
        creditHeading: 'Storno-E-Mail',
        creditSubjectTip: 'Betreff der Storno-/Gutschriftmail. Platzhalter: {company}, {month}, {year}.',
        creditBodyTip: 'Wird bei einer Erstattung statt des Rechnungstextes verwendet — Stornobelege gehen in einer eigenen Mail raus. Gilt für alle Kunden (keine Pro-Kunde-Variante). Platzhalter: {salutation}, {title}, {name}, {company}, {month}, {year}, {productlist} (Bestellte Artikel (Menge × Produkt), in einer eigenen Zeile).',
        exportHeading: 'Monats-Export (Steuerberater)',
        noMovementLabel: 'Zeile „keine Bewegung“ in der Begleitmail',
        noMovementTip: 'Eine Zeile je Prüfpunkt, dessen Anbieter im Monat keine Bewegung hatte — nur wenn am Prüfpunkt „Hinweis in der Begleitmail“ eingeschaltet ist. Leer = Vorgabe. Platzhalter: {label} (Prüfpunkt), {brands} (betroffene Marken in Klammern, bei PayPal leer), {period} (JJJJ-MM).',
        balanceLabel: 'Hinweis im Statement, wenn der Saldo fehlt',
        balanceTip: 'Steht im Stripe- oder PayPal-Statement, wenn der Kontostand beim Anbieter nicht abrufbar war — statt 0,00. Leer = Vorgabe. Platzhalter: {provider}.',
        pdfNameLabel: 'Dateiname der Rechnungs-PDF',
        pdfNameTip: 'So heißt die Rechnung, wenn sie die Anwendung verlässt: Herunterladen, Link in der Mail, Mail-Anhang, Abruf durch vitalpilze. Platzhalter: {brand} Kürzel, {ref} Referenznummer, {yyyy}/{yy} Jahr, {mm} Monat, {dd} Tag, {counter} Tageszähler (mind. zweistellig), {number} ganze Nummer. Muss {ref} oder {number} enthalten. Leer = Vorgabe {pattern} (z. B. D1317-26103102.pdf). Der Speichername und der Monatsexport bleiben, wie sie sind; Altrechnungen aus V1 behalten ihren Namen.',
        reportPrefixLabel: 'Dateiname des Umsatzberichts',
        reportPrefixTip: 'Der Teil vor dem Monat: Auto-Import und Versand an den Steuerberater legen den Bericht unter Zahlungs-Reports als „Präfix-Monat-Jahr.pdf“ ab, Monat englisch, z. B. {prefix}-September-2026.pdf. Leer = Vorgabe. Gilt ab dem nächsten Bericht.',
        framePanel: 'Mail-Rahmen',
        // The first tab of both panels; the brand tabs are named by the brands themselves.
        tabCommon: 'Allgemein',
        tabsLabel: 'Allgemein oder Marke',
        pillBlocked: 'Erst übernehmen oder die Änderungen verwerfen',
    },

    // src/client/components/BrandMailTexts.tsx
    COMPONENTS_BRAND_MAIL_TEXTS_TEXTS: {
        ownToggle: 'Eigene Mailtexte für diese Marke',
        ownHint: 'Aus: Es gelten die Mailtexte unter „Allgemein“. Ein: Jedes ausgefüllte Feld ersetzt das gemeinsame — ein leeres Feld lässt das gemeinsame gelten, Betreff und Text getrennt. Ein Text pro Kunde sticht weiterhin alles.',
        offClears: 'Ausschalten leert die eigenen Texte dieser Marke beim Übernehmen.',
        placeholders: 'Platzhalter im Text: {salutation}, {title}, {name}, {company}, {month}, {year}, {invoiceNumber} (nur bei einem Beleg), {productlist} (Bestellte Artikel (Menge × Produkt), in einer eigenen Zeile).',
        invoice: 'Rechnungs-E-Mail',
        single: 'Rechnungs-E-Mail bei einem einzelnen Beleg',
        creditNote: 'Storno-E-Mail',
        // Under the preview of the „Rechnungs-E-Mail" (operator, 27.10.2026, „140a"/„143b"); the names come from constants.ts.
        invoicePreviewHint: 'Diese E-Mail geht nur hinaus, wenn sie mehrere Belege trägt — die Vorschau rechnet mit zwei. {url} bleibt darin leer, die Links je Beleg gibt {list}. Eine einzelne Rechnung, auch die wiederkehrende, geht mit dem Text „Rechnungs-E-Mail bei einem einzelnen Beleg“.',
        subject: 'Betreff',
        text: 'Text',
        singleHint: 'Leer = es gilt der Rechnungstext dieser Marke, erst ohne ihn der gemeinsame.',
        commonPlaceholder: 'leer = gemeinsamer Betreff',
        singlePlaceholder: 'leer = Betreff der Rechnungs-E-Mail dieser Marke, sonst der gemeinsame',
        applies: 'Es gilt',
        appliesSubject: 'Betreff',
        appliesText: 'Text',
        preview: 'Vorschau mit Beispielwerten',
        previewTitle: 'Vorschau der Mail',
        previewSubject: 'Betreff:',
        loadFailed: 'Mailtexte der Marke konnten nicht geladen werden',
        sources: {
            customer: 'Text des Kunden',
            brandSingle: 'eigener Text (Einzelbeleg)',
            brandInvoice: 'eigener Rechnungstext der Marke',
            brandCreditNote: 'eigener Text',
            commonSingle: 'gemeinsamer Text (Einzelbeleg)',
            commonInvoice: 'gemeinsamer Rechnungstext',
            commonCreditNote: 'gemeinsamer Text',
        },
    },

    // src/client/pages/Templates.tsx
    PAGES_TEMPLATES_TEXTS: {
        pillPdf: 'PDF',
        pillMail: 'E-Mail',
        saved: 'Gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen',
        applyOrDiscardFirst: 'Bitte zuerst übernehmen oder die Änderungen verwerfen.',
        noAccess: 'Kein Zugriff auf Vorlagen.',
        title: 'Vorlagen',
        apply: 'Übernehmen',
        discard: 'Verwerfen',
        readOnlyNotice: 'Nur-Lese-Ansicht — deine Rolle hat keine Schreibrechte für Einstellungen.',
        pillBlocked: 'Erst übernehmen oder die Änderungen verwerfen',
    },

    // src/client/pages/InvoiceTemplateEditor.tsx
    PAGES_INVOICE_TEMPLATE_EDITOR_TEXTS: {
        previewFailed: 'Vorschau fehlgeschlagen',
        saveFailed: 'Speichern fehlgeschlagen',
        uploadFailed: 'Upload fehlgeschlagen',
        deleteFailed: 'Löschen fehlgeschlagen',
        save: 'Speichern',
        saved: 'Gespeichert',
        images: 'Bilder…',
        rendering: 'rendert…',
        unsaved: 'ungespeichert',
        current: 'aktuell',
        hint: 'Dummy-Daten · Änderungen am XML aktualisieren die Vorschau',
        pdfPreview: 'PDF-Vorschau',
        noPreview: 'Keine Vorschau.',
        loading: 'lädt…',
        manageImages: 'Bilder verwalten',
        uploadImage: 'Bild hochladen',
        imagesIntro: 'Im Vorlagen-Verzeichnis gespeichert. Referenzieren mit ',
        imagesIntroExample: '<image src="dateiname.png" …/>',
        noImages: 'Noch keine Bilder.',
        delete: 'Löschen',
        // Shown only when the opened version is not the active one (operator, 28.10.2026, „139“). Status: offen.
        inactiveVersion: 'Diese Version ist nicht aktiv. Neue Belege werden mit Version {active} erzeugt. Änderungen hier wirken sich nur auf ältere Belege aus, die mit dieser Version erstellt wurden, wenn sie neu erzeugt werden.',
    },

    // src/client/pages/InvoiceTemplatesList.tsx
    PAGES_INVOICE_TEMPLATES_LIST_TEXTS: {
        info: 'Vorlagen für die erzeugten PDFs — Rechnungen und Zertifikate. Doppelklick öffnet den Editor. Welche Vorlage ein Dokument nimmt, hängt an seiner Marke und Art und steht im Code — hier lassen sich Vorlagen bearbeiten, aber keine neuen anlegen.',
        loadFailed: 'Laden fehlgeschlagen',
        tabName: 'Vorlage: {label}',
        colTemplate: 'Vorlage',
        colKind: 'Art',
        kindCertificate: 'Zertifikat',
        kindInvoice: 'Rechnung',
        colVersion: 'Version',
        // The version new documents are printed with (operator, 27.10.2026, „137b“). Status: offen.
        active: 'aktiv',
        activeHint: 'Mit dieser Version werden neue Belege erzeugt. Die anderen Versionen bleiben für ältere Belege erhalten.',
        colActions: 'Aktionen',
        edit: 'Bearbeiten',
        title: 'PDF-Vorlagen',
        loading: 'lädt…',
        empty: 'Keine Vorlagen gefunden.',
    },

    // src/client/pages/WebhooksManager.tsx
    PAGES_WEBHOOKS_MANAGER_TEXTS: {
        loadFailed: 'Fehler beim Laden',
        error: 'Fehler',
        testOk: 'Test ok ({status})',
        testFailed: 'Test fehlgeschlagen: {reason}',
        deleteFailed: 'Löschen fehlgeschlagen',
        converter: 'Converter',
        colName: 'Name',
        colEvents: 'Events',
        colUrl: 'Ziel-URL',
        colSecret: 'Secret',
        colEnabled: 'Aktiv',
        colStatus: 'Status',
        colActions: 'Aktionen',
        sendTest: 'Test senden',
        edit: 'Bearbeiten',
        delete: 'Löschen',
        setupFailed: 'Einrichtung fehlgeschlagen',
        title: 'Webhooks',
        info: 'Ausgehende, signierte Webhooks (bei internen Ereignissen) und die eingehenden PSP-Endpoints je Marke (aktiver Anbieter).',
        newWebhookButton: '+ Neuer Webhook',
        outgoing: 'Ausgehend',
        incoming: 'Eingehend',
        searchPlaceholder: 'Webhook suchen…',
        noneFound: 'Keine Webhooks gefunden.',
        noneYet: 'Noch keine Webhooks angelegt.',
        inboundHeading: 'Eingehende PSP-Endpoints',
        inboundIntro1: 'Je Marke der Endpoint des ',
        inboundIntroActive: 'aktiven Zahlungsanbieters',
        inboundIntro2: '(Einstellungen → Marken). ',
        inboundIntro3: ' braucht einen registrierten Endpoint + Signing-Secret — „Einrichten" legt ihn per Stripe-API an und speichert das Secret. ',
        inboundIntro4: ' braucht kein Setup: die Webhook-URL wird pro Zahlung gesetzt und per Nachladen der Payment verifiziert (kein Endpoint, kein Secret).',
        stripe: 'Stripe',
        mollie: 'Mollie',
        colBrand: 'Marke',
        colPsp: 'PSP',
        colPath: 'Endpoint-Pfad',
        colMode: 'Modus',
        colRegistered: 'Registriert',
        modeTest: 'Test',
        modeLive: 'Live',
        secretSet: 'gesetzt',
        yes: 'ja',
        setup: 'Einrichten',
        noSetupNeeded: 'kein Setup nötig (pro Zahlung gesetzt, per Fetch verifiziert)',
        editWebhook: 'Webhook bearbeiten',
        newWebhook: 'Neuer Webhook',
        save: 'Speichern',
        create: 'Anlegen',
        cancel: 'Abbrechen',
        namePlaceholder: 'z. B. Rechnungslauf',
        urlPlaceholder: 'https://…/webhook',
        eventsTip: 'Bei welchen internen Ereignissen dieser Webhook ausgelöst wird. Mehrere möglich — alle gewählten Events gehen an dieselbe URL.',
        noEvents: 'Keine Events verfügbar.',
        secretTip: 'Gemeinsames Secret zum Signieren (HMAC-SHA256). Das Auge zeigt den gespeicherten Wert zum Abgleich mit der Gegenstelle.',
        keepUnchanged: 'unverändert lassen',
        deleteTitle: 'Webhook löschen',
        deleteConfirm: '„{name}" wirklich löschen?',
        deliveries: {
            colWhen: 'Wann',
            colEvent: 'Ereignis',
            colLabel: 'Vorgang',
            colAttempts: 'Versuche',
            colReason: 'Grund',
            heading: 'Zustellungen an vitalpilze',
            tip1: 'Meldungen an vitalpilze, die nicht beim ersten Mal ankamen. ',
            tipOpen: 'Offen',
            tip2: ' wird bis zu sieben Tage lang nachgefasst; ',
            tipGaveUp: 'aufgegeben',
            tip3: ' heißt, sieben Tage ohne Erfolg. ',
            tipRejected: 'Abgelehnt',
            tip4: ' heißt: Die Gegenseite ist eine andere Umgebung (dev ↔ prod, HTTP 421) — das wird nie wiederholt, die Adresse oder Umgebungskennung stimmt nicht.',
            counts: 'zugestellt {delivered} · offen {pending} · überholt {superseded} · aufgegeben {gaveUp} · abgelehnt {rejected}',
            listRejected: 'Abgelehnt — falsche Umgebung',
            listGaveUp: 'Aufgegeben',
            listOpen: 'Offen',
        },
    },

    // src/client/pages/ExpenseMappingAdmin.tsx
    PAGES_EXPENSE_MAPPING_ADMIN_TEXTS: {
        sourcePdf: 'PDF-Anhang',
        sourceLink: 'Download-Link',
        error: 'Fehler',
        deleteFailed: 'Löschen fehlgeschlagen',
        backfillDone: '{count} Beleg(e) nachträglich zugeordnet.',
        chooseType: 'Typ wählen…',
        sourceTitle: 'Wie die Rechnung ankommt',
        colDomain: 'Domain',
        colVendor: 'Lieferant',
        colExpenseType: 'Ausgaben-Typ',
        colSource: 'Typ',
        colActions: 'Aktionen',
        colLabel: 'Bezeichnung',
        colKey: 'Schlüssel',
        save: 'Speichern',
        cancel: 'Abbrechen',
        edit: 'Bearbeiten',
        delete: 'Löschen',
        rename: 'Umbenennen',
        title: 'Beleg-Zuordnung',
        info: 'Absender-/Rechnungs-Domains einem Ausgaben-Typ zuordnen. Eingehende Lieferanten-Rechnungen werden so automatisch klassifiziert. Typ „Download-Link“: kommt die Rechnung ohne PDF-Anhang, sondern als Link im Mail-Text (z. B. Anthropic/Stripe), lädt der Postfach-Import die Rechnung selbst herunter.',
        backfill: 'Bestehende Belege zuordnen',
        tabDomains: 'Domains',
        tabTypes: 'Ausgaben-Typen',
        addTypeHeading: 'Ausgaben-Typ hinzufügen',
        newTypePlaceholder: 'Neuer Typ (z. B. Server-Hosting)…',
        add: '+ Hinzufügen',
        typeSearchPlaceholder: 'Typ suchen…',
        noTypesFound: 'Keine Typen gefunden.',
        noTypesYet: 'Noch keine Typen angelegt.',
        addMappingHeading: 'Zuordnung hinzufügen',
        domainPlaceholder: 'Domain (z. B. hetzner.com)',
        vendorPlaceholder: 'Lieferant (optional)',
        loading: 'lädt…',
        noMappings: 'Keine Zuordnungen gefunden.',
        deleteType: 'Typ löschen',
        deleteMapping: 'Zuordnung löschen',
        deleteConfirm: '„{label}" wirklich löschen?',
    },

    // src/client/components/MailFrameEditor.tsx
    COMPONENTS_MAIL_FRAME_EDITOR_TEXTS: {
        sourceLabels: {
            brand: 'ihren eigenen Rahmen',
            common: 'den gemeinsamen Rahmen',
            default: 'die Vorgabe des Programms',
        },
        help1: 'HTML mit Platzhaltern: ',
        help2: ' (der Mailtext, ',
        helpRequired: 'Pflicht',
        help3: ' (Bildadresse des Logos, für ',
        help4: ' — die Werte kommen aus der jeweiligen Marke. ',
        help5: ' erscheint nur, wenn die Marke eine Impressum-Adresse hat (so entsteht ohne Adresse kein leerer Link); das geht mit jedem der Platzhalter.',
        previewTitle: 'Vorschau Mail-Rahmen',
        loadFailed: 'Mail-Rahmen nicht geladen: {error}',
        loading: 'lädt…',
        commonReset: 'Gemeinsamer Mail-Rahmen steht wieder auf der Vorgabe.',
        common1: 'Kopf mit Logo, Platz für den Mailtext, Fuß — für ',
        commonAllBrands: 'alle Marken',
        common2: ', solange eine Marke keinen eigenen Rahmen hat (Reiter der Marke). Gilt für Rechnungs-, Sammel- und Stornomail und die internen Mails.',
        commonStored: 'Eigene Fassung gespeichert.',
        commonDefault1: 'Zurzeit gilt die ',
        commonDefaultBold: 'Vorgabe des Programms',
        commonOwn: ' Eigenen Rahmen haben: {names}.',
        savedWith: 'Gespeichert wird mit ',
        applyQuoted: '„Übernehmen“',
        above: ' oben.',
        previewFor: 'Vorschau mit Beispieltext für',
        loadDefault: 'Vorgabe in den Editor laden',
        applyOrDiscardFirst: 'Erst übernehmen oder verwerfen',
        resetToDefault: 'Auf Vorgabe zurücksetzen',
        changedHint: 'geändert — „Übernehmen“ speichert',
        resetCommonTitle: 'Gemeinsamen Rahmen zurücksetzen',
        resetCommonMessage: 'Die gespeicherte Fassung wird gelöscht; alle Marken ohne eigenen Rahmen bekommen wieder die Vorgabe des Programms.',
        reset: 'Zurücksetzen',
        logoUploaded: 'Logo für {brand} hochgeladen.',
        brand1: 'Mails dieser Marke verwenden zurzeit ',
        brand2: '. Den gemeinsamen Rahmen für alle Marken bearbeitest du im Reiter „Allgemein“. Werte und eigener Rahmen werden mit ',
        brand3: ' oben gespeichert; das Logo gilt sofort nach dem Hochladen.',
        logo: 'Logo',
        logoTip: 'PNG oder JPG, höchstens {mb} MB. SVG zeigen Gmail und Outlook nicht an. Am besten doppelt so breit wie die Logobreite, dann bleibt es auf hochauflösenden Bildschirmen scharf.',
        logoAlt: 'Logo {brand}',
        uploaded: 'hochgeladen',
        default: 'Vorgabe',
        uploadLogo: 'Logo hochladen',
        logoReset: 'Logo steht wieder auf der Vorgabe.',
        defaultLogo: 'Vorgabe-Logo',
        logoWidth: 'Logobreite',
        logoWidthUnit: '(px)',
        footerColor: 'Farbe der Fußleiste',
        footerTextColor: 'Schriftfarbe der Fußleiste',
        emptyIsDefault: 'leer = Vorgabe',
        ownFrameFor: 'Eigener Rahmen nur für {brand}',
        ownFrameOtherwise: '(sonst gilt der gemeinsame)',
        previewWithSample: 'Vorschau mit Beispieltext',
        previewWithChanges: ' — mit den Änderungen, noch nicht übernommen',
        removeOwnTitle: 'Eigenen Rahmen von {brand} entfernen',
        removeOwnMessage: 'Der eigene Rahmen dieser Marke wird sofort gelöscht; ihre Mails bekommen wieder den gemeinsamen Rahmen.',
        remove: 'Entfernen',
        backToCommon: '{brand} verwendet wieder den gemeinsamen Rahmen.',
    },
} as const
